import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step5Part2 from './Step5-part2';
import Step6 from './Step6';
import Step7 from './Step7';
import Step8 from './Step8';
import Step9Part1 from './Step9-part1';
import Step9Part2 from './Step9-part2';
import Step9Part3 from './Step9-part3';
import AutocompleteInput from './AutocompleteInput';
import { SheetRow } from '../lib/googleSheetsService';
import Step10Part3 from './Step10Part3';
import Step10Part4 from './Step10Part4';
import Step10Part5 from './Step10Part5';
import DataScraper from './DataScraper';
import CapturifyProject from './CapturifyProject';
import ImageCropper from './ImageCropper';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../lib/loggingService';
import { clientsApi, pageInclusionsApi, reportsApi, stepsApi, webhookLogsApi, StepTable } from '../lib/dbApi';
import { stabilizeCapturifyUrls } from '../lib/capturifyImages';
import { toSameOriginImageUrl } from '../lib/imageProxy';

/**
 * The first step of the wizard.
 *
 * Arriving from Capturify's "Generate report" button (?capturifyProject=<id>)
 * opens the project straight away, with its photos grouped by visit. Everything
 * else — and "Import from a link instead" — gets the original DataScraper, which
 * is unchanged and still the fallback when Capturify can't be reached.
 */
const ScrapeStep: React.FC<{
  onDataExtracted: (data: any) => void;
  setCurrentStep: (step: any) => void;
  setFormData: (data: any) => void;
  userEmail?: string;
}> = ({ onDataExtracted, setCurrentStep, setFormData, userEmail }) => {
  // Read once on mount: the id shouldn't change under a mounted wizard.
  const [{ capturifyId, handoffToken }] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return {
      capturifyId: q.get('capturifyProject') || '',
      handoffToken: q.get('t') || '',
    };
  });
  const [manualImport, setManualImport] = useState(false);

  if (capturifyId && !manualImport) {
    return (
      <CapturifyProject
        clientId={capturifyId}
        handoffToken={handoffToken}
        onDataExtracted={onDataExtracted}
        onUseManualImport={() => setManualImport(true)}
        userEmail={userEmail}
      />
    );
  }

  return (
    <DataScraper
      onDataExtracted={onDataExtracted}
      setCurrentStep={setCurrentStep}
      setFormData={setFormData}
      userEmail={userEmail}
    />
  );
};

// Email Modal Component
interface EmailModalProps {
  onSend: (email: string) => void;
  onSkip: () => void;
  isSending: boolean;
  clientName: string;
  pdfUrl: string | null;
}

const EmailModal: React.FC<EmailModalProps> = ({ onSend, onSkip, isSending, clientName, pdfUrl }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    
    if (!email.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    onSend(email.trim());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Send Report to Client
          </h3>
          <p className="text-sm text-gray-600 text-center mb-6">
            Would you like to send the report to {clientName} via email? This is optional.
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Client Email Address
              </label>
              <input
                type="email"
                id="clientEmail"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent ${
                  emailError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="client@example.com"
                autoFocus
                disabled={isSending}
              />
              {emailError && (
                <p className="text-xs text-red-500 mt-1">{emailError}</p>
              )}
            </div>
            
            {!pdfUrl && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-xs text-yellow-800">
                  Note: PDF URL not available. Email sending may not work. You can still skip this step.
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={isSending || !email.trim() || !pdfUrl}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
                  isSending || !email.trim() || !pdfUrl
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#722420] text-white hover:bg-[#5a1d1a]'
                }`}
              >
                {isSending ? 'Sending...' : 'Send Email'}
              </button>
              <button
                type="button"
                onClick={onSkip}
                disabled={isSending}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
                  isSending
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Do Not Send Email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export interface ImageItem {
  id: string;
  url: string;
  alt?: string;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  pageNumber?: number; // Page number this image belongs to (1-indexed) for Step6
}

export interface FormData {
  // Client Information
  clientName: string;
  clientAddress: string;
  chimneyType: string;
  reportDate: string;
  timelineCoverImage?: string; // Timeline cover image from scraped data
  scrapedImages?: ImageItem[]; // All images from DataScraper
  selectedImages?: ImageItem[]; // Selected images for Page6
  step6TextPositionX?: number; // Text position X for Step 6
  step6TextPositionY?: number; // Text position Y for Step 6
  step6CurrentPage?: number; // Current page number for Step6 (1-indexed)
  step6TotalPages?: number; // Total number of pages for Step6
  dataSourceUrl?: string; // Data source URL from DataScraper
  invoiceData?: {
    invoiceNumber: string;
    paymentMethod: string;
    paymentNumber: string;
    notes?: string;
    rows: Array<{
      id: string;
      description: string;
      unit: string;
      price: string;
    }>;
  };
  repairEstimateData?: {
    estimateNumber: string;
    paymentMethod: string;
    paymentNumber: string;
    rows: Array<{
      id: string;
      description: string;
      unit: string;
      price: string;
    }>;
  };
  // Recommendations (Step5-part2) multi-section support
  recommendationSection1Title?: string; // Keep for backward compatibility
  showRecommendationSection2?: boolean; // Keep for backward compatibility
  recommendationSection2Title?: string; // Keep for backward compatibility
  recommendationSection2?: {
    rows: Array<{
      id: string;
      description: string;
      unit: string;
      price: string;
      }>;
    }; // Keep for backward compatibility
  // New: Array of sections for n-number support
  recommendationSections?: Array<{
    id: string;
    title: string;
    rows: Array<{
      id: string;
      description: string;
      unit: string;
      price: string;
    }>;
  }>;
  // Whether the combined totals table at the end of the repair estimate section
  // (only relevant/shown when there's more than one section) is included.
  // Undefined/missing (older saved reports) is treated as true.
  showRepairEstimateSummary?: boolean;
    notes: string;
    repairEstimatePages?: Array<{
    id: string;
    reviewImage: string;
    customRecommendation?: string; // Custom recommendation text
    imagePositionX?: number; // Image X position in pixels (relative to page)
    imagePositionY?: number; // Image Y position in pixels (relative to page)
    imageWidth?: number; // Image width in pixels
    imageHeight?: number; // Image height in pixels
    repairEstimateData: {
      manualEntry: boolean;
      rows: Array<{
        id: string;
        srNo: number;
        description: string;
        unit: number;
        price: number;
        isManual: boolean; // Individual row entry mode
        recommendation?: string; // Recommendation text for predefined items
      }>;
    };
  }>;
  usedReviewImages?: string[]; // Track used images to remove from available list
  excludedStep8Images?: string[]; // Track images excluded from Step 8
  inspectionImagesOrder?: string[]; // Track custom order of inspection images
  uploadedInspectionImages?: ImageItem[]; // Images uploaded by user for Step 8
  step8ImageLayouts?: Record<string, { x: number; y: number; width: number; height: number }>; // Per-image layout for Step 8
}

const dropdownOptions = [
  {
    id: '1',
    srNo: 1,
    description: 'Install Multi-Flue Chimney Cap With Manf Lifetime Warranty',
    unit: 1,
    price: 340,
    recommendation: 'REPLACE SINGLE FLUE CHIMNEY CAP Virginia Residential code R1003.9.1 states: Chimney caps. Masonry chimneys shall have a concrete, metal or stone cap….Chimney caps are designed to keep animals, rain, leaves and wind induced downdrafts from entering the chimney. They also help prevent hot embers from drafting out of the flue and landing on the roof. Chimney caps are required by modern building codes.A Step in Time recommends replacing chimney caps that are missing, rusted, damaged, improperly sized, improperly bolted or improperly screened as required by Virginia building codes.'
  },
  {
    id: '2',
    srNo: 2,
    description: 'Replace Top Flue Tile',
    unit: 1,
    price: 990,
    recommendation: 'INSTALL MUILTFLUE CHIMNEY CAPVirginia Residential code R1003.9.1 states: Chimney caps. Masonry chimneys shall have a concrete, metal or stone cap….Chimney caps are designed to keep animals, rain, leaves and wind induced downdrafts from entering the chimney. They also help prevent hot embers from drafting out of the flue and landing on the roof. Chimney caps are required by modern building codes.A Step in Time recommends replacing chimney caps that are missing, rusted, damaged, improperly sized, improperly bolted or improperly screened as required by Virginia building codes.'
  },
  {
    id: '3',
    srNo: 3,
    description: 'Parge Top Flue Tile Joint With Refractory Mortar',
    unit: 1,
    price: 890,
    recommendation: 'REPLACE TOP FLUE LINER Virginia Residential code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. Virginia Residential Code R1003.9.1 require flue liners to be installed in accordance with ASTM C1283. NFPA 211 14.9 require damaged or deteriorated liners to be replaced, repaired or relined. NFPA 211 7.2.13.3 requires the top flue liner to extend above the crown at least 2 inches. ASTM C1283 8.5 specifies the flue shall not extend father than 4 inches above the chimney crown.A Step in Time recommends replacement of top flue liners if the liner is less than 2 inches above the crown so that chimney caps can be properly fastened to the flue tile. A Step in Time recommends replacing flues that extend above 4 inches if they are part of a multiflued chimney so a proper cap can be installed to allow appropriate draft. A Step in Time recommends replacement of damaged or deteriorated top flue liners, depending on the extent of damage and the ability to contain combustion and house a chimney cap.'
  },
  {
    id: '4',
    srNo: 4,
    description: 'Fireguard All Flue Liner Joints',
    unit: 1,
    price: 290,
    recommendation: 'REPAIR GAPPED VOIDED JOINT BETWEEN 1ST AND 2ND FLUE TILESVirginia Building code R1003.12 require clay flue liners to be installed in accordance with ASTM C1283. ASTM C1283 states  "Flue liners shall be maintained by filling any voids in the interior, or hot face, with medium duty water insoluble refractory mortar conforming to Test Method C199."A Step in Time recommends repairing any voids between flue liners with approved material. This material may be products like FIREGUARD LINER REPAIR or refractory mortar conforming to C199.'
  },
  {
    id: '5',
    srNo: 5,
    description: 'Install Stainless Steel Chimney Liner',
    unit: 1,
    price: 3500,
    recommendation: 'REPAIR GAPPED VOIDED JOINTS BETWEEN FLUE LINERSVirginia Building code R1003.12 require clay flue liners to be installed in accordance with ASTM C1283. ASTM C1283 states  "Flue liners shall be maintained by filling any voids in the interior, or hot face, with medium duty water insoluble refractory mortar conforming to Test Method C199."A Step in Time recommends repairing any voids between flue liners with approved material. This material may be products like FIREGUARD LINER REPAIR or refractory mortar conforming to C199.'
  },
  {
    id: '6',
    srNo: 6,
    description: 'Install Stainless Steel Chimney Liner To Repair Damaged Chimney Fire Liner',
    unit: 1,
    price: 4900,
    recommendation: 'CHIMNEY LINERS ARE OFFSET & REQUIRE RELININGVirginia Building code R1003.12 require clay flue liners to be installed in accordance with ASTM C1283. ASTM C1283 5.4 states: Flue Liners shall be installed each flue liner carefully bedded on the previous one, using water insoluble refractory mortar complying with Test Method C199 (medium duty). All joints of the flue liner shall be 1/16 inch to 1/8 inch thick and struck flush to produce a straight, smooth, fully aligned flue. Liners shall be placed in such a manner as to minimize ledges or steps within the flue passageway.The flue liners are offset which can cause creosote and flue gases to escape into the chimney chase. This can lead to fire hazards and CO migration into the residence. A Step in Time recommends relining with a UL listed stainless steel chimney liner.'
  },
  {
    id: '7',
    srNo: 7,
    description: 'Repair Chimney Fire Damaged Flue Tiles With Fireguard Chimney Liner Repair',
    unit: 1,
    price: 4900,
    recommendation: 'EVIDENCE OF CHIMNEY FIRE - OFFSET TILESVirginia Building code R1003.12 require clay flue liners to be installed in accordance with ASTM C1283. ASTM C1283 5.4 states: Flue Liners shall be installed each flue liner carefully bedded on the previous one, using water insoluble refractory mortar complying with Test Method C199 (medium duty). All joints of the flue liner shall be 1/16 inch to 1/8 inch thick and struck flush to produce a straight, smooth, fully aligned flue. Liners shall be placed in such a manner as to minimize ledges or steps within the flue passageway.CHIMNEY FIRES OVERHEAT THE CHIMNEY LINER. If the chimney liner overheats, the thermal expansion of the chimney liner causes the liners to offset. The bottom of the liner is pressed on the supporting brick at the top of the smoke chamber. The top of the chimney liner is encased by the chimney crown. When the liner overheats from a chimney fire, it thermally expands and causes the entire liner to buckle, which results in offset flue tiles.Flue liners are designed to contain creosote and flue gases to safely expel to the atmosphere. Offset tiles will allow gases and creosote to transfer to the inner chimney chase, which can result in potential fire hazards and CO poisoning into the residence. A Step in Time recommends the installation of a stainless steel chimney liner to repair the damaged offset flue liners. '
  },
  {
    id: '8',  
    srNo: 8,
    description: 'Fireguard Repair Of Chimney Fire Damaged Liner Joints',
    unit: 1,
    price: 4900,
    recommendation: "EVIDENCE OF CHIMNEY FIRE - CRACKED TILESVirginia Residential code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. NFPA 211 14.9 discuss Damaged or Deteriorated Liners. If the flue liner in a chimney has softened, cracked, or otherwise deteriorated so that it no longer has the continued ability to contain the products of combustion, the liner shall be either removed and replaced, repaired, or relined with a listed liner system or other approved material that will resist corrosion, softening, or cracking from flue gases at temperatures appropriate to the class of chimney service.The specific reason why chimney liners crack is documented by many white papers. Basically, the chimney fire causes a quick increase in flue gas temperature. The interior of the chimney liner quickly thermally expands, and the exterior portion of the chimney liner is at a lower temperature. This causes the liner to experience a thermal shock, where the exterior is in extreme tension, and the interior of the liner is in extreme compression. Vitrified clay flue tiles are very weak in tension, and this causes thermal cracks. These thermal cracks open during hot flue gases and return to a nearly hairline crack when the liner cools. This crack opening results in the loss of the liner's continued ability to contain the products of combustion.A Step in Time recommends repairing the chimney liner with FIREGUARD liner repair material."
  },
  {
    id: '9',
    srNo: 9,
    description: 'Install Stainless Steel Chimney Liner To Repair Damaged Chimney Fire Liner',
    unit: 1,
    price: 3500,
    recommendation: "EVIDENCE OF CHIMNEY FIRE - CRACKED TILESVirginia Residential code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. NFPA 211 14.9 discuss Damaged or Deteriorated Liners. If the flue liner in a chimney has softened, cracked, or otherwise deteriorated so that it no longer has the continued ability to contain the products of combustion (i.e., heat, moisture, creosote, and flue gases), the liner shall be either removed and replaced, repaired, or relined with a listed liner system or other approved material that will resist corrosion, softening, or cracking from flue gases at temperatures appropriate to the class of chimney service.The specific reason why chimney liners crack is documented by many white papers. Basically, the chimney fire causes a quick increase in flue gas temperature. The interior of the chimney liner quickly thermally expands, and the exterior portion of the chimney liner is at a lower temperature. This causes the liner to experience a thermal shock, where the exterior is in extreme tension, and the interior of the liner is in extreme compression. Vitrified clay flue tiles are very weak in tension, and this causes thermal cracks. These thermal cracks open during hot flue gases and return to a nearly hairline crack when the liner cools. This crack opening results in the loss of the liner's continued ability to contain the products of combustion.A Step in Time recommends replacing the chimney liner with a UL listed stainless steel liner."
  },
  {
    id: '10',
    srNo: 10,
    description: 'No Wythe Wall',
    unit: 1,
    price: 4900,
    recommendation: "EVIDENCE OF CHIMNEY FIRE - CRACKED TILESVirginia Residential code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. NFPA 211 14.9 discuss Damaged or Deteriorated Liners. If the flue liner in a chimney has softened, cracked, or otherwise deteriorated so that it no longer has the continued ability to contain the products of combustion (i.e., heat, moisture, creosote, and flue gases), the liner shall be either removed and replaced, repaired, or relined with a listed liner system or other approved material that will resist corrosion, softening, or cracking from flue gases at temperatures appropriate to the class of chimney service.The specific reason why chimney liners crack is documented by many white papers. Basically, the chimney fire causes a quick increase in flue gas temperature. The interior of the chimney liner quickly thermally expands, and the exterior portion of the chimney liner is at a lower temperature. This causes the liner to experience a thermal shock, where the exterior is in extreme tension, and the interior of the liner is in extreme compression. Vitrified clay flue tiles are very weak in tension, and this causes thermal cracks. These thermal cracks open during hot flue gases and return to a nearly hairline crack when the liner cools. This crack opening results in the loss of the liner's continued ability to contain the products of combustion.A Step in Time recommends replacing the chimney liner with a UL listed stainless steel liner."
  },
  {
    id: '11',
    srNo: 11,
    description: 'Cracked Crown Pmp',
    unit: 1,
    price: 790,
    recommendation: "CHIMNEY CROWN IS CRACKED AND LEAKINGVirginia Building Code R1003.9.1 requires chimney crowns to be built according to ASTM C1283. This type of construction specifically requires drip edges, thermal expansion joints, and the crown being thick enough to prevent water leakage into the chase. Many crowns have been improperly constructed, and thus, A Step in Time recommends the following: If the crown has a drip edge and is cracked, which now allows thermal expansion, then we recommend sealing the crown with PMP crown repair material to prevent leakage. If the crown does not have a drip edge, then we recommend replacement with an ASTM C1283 crown. If the crown is bonded and not cracked, then we recommend masonry cut separation between the crown and flue tile to allow for thermal expansion and seal with PMP to prevent leakage."
  },
  {
    id: '12',
    srNo: 12,
    description: 'CrownNew Rebuild',
    unit: 1,
    price: 790,
    recommendation: "CHIMNEY CROWN IS CRACKED AND NO DRIP EDGEVirginia Building Code R1003.9.1 requires chimney crowns to be built according to ASTM C1283. This type of construction specifically requires drip edges, thermal expansion joints, and the crown being thick enough to prevent water leakage into the chase. Many crowns have been improperly constructed, and thus, A Step in Time recommends the following: If the crown has a drip edge and is cracked, which now allows thermal expansion, then we recommend sealing the crown with PMP crown repair material to prevent leakage. If the crown does not have a drip edge, then we recommend replacement with an ASTM C1283 crown. If the crown is bonded and not cracked, then we recommend masonry cut separation between the crown and flue tile to allow for thermal expansion and seal with PMP to prevent leakage"
  },
  {
    id: '13',
    srNo: 13,
    description: 'Crown Cut Seperation',
    unit: 1,
    price: 2100,
    recommendation: "CHIMNEY CROWN IS BONDED TO FLUE TILEVirginia Building Code R1003.9.1 requires chimney crowns to be built according to ASTM C1283. This type of construction specifically requires drip edges, thermal expansion joints, and the crown being thick enough to prevent water leakage into the chase. Many crowns have been improperly constructed, and thus, A Step in Time recommends the following: If the crown has a drip edge and is cracked, which now allows thermal expansion, then we recommend sealing the crown with PMP crown repair material to prevent leakage. If the crown does not have a drip edge, then we recommend replacement with an ASTM C1283 crown. If the crown is bonded and not cracked, then we recommend masonry cut separation between the crown and flue tile to allow for thermal expansion and seal with PMP to prevent leakage."
  },
  {
    id: '14',
    srNo: 14,
    description: 'Seal Drip Edge',
    unit: 1,
        price: 1100,
    recommendation: "DRIP EDGE IS LEAKINGVirginia Building code R1003.9.1 requires masonry chimneys to have a drip edge. The drip edge is designed to have water that flows off the top of the chimney to drip off the chimney instead of cascading. Water that saturates the exterior of the chimney will damage mortar joints and lead to leaks.A Step in Time recommends sealing drip edges with elastomeric sealant to prevent water from entering the horizontal mortar joint above the drip edge. This helps prevent water from damaging underlying mortar joints below the drip edge."
  },
  {
    id: '15',
    srNo: 15,
    description: 'Flex Shot Brick Joints',
    unit: 1,
    price: 390,
    recommendation: "BRICK JOINTS GAPPED, VOIDED AND LEAKINGTight well tooled brick joints prevent leaks in brick joints. When water enters gapped brick joints, the water can freeze and thaw and make the voids worse. The expansion of the ice will deteriorate mortar joints. When brick joints develop these voids, the solution is to try to keep it from getting worse. The best ways to keep water from entering these voids is to inject clear rubber material and coat the masonry brick with industrial strength \"breathable\" masonry water repellant. This helps to prevent water from entering that masonry."
  },
  {
    id: '16',
    srNo: 16,
    description: 'Rust Inhibit Flashing',
    unit: 1,
    price: 690,
    recommendation: "CHIMNEY FLASHING IS RUSTEDVirginia Building Code R507.2.4 requires that \"flashing shall be corrosion-resistant.\"The flashing that has been installed is rusted and will soon leak.A Step in Time recommends to either rust inhibit the existing flashing or replace it with new flashing. Rust inhibiting the flashing will simply add life but cannot change the underlying material composition."
  },
  {
    id: '17',
    srNo: 17,
    description: 'Replace Chimney Flashing',
    unit: 1,
    price: 290,
    recommendation: "CHIMNEY FLASHING HAS BEEN IMPROPERLY INSTALLEDVirginia Building Code R905.2.8.4 Other Flashing: chimney flashing shall be applied in accordance with the asphalt manufacture's printed instructions.GAF requires counterflashing to extend over the base step flashing and to be completely sealed with elastomeric polyurethane sealant.A Step in Time recommends replacing the chimney flashing."
  },
  {
    id: '18',
    srNo: 18,
    description: 'Reseal Flashing',
    unit: 1,
    price: 1500,
    recommendation: "CHIMNEY FLASHING REQUIRES RESEALINGVirginia Building Code R905.2.8.4 Other Flashing: chimney flashing shall be applied in accordance with the asphalt manufacture's printed instructions.GAF requires counterflashing to extend over the base step flashing and to be completely sealed with elastomeric polyurethane sealant."
  },
  {
    id: '19',
    srNo: 19,
    description: 'Install Cricket',
    unit: 1,
    price: 290,
    recommendation: "CHIMNEY REQUIRES A CRICKETVirginia Building code R1003.20 Chimney Crickets. Chimneys shall be provided with crickets where the dimensions parallel to the ridgeline are greater than 30 inches.Chimney crickets are designed to divert water around the chimney and can prevent leaks around the chimney. The installation of chimney crickets requires complete removal of chimney flashing, removal of shingles and underlayment, rebuilding the roof,  reinstallation of underlayment and chimney flashing, and the installation of a code compliant chimney cricket."
  },
  {
    id: '20',
          srNo: 20,
    description: 'Profile Seams Exterior Caulking',
    unit: 1,
    price: 2100,
    recommendation: "SEAL CHIMNEY TO SIDING LIFETIME CAULK Virginia Maintenance code requires sealing all exposed opening that can be exposed to weather. The joint between the chimney and the residence requires sealing to prevent sideways rain from entering between the chimney and the residence./ A Step in Time recommends sealing from ground to roofline with lifetime caulk."
  },
  {
    id: '21',
    srNo: 21,
    description: 'Repair Shoulders',
    unit: 1,
    price: 590,
    recommendation: "CHIMNEY SHOULDERS REQUIRE REPAIRVirginia Building code R1003.5 Corbeling: The projection of a single course shall not exceed half the unit height or one third the unit bed depth, whichever is less.Many times, shoulders are not properly corbeled, and brick holes are exposed, or the chimney shoulders can leak into the smoke chamber. Rebuilding an improperly built chimney is not practical, but parging or repairing exposed chimney shoulders can help prevent water leakage into the chimney."
  },
  {
    id: '22',
    srNo: 22,
    description: 'Cut And Reinstall Thermal Damaged Bricks Behind Smoke Chamber',
    unit: 1,
    price: 890,
    recommendation: "THERMAL CRACKS ARE LOCATED BEHIND THE SMOKE CHAMBERVirginia Building Code R1001.8 requires the smoke chamber wall to be a minimum of 8 inches thick and parged with refractory mortar. The exposed side of the smoke chamber will crack if the wall is too thin and not properly parged with refractory mortar. The exact reason is that the inner smoke chamber is heated during use, and the opposite side of the exposed wall is cool from the outside air. This change in temperature of the brick will cause the brick to expand on the heated inner surface and contract on cooler outer surface. This thermal expansion difference causes stresses in the brick, which causes vertical cracks. Coating the inner smoke chamber with thick refractory mortar helps prevent this noticeable change in temperatures problems."
  },
  {
    id: '23',
    srNo: 23,
    description: 'Install Stainless Steel Chimney Liner To Replace Unsupported Liner',
    unit: 1,
    price: 1800,
    recommendation: "BOTTOM FLUE TILE IS NOT PROPERLY SUPPORTEDVirginia Building code R1003.12 states: \"Flue liners shall be supported on all sides.\"If the flue liner is not supported by all sides,  when the inner portion of the liners heat, the liner can extend into the smoke chamber, and ultimately may collapse into the smoke chamber.A Step in Time recommends removal of all the vitrified clay flue tiles and installation of a stainless steel chimney liner."
  },
  {
    id: '24',
    srNo: 24,
    description: 'Parge Lower Flue Tile Gape With Refractory Mortar',
    unit: 1,
    price: 4900,
    recommendation: "BOTTOM FLUE TILE IS NOT PROPERLY SUPPORTEDVirginia Building code R1003.12 states: \"Flue liners shall be supported on all sides.\"If the flue liner is not supported by all sides, then gas and creosote can migrate between the flue liner and the enclosed chimney. This can cause a fire hazard and can also allow CO to escape through the chimney and into the residence.A Step in Time recommend sealing the voids where the liner is not supported with high temperature refractory mortar."
  },
  {
    id: '25',
    srNo: 25,
    description: 'Parge Smoke Chamber With Chambertech 2000',
    unit: 1,
    price: 690,
    recommendation: "SMOKE CHAMBER REQUIRES PARGINGVirginia Building Code R1001.8.1 Smoke Chamber: requires \"The inside surface shall be parged smooth with refractory mortar.\" Smoke chambers are required to be parged smooth with refractory mortar to provide a smooth draft through the smoke chambers and prevent creosote and heat from transferring through the corbeled brick, which can lead to heating of surrounding timber structural members.A Step in Time recommends parging the smoke chamber with refractory mortar conforming with ASTM C199."
  },
  {
    id: '26',
    srNo: 26,
    description: 'Fill Voided Smoke Shelf With Fireclay Mortar',
    unit: 1,
    price: 1500,
    recommendation: "SMOKE SHELF IS MISSING AND REQUIRES INSTALLATIONVirginia Building Code Figure R1001.1 (Fireplace and Chimney Details) shows the curved smoke shelf next to detail H - vertical reinforcement. The upward smooth curve of the smoke shelf is designed to provide a circular airflow where air travels downward along the rear smoke shelf and reverses direction along the curved smoke shelf to direct upward airflow past the damper, which increases draft. Filled smoke shelves also reinforce the structural integrity of the rear firebox wall.A Step in Time recommends filling the smoke shelf area with refractory mortar as per Virginia Building codes."
  },
  {
    id: '27',
    srNo: 27,
    description: 'Install Stainless Steel Cap Damper In Place Of Rotary Damper',
    unit: 1,
    price: 1500,
    recommendation: "RECOMMEND CAP DAMPERVirginia Building Code R1001.7.1 Damper - Masonry fireplaces shall be equipped with a ferrous metal damper. R1003.17 Masonry chimney cleanout openings shall be provided. Exception: Chimney flues servicing masonry fireplaces where cleaning is possible through the fireplace opening.A Step in Time recommends replacing all rotary dampers with cap dampers because the rotary damper mechanism prevents proper cleaning through the fireplace opening."
  },
  {
    id: '28',
    srNo: 28,
    description: 'Install Stainless Steel Cap Damper To Improve Draft',
    unit: 1,
    price: 1090,
    recommendation: "RECOMMEND CAP DAMPERVirginia Building Code R1001.7.1 Damper - Masonry fireplaces shall be equipped with a ferrous metal damper. R1003.17 Masonry chimney cleanout opening shall be provided. Exception: Chimney flues servicing masonry fireplaces where cleaning is possible through the fireplace opening.A Step in Time recommends installing an energy efficient cap damper, which improves draft and allows for easier cleaning through the fireplace opening."
  },
  {
    id: '29',
    srNo: 29,
    description: 'Liner To Wood Stove',
    unit: 1,
    price: 890,
    recommendation: "WOOD STOVE REQUIRES DIRECT CONNECT LINERVirginia Building Code G2427.5.2 requires masonry chimneys to be installed in accordance with NFPA 211. 13.4.5.1 \"A natural draft solid fuel burning appliance such as a wood stove or insert shall be permitted to be used in a masonry fireplace flue, where the following conditions are met: 1) There is a connector between the appliance to the flue liner.In the 1970's and 1980's many homes had wood stove inserts installed, and insurance companies found that the decreased opening of the wood stove drafted the appliance extremely slowly, which produced large amounts of creosote. This dangerous situation caused many chimney fires, and the building code officials solved this problem by requiring smaller direct connect liners installed from the appliance up the chimney liner."
  },
  {
    id: '30',
        srNo: 30,
    description: 'Parge Lintel Joint With Refractory Mortar',
    unit: 1,
    price: 2900,
    recommendation: "LINTEL JOINT REQUIRES PARGINGVirginia Building Code G2427.5.2 requires masonry chimneys to be installed in accordance with NFPA 211. 11.2.1.5 All joints and intersections between the hearth extension/ fireplace facing and the fire chamber (firebox) shall be fully sealed with medium-duty refractory mortar (ASTM C199, Standard Test Method for Pier Test for Refractory Mortars). Gaps or voids at supporting lintels and joints between steel fireplace units and the fireplace face or between the frames of dampers and the fireplace face shall be sealed with the same material or with a high-temperature insulating mortar."
  },
  {
    id: '31',
    srNo: 31,
    description: 'Repair Cracked Profile Seams With Refractory Mortar',
    unit: 1,
    price: 390,
    recommendation: "FIREBOX PROFILE SEAMS REQUIRES PARGINGVirginia Building Code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. 11.2.1.5 All joints and intersections between the hearth extension/ fireplace facing and the fire chamber (firebox) shall be fully sealed with medium-duty refractory mortar (ASTM C199, Standard Test Method for Pier Test for Refractory Mortars). Gaps or voids at supporting lintels and joints between steel fireplace units and the fireplace face or between the frames of dampers and the fireplace face shall be sealed with the same material or with a high temperature insulating mortar."
  },
  {
    id: '32',
    srNo: 32,
    description: 'Repair Inner/Outer Profile Seam With Refractory Mortar',
    unit: 1,
    price: 290,
    recommendation: "JOINT BETWEEN INNER & OUTER HEARTH REQUIRES PARGINGVirginia Building Code G2427.5.2 requires masonry chimneys to be installed in accordance with NFPA 211. 11.2.1.5 All joints and intersections between the hearth extension/ fireplace facing and the fire chamber (firebox) shall be fully sealed with medium-duty refractory mortar (ASTM C199, Standard Test Method for Pier Test for Refractory Mortars). Gaps or voids at supporting lintels and joints between steel fireplace units and the fireplace face or between the frames of dampers and the fireplace face shall be sealed with the same material or with a high temperature insulating mortar."
  },
  {
    id: '33',
    srNo: 33,
    description: 'Repoint Firebox Joints With Refractory Mortar',
    unit: 1,
    price: 190,
    recommendation: "FIREBOX MORTAR JOINTS REQUIRE REPOINTINGVirginia Building Code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. 11.2.1.5 All joints and intersections between the hearth extension/ fireplace facing and the fire chamber (firebox) shall be fully sealed with medium-duty refractory mortar (ASTM C199, Standard Test Method for Pier Test for Refractory Mortars). Gaps or voids at supporting lintels and joints between steel fireplace units and the fireplace face or between the frames of dampers and the fireplace face shall be sealed with the same material or with a high temperature insulating mortar."
  },
  {
    id: '34',
    srNo: 34,
    description: 'Replace Ash Dump Door',
    unit: 1,
    price: 990,
    recommendation: "REPLACE ASH DUMP DOORVirginia Building Code R1001.2.1 Ash Dump Cleanout is required \"to remain tightly closed except when in use.\"Many times, ash dump doors will warp, rust, or become inoperable after repeated use.A Step in Time recommends replacement of the ash dump door."
  },
  {
    id: '35',
    srNo: 35,
    description: 'Replace Clean Out Door',
    unit: 1,
    price: 90,
    recommendation: "REPLACE CLEAN-OUT DOORVirginia Building Code R1001.2.1 Clean outs shall be located to allow access so that ash removal will not create a hazard to combustible materials. Additional clean-out doors are required to remain tightly closed.Many times, clean-out doors will warp, rust, or become inoperable after repeated use.A Step in Time recommends replacement of the clean-out door."
  },
  {
    id: '36',
    srNo: 36,
    description: 'Remove Timber Formwork',
    unit: 1,
    price: 490,
    recommendation: "REMOVE COMBUSTIBLE MATERIAL UNDER HEARTHVirginia Building Code R1001.9 requires: \"Combustible material shall not remain against the underside of hearths and hearth extensions after construction.\"The heat from the firebox can heat the combustible material under the hearth. This combustible material should be removed, and the outer heart may require support using foundation and pier supports."
  },
  {
    id: '37',
    srNo: 37,
    description: 'Remove Wood Stove',
    unit: 1,
    price: 2100,
    recommendation: "WOOD STOVE INSERT SHOULD BE REMOVEDVirginia Building Code G2427.5.2 require masonry chimneys to be installed in accordance with NFPA 211. 13.4.5.1 \"A natural draft solid fuel burning appliance such as a wood stove or insert shall be permitted to be used in a masonry fireplace flue, where the following conditions are met: 1) There is a connector between the appliance to the flue liner.In the 1970's and 1980's many homes had wood stove inserts installed, and insurance companies found that the decreased opening of the wood stove drafted the appliance extremely slowly, which produced large amounts of creosote. This dangerous situation caused many chimney fires, and the building code officials solved this problem by requiring smaller direct connect liners installed from the appliance up the chimney liner."
  },
  {
    id: '38',
    srNo: 38,
    description: 'Level 2 Chimney Inspection Is Required',
    unit: 1,
    price: 450,
    recommendation: "Virginia Chimneys, Fireplaces, Vents and Solid Fuel-Burning Appliance Code 15.4 states: A Level II inspection is indicated when verification of the suitability of the chimney for new or changed conditions of service is needed or when a Level I inspection is not sufficient to determine the serviceability of the chimney. 15.4.1: Circumstances: After a building or chimney fire, weather or seismic event, or other incident likely to have caused damage to the chimney."
  },
  {
    id: '39',
    srNo: 39,
    description: 'Level 2 Chimney Inspection Is Required',
    unit: 1,
    price: 100,
    recommendation: "Virginia Chimneys, Fireplaces, Vents and Solid Fuel-Burning Appliance Code 15.4 states: A Level II inspection is indicated when verification of the suitability of the chimney for new or changed conditions of service is needed or when a Level I inspection is not sufficient to determine the serviceability of the chimney. 15.4.1: Circumstances: After a building or chimney fire, weather or seismic event, or other incident likely to have caused damage to the chimney."
  }
];

// Helper function to sanitize filename
const sanitizeFileName = (text: string): string => {
  if (!text) return '';
  // First, normalize Unicode characters and convert to ASCII-safe equivalents
  // Replace common Unicode characters with ASCII equivalents
  let sanitized = text
    .normalize('NFD') // Decompose characters into base + combining marks
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/[•·]/g, '-') // Replace bullet points with dash
    .replace(/[—–]/g, '-') // Replace em/en dashes with regular dash
    .replace(/[""]/g, '"') // Replace smart quotes with regular quotes
    .replace(/['']/g, "'") // Replace smart apostrophes with regular apostrophe
    .replace(/[^\x00-\x7F]/g, '') // Remove any remaining non-ASCII characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
    .trim();
  
  // Ensure we don't have empty result
  return sanitized || 'file';
};

const MultiStepForm: React.FC<{ userEmail: string; webhookData?: any; onStepChange?: (step: 'scrape' | 'form') => void }> = ({ userEmail, webhookData, onStepChange }) => {
  const [currentStep, setCurrentStep] = useState<'scrape' | 'form'>('scrape');

  // Let the parent know when we've moved past data collection, so it can hide
  // anything (like webhook notifications) that only makes sense before a report
  // is actively being generated/edited.
  useEffect(() => {
    onStepChange?.(currentStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentRecommendationPageIndex, setCurrentRecommendationPageIndex] = useState<number>(0);
  const [currentRecommendationSubPage, setCurrentRecommendationSubPage] = useState<number>(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<FormData>({
    clientName: '',
    clientAddress: '',
    chimneyType: '',
    reportDate: new Date().toISOString().split('T')[0],
    timelineCoverImage: '',
    scrapedImages: [],
    selectedImages: [],
    invoiceData: {
      invoiceNumber: '',
      paymentMethod: '',
      paymentNumber: '',
      notes: '',
      rows: []
    },
    repairEstimateData: {
      estimateNumber: '',
      paymentMethod: '',
      paymentNumber: '',
      rows: []
    },
    repairEstimatePages: [],
    usedReviewImages: [],
    excludedStep8Images: [],
    inspectionImagesOrder: [],
    uploadedInspectionImages: [],
    step8ImageLayouts: {},
    notes: 'This quote is good for 30 days from date of service. Deposits for scheduled future service is non-refundable.'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<'editing' | 'saving' | 'generating' | 'uploading' | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isSavingStep, setIsSavingStep] = useState(false); // Loading indicator while auto-saving the current step before navigating away from it
  // Set when a per-step autosave fails. Previously this failed silently (console.error only),
  // which meant a step's edits could be lost with zero visible indication - the generated PDF
  // still looked correct (it's rendered from live in-memory state, not the database), but
  // re-opening the report for editing later would show nothing for that step.
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null); // Track current report ID when editing
  // True only when this report was opened via "Edit" on an already-existing report
  // (loadForEdit below). "Start Over" deletes the in-progress report on exit, but
  // only when this is false - never delete a report that existed before this
  // session, or a real historical report would be destroyed along with it.
  const [isEditingExistingReport, setIsEditingExistingReport] = useState(false);
  const [webhookLogId, setWebhookLogId] = useState<string | null>(null); // Track webhook log ID for report source tracking
  const [isMobile, setIsMobile] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [preferGoogleDocs, setPreferGoogleDocs] = useState(true); // User preference for PDF viewing
  const [includeRepairEstimate, setIncludeRepairEstimate] = useState(true); // Step 7 is always included
  const [includedPages, setIncludedPages] = useState<Set<number>>(new Set()); // Will be initialized in useEffect
  const hasInitializedPages = useRef(false); // Track if pages have been initialized
  const maxInitializedPage = useRef<number>(0); // Track the maximum page number that was initialized
  const newRowDescriptionRef = useRef<string | null>(null); // Track new row ID to focus after adding
  const hasConsumedWebhookData = useRef(false); // Track if webhook data has been consumed (used once)
  const [draggedImageId, setDraggedImageId] = useState<string | null>(null); // Track dragged image for drag-and-drop
  const fileInputRef = useRef<HTMLInputElement>(null); // Ref for file input
  const [cropData, setCropData] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [croppingImageId, setCroppingImageId] = useState<string | null>(null); // Track which invoice image is being cropped
  const [showReviewImageSelector, setShowReviewImageSelector] = useState(false);
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [showPredefinedModal, setShowPredefinedModal] = useState(false);
  const [showChangeImageModal, setShowChangeImageModal] = useState(false);
  const [showInvoiceQuickAdd, setShowInvoiceQuickAdd] = useState(false);
  const [predefinedSearchTerm, setPredefinedSearchTerm] = useState('');
  
  // Date selection state for Step 8
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [showPreviousJobImages, setShowPreviousJobImages] = useState(false);
  const [allJobs, setAllJobs] = useState<Array<{
    id: string;
    date: string;
    imageUrls: string[];
    scrapedImages: ImageItem[];
  }>>([]);
  const [selectedDateJob, setSelectedDateJob] = useState<{
    id: string;
    date: string;
    imageUrls: string[];
    scrapedImages: ImageItem[];
  } | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [imageForPositionMove, setImageForPositionMove] = useState<string | null>(null);
  const [targetPosition, setTargetPosition] = useState<string>('');
  // Approval and email flow state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState<Blob | null>(null);
  const [generatedPdfFileName, setGeneratedPdfFileName] = useState<string>('');
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number>(0); // Track which section is being edited
  const prevRepairEstimatePages = useRef<number>(0); // Track previous repair estimate page count

  // Function to parse all jobs from HTML and extract their dates
  const parseAllJobsFromHtml = (htmlContent: string): Array<{
    id: string;
    date: string;
    imageUrls: string[];
    scrapedImages: ImageItem[];
  }> => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const jobs: Array<{
      id: string;
      date: string;
      imageUrls: string[];
      scrapedImages: ImageItem[];
    }> = [];
    
    // Look for timeline-block elements (each represents a job/date)
    const timelineBlocks = doc.querySelectorAll('div.timeline-block');
    
    timelineBlocks.forEach((timelineBlock, index) => {
      // Extract date from span.date element
      let jobDate = '';
      const dateElement = timelineBlock.querySelector('span.date');
      if (dateElement) {
        jobDate = dateElement.textContent?.trim() || '';
      }
      
      // Extract images from photo-grid
      const imageUrls: string[] = [];
      const photoItems = timelineBlock.querySelectorAll('a.photo-item[data-full]');
      
      photoItems.forEach(photoItem => {
        const dataFull = photoItem.getAttribute('data-full');
        if (dataFull) {
          imageUrls.push(dataFull);
        }
      });
      
      // Create job data
      const jobData = {
        id: `job-${index}-${Date.now()}`,
        date: jobDate || new Date().toISOString().split('T')[0],
        imageUrls: imageUrls,
        scrapedImages: imageUrls.map((url, imgIndex) => ({
          id: `job-${index}-img-${imgIndex}-${Date.now()}`,
          url: url,
          alt: `Job ${index + 1} image ${imgIndex + 1}`
        }))
      };
      
      jobs.push(jobData);
    });
    
    // Sort jobs by date (newest first)
    jobs.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
    
    return jobs;
  };

  // Function to load jobs from data source URL
  const loadJobsFromUrl = async () => {
    if (!formData.dataSourceUrl) return;
    
    setIsLoadingJobs(true);
    try {
      const response = await fetch(formData.dataSourceUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const htmlContent = await response.text();
      const jobsData = parseAllJobsFromHtml(htmlContent);
      setAllJobs(jobsData);
      setShowDateSelector(true);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      alert('Failed to load jobs from data source. Please verify the URL and try again.');
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Function to handle date selection
  const handleDateSelection = (job: {
    id: string;
    date: string;
    imageUrls: string[];
    scrapedImages: ImageItem[];
  }) => {
    setSelectedDateJob(job);
    setSelectedImages([]); // Reset selected images when selecting a new date
    setShowDateSelector(false);
    setShowPreviousJobImages(true);
  };

  // Function to handle date image selection
  const handleDateImageSelection = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        // Remove image if already selected
        return prev.filter(url => url !== imageUrl);
      } else {
        // Add image if not selected
        return [...prev, imageUrl];
      }
    });
  };

  // Function to handle select all images
  const handleSelectAllImages = () => {
    if (!selectedDateJob) return;
    
    if (selectedImages.length === selectedDateJob.imageUrls.length) {
      // If all images are selected, deselect all
      setSelectedImages([]);
    } else {
      // Select all images
      setSelectedImages([...selectedDateJob.imageUrls]);
    }
  };

  // Function to merge selected date images
  const handleMergeSelectedDateImages = () => {
    if (!selectedDateJob || selectedImages.length === 0) return;
    
    // Convert selected image URLs to ImageItem format
    const selectedImageItems: ImageItem[] = selectedImages.map((url, index) => ({
      id: `selected-${index}-${Date.now()}`,
      url: url,
      alt: `Selected image from ${selectedDateJob.date}`
    }));
    
    // Merge selected images with current inspection images
    const currentUploaded = formData.uploadedInspectionImages || [];
    const currentImages = getUnusedImages();
    const allImages = [...currentImages, ...selectedImageItems];
    
    updateFormData({
      uploadedInspectionImages: [...currentUploaded, ...selectedImageItems],
      inspectionImagesOrder: allImages.map(img => img.id)
    });
    
    setShowPreviousJobImages(false);
    setSelectedDateJob(null);
    setSelectedImages([]);
  };

  // On refresh/load, clear previous saved snapshots for a clean session
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || '';
        if (key.startsWith('sumoquote_v2_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (err) {
      // Ignore storage errors
    }
  }, []);

  // Track if this is initial mount
  const isInitialMount = useRef(true);

  // Log step navigation when page changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only log if user is actively working (has entered form step and has data)
    if (currentPage > 0 && currentStep === 'form' && (currentReportId || formData.clientName)) {
      const reportId = currentReportId || formData.clientName || 'new_report';
      const logicalStep = getLogicalStep(currentPage);
      logger.logStepEntered(userEmail, reportId, logicalStep);
    }
  }, [currentPage]);

  // Toggle a page's inclusion in PDF. Defaults to the current page (used by the inline
  // include/exclude switch), but accepts any page number (used by the full page checklist).
  const togglePageInclusion = async (pageNumber: number = currentPage) => {
    const newIncluded = !isPageIncluded(pageNumber);
    setIncludedPages(prev => {
      const newSet = new Set(prev);
      if (newIncluded) {
        newSet.add(pageNumber);
      } else {
        newSet.delete(pageNumber);
      }
      return newSet;
    });

    // Save to database if we have a report ID
    if (currentReportId) {
      try {
        if (newIncluded) {
          await pageInclusionsApi.include(currentReportId, pageNumber);
        } else {
          await pageInclusionsApi.exclude(currentReportId, pageNumber);
        }
      } catch (error) {
        console.error('Failed to save page inclusion:', error);
        // Revert on error
        setIncludedPages(prev => {
          const newSet = new Set(prev);
          if (newIncluded) {
          newSet.delete(pageNumber);
        } else {
          newSet.add(pageNumber);
        }
          return newSet;
        });
      }
    }
  };

  // Include all pages in PDF
  const includeAllPages = async () => {
    const allPages = new Set<number>();
    for (let page = 1; page <= totalPages; page++) {
      allPages.add(page);
    }
    setIncludedPages(allPages);
    
    // Save to database if we have a report ID
    if (currentReportId) {
      try {
        await pageInclusionsApi.bulkSet(currentReportId, Array.from(allPages));
      } catch (error) {
        console.error('Failed to save page inclusions:', error);
      }
    }
  };

  // Exclude all pages from PDF
  const excludeAllPages = async () => {
    setIncludedPages(new Set());

    // Save to database if we have a report ID
    if (currentReportId) {
      try {
        await pageInclusionsApi.bulkSet(currentReportId, []);
      } catch (error) {
        console.error('Failed to delete page inclusions:', error);
      }
    }
  };

  // Check if a specific page number is included in PDF
  const isPageIncluded = (pageNumber: number) => {
    return includedPages.has(pageNumber);
  };

  // Check if the current page is included in PDF
  const isCurrentPageIncluded = () => {
    return includedPages.has(currentPage);
  };

  // Get the page number for a logical step
  const getPageForLogicalStep = (logicalStep: number) => {
    switch (logicalStep) {
      case 1: return 1;
      case 2: return 2;
      case 3: return 3;
      case 4: return 4;
      case 5: return 5; // First invoice page (handled specially via currentPage toggle)
      case 6: {
        // Image selection page comes after invoices and repair estimate pages
        return 4 + totalInvoicePages + totalRepairEstimatePages + 1;
      }
      case 7: {
        // First recommendation page (or setup page if none created)
        const imageSelectionPage = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
        const recommendationStart = imageSelectionPage + 1;
        return recommendationStart;
      }
      case 8: {
        // First inspection image page (after recommendation pages)
        const imageSelectionPage = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
        const recommendationStart = imageSelectionPage + 1;
        const totalRecommendationPages = formData.repairEstimatePages?.length || 0;
        const imagePagesStart = recommendationStart + Math.max(1, totalRecommendationPages);
        return imagePagesStart;
      }
      case 9: return 0; // Not used; handled via currentPage toggle
      case 10: return totalPages - 2; // First Step 10 page (Part 3)
      default: return 1;
    }
  };

  // Get all page numbers for a logical step (for display purposes)
  const getPagesForLogicalStep = (logicalStep: number): number[] => {
    switch (logicalStep) {
      case 1:
        return [1];
      case 2:
        return [2];
      case 3:
        return [3];
      case 4:
        return [4];
      case 5: {
        // Invoice and Repair Estimate pages
        const invoiceStart = 5;
        const pages: number[] = [];
        for (let i = 0; i < totalInvoicePages + totalRepairEstimatePages; i++) {
          pages.push(invoiceStart + i);
        }
        return pages;
      }
      case 6: {
        // Image selection page
        const page = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
        return [page];
      }
      case 7: {
        // Recommendation pages
        const imageSelectionPage = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
        const recommendationStart = imageSelectionPage + 1;
        const totalRecommendationPages = formData.repairEstimatePages?.length || 0;
        const pages: number[] = [];
        for (let i = 0; i < Math.max(1, totalRecommendationPages); i++) {
          pages.push(recommendationStart + i);
        }
        return pages;
      }
      case 8: {
        // Inspection image pages
        const imageSelectionPage = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
        const recommendationStart = imageSelectionPage + 1;
        const totalRecommendationPages = formData.repairEstimatePages?.length || 0;
        const imagePagesStart = recommendationStart + Math.max(1, totalRecommendationPages);
        const totalImagePages = getTotalImagePages();
        const pages: number[] = [];
        for (let i = 0; i < totalImagePages; i++) {
          pages.push(imagePagesStart + i);
        }
        return pages;
      }
      case 9: {
        // Step 9 pages (3 pages) immediately before Step 10
        const step9Start = totalPages - 5; // first Step 9 page
        return [step9Start, step9Start + 1, step9Start + 2];
      }
      case 10: {
        // Step 10 pages start (first Step 10 page)
        const step10Start = totalPages - 2;
        return [step10Start];
      }
      default:
        return [];
    }
  };

  // Check if any page in a logical step is included (for display purposes)
  const hasAnyPageIncludedForStep = (logicalStep: number): boolean => {
    const pages = getPagesForLogicalStep(logicalStep);
    return pages.some(page => includedPages.has(page));
  };

  // Block mobile devices until they switch to desktop view
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      
      if (isMobileDevice) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  const updateFormData = (data: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  // Load existing report for editing when coming from Library
  useEffect(() => {
    const loadForEdit = async () => {
      let ctxRaw: string | null = null;
      try { ctxRaw = localStorage.getItem('edit_context'); } catch {}
      if (!ctxRaw) return;
      let ctx: any = null;
      try { ctx = JSON.parse(ctxRaw); } catch { return; }
      const clientId: string | undefined = ctx?.clientId;
      const dateISO: string | undefined = ctx?.date;
      const explicitReportId: string | undefined = ctx?.reportId;
      if (!explicitReportId && (!clientId || !dateISO)) return;
      try {
        // Immediately switch UI to form and show loading to avoid flicker
        setCurrentStep('form');
        setCurrentPage(1);
        setIsEditLoading(true);
        setGenerationStatus('editing');
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw userErr;
        const userId = userData?.user?.id;
        if (!userId) throw new Error('Not signed in');

        // Prefer the exact report the user clicked "Edit" on. Falling back to a
        // date-range lookup (below) picks whichever report happens to be first for
        // that client/day, which is ambiguous when a client has multiple reports
        // on the same date.
        let report: any = explicitReportId
          ? await reportsApi.get(explicitReportId).then(r => r.data).catch(() => null)
          : null;

        if (!report && clientId && dateISO) {
          const start = new Date(dateISO + 'T00:00:00Z').toISOString();
          const end = new Date(new Date(dateISO).getTime() + 24*60*60*1000).toISOString();

          const { data: reportsData } = await reportsApi.list({
            client_id: clientId,
            created_after: start,
            created_before: end,
            limit: 1,
          });
          report = (reportsData || [])[0];
        }
        if (!report) return;
        const reportId = report.id as string;
        setCurrentReportId(reportId); // Store report ID for later updates
        setIsEditingExistingReport(true); // This report existed before this session - never delete it

        // Fetch step JSONs and page inclusions in parallel. A 404 here just means that step
        // was never saved (normal for a partially-filled report) - but any OTHER failure (the
        // backend being unreachable, a 500, etc.) must not be treated the same way, or a real
        // outage looks identical to "this report genuinely has no data".
        const loadFailures: string[] = [];
        const fetchStepOrNull = (table: StepTable, label: string) =>
          stepsApi.get(table, reportId).catch((err: any) => {
            if (err?.status !== 404) {
              console.error(`Failed to load ${label} for report ${reportId}:`, err);
              loadFailures.push(label);
            }
            return null;
          });

        const [s1, s3, s5i, s5p2, s6, s7, s8, pageInclusionsRes] = await Promise.all([
          fetchStepOrNull('step1_json', 'Step 1 (client info)'),
          fetchStepOrNull('step3_json', 'Step 3 (cover letter)'),
          fetchStepOrNull('step5_invoice_json', 'Step 5 (invoice)'),
          fetchStepOrNull('step5_part2_json', 'Step 5 (repair estimate)'),
          fetchStepOrNull('step6_json', 'Step 6 (images)'),
          fetchStepOrNull('step7_json', 'Step 7 (recommendations)'),
          fetchStepOrNull('step8_json', 'Step 8 (inspection images)'),
          pageInclusionsApi.list(reportId).catch((err: any) => {
            console.error(`Failed to load page inclusions for report ${reportId}:`, err);
            loadFailures.push('Page inclusions');
            return { data: [] };
          }),
        ]);

        if (loadFailures.length > 0) {
          alert(
            `Warning: couldn't load ${loadFailures.join(', ')} for this report ` +
            `(check your connection). Those sections may appear empty even though data exists - ` +
            `reloading and trying again is safer than assuming it's actually missing.`
          );
        }

        const step1 = stabilizeCapturifyUrls((s1 as any)?.data?.data || {});
        const step5Inv = stabilizeCapturifyUrls((s5i as any)?.data?.data || {});
        const step5p2 = stabilizeCapturifyUrls((s5p2 as any)?.data?.data || {});
        const step6j = stabilizeCapturifyUrls((s6 as any)?.data?.data || {});
        const step7j = stabilizeCapturifyUrls((s7 as any)?.data?.data || {});
        const step8j = stabilizeCapturifyUrls((s8 as any)?.data?.data || {});

        // Map to formData
        const mapped: Partial<FormData> = {
          clientName: step1['Client Name'] || report.client_name || '',
          clientAddress: step1['Client Address'] || '',
          chimneyType: (step1['Chimney Type'] || '').toString().toLowerCase(),
          reportDate: step1['Report Date'] || new Date().toISOString().split('T')[0],
          timelineCoverImage: step1['House Image Link'] || '',
          invoiceData: {
            invoiceNumber: step5Inv['Invoice Number'] || '',
            paymentMethod: step5Inv['Method of Payment'] || '',
            paymentNumber: step5Inv['Payment No'] || '',
            notes: step5Inv['Notes'] || '',
            rows: (step5Inv['Invoice Table'] || []).map((r: any) => ({
              id: r.id || String(Math.random()),
              description: r.description || '',
              unit: r.unit || '',
              price: r.price || ''
            }))
          },
          notes: step5p2['Notes'] || '',
          repairEstimateData: {
            estimateNumber: step5p2['Estimate Number'] || '',
            paymentMethod: step5p2['Method of Payment'] || '',
            paymentNumber: step5p2['Payment No'] || '',
            rows: (step5p2['Section 1 Rows'] || []).map((r: any) => ({
              id: r.id || String(Math.random()),
              description: r.description || '',
              unit: r.unit || '',
              price: r.price || ''
            }))
          },
          // Build recommendationSections from all sections in the database
          recommendationSections: (() => {
            const sections: Array<{ id: string; title: string; rows: any[] }> = [];
            let sectionIndex = 1;
            while (step5p2[`Section ${sectionIndex} Title`] || step5p2[`Section ${sectionIndex} Rows`]) {
              sections.push({
                id: String(sectionIndex),
                title: step5p2[`Section ${sectionIndex} Title`] || `Repair Estimate ${sectionIndex}`,
                rows: (step5p2[`Section ${sectionIndex} Rows`] || []).map((r: any) => ({
                  id: r.id || String(Math.random()),
                  description: r.description || '',
                  unit: r.unit || '',
                  price: r.price || ''
                }))
              });
              sectionIndex++;
            }
            return sections.length > 0 ? sections : undefined;
          })(),
          // Keep old properties for backward compatibility
          recommendationSection1Title: step5p2['Section 1 Title'] || 'Repair Estimate 1',
          showRecommendationSection2: !!step5p2['Section 2 Rows'],
          showRepairEstimateSummary: step5p2['Show Summary Table'] !== false,
          recommendationSection2Title: step5p2['Section 2 Title'] || '',
          recommendationSection2: step5p2['Section 2 Rows'] ? {
            rows: (step5p2['Section 2 Rows'] || []).map((r: any) => ({
              id: r.id || String(Math.random()),
              description: r.description || '',
              unit: r.unit || '',
              price: r.price || ''
            }))
          } : undefined,
          selectedImages: (step6j['Selected Images'] || []).map((img: any) => ({ 
            id: img.id, 
            url: img.url,
            positionX: img.positionX,
            positionY: img.positionY,
            width: img.width,
            height: img.height,
            pageNumber: img.pageNumber || 1
          })),
          step6TextPositionX: step6j['Text Position']?.x,
          step6TextPositionY: step6j['Text Position']?.y,
          step6CurrentPage: step6j['Current Page'] || 1,
          step6TotalPages: step6j['Total Pages'] || 1,
          // Build scrapedImages from selected (step6) + inspection (step8)
          scrapedImages: (() => {
            const selected = (step6j['Selected Images'] || []).map((img: any) => ({ 
              id: img.id || `sel-${Math.random()}`, 
              url: img.url,
              positionX: img.positionX,
              positionY: img.positionY,
              width: img.width,
              height: img.height,
              pageNumber: img.pageNumber || 1
            }));
            const inspection = (step8j['Inspection Images'] || step8j['inspectionImages'] || []).map((img: any) => ({ id: img.id || `ins-${Math.random()}`, url: img.url }));
            const uploaded = (step8j['Uploaded Images'] || []).map((img: any) => ({ id: img.id || `uploaded-${Math.random()}`, url: img.url }));
            const seen = new Set<string>();
            const merged: any[] = [];
            [...selected, ...inspection, ...uploaded].forEach((img) => {
              const key = img.url;
              if (!seen.has(key)) { seen.add(key); merged.push(img); }
            });
            return merged;
          })(),
          excludedStep8Images: [],
          inspectionImagesOrder: step8j['Inspection Images Order'] || [],
          uploadedInspectionImages: (step8j['Uploaded Images'] || []).map((img: any) => ({ 
            id: img.id || `uploaded-${Math.random()}`, 
            url: img.url 
          })),
          step8ImageLayouts: step8j['Image Layouts'] || {},
          repairEstimatePages: (step7j['Recommendations'] || []).map((p: any) => ({
            id: String(Math.random()),
            reviewImage: p.reviewImage,
            customRecommendation: '',
            repairEstimateData: {
              manualEntry: false,
              rows: (p.rows || []).map((r: any) => ({
                id: r.id || String(Math.random()),
                description: r.description || '',
                unit: r.unit || '',
                price: r.price || '',
                recommendation: r.recommendation || ''
              }))
            }
          }))
        } as any;

        setFormData(prev => ({ ...prev, ...mapped }));
        
        // Load page inclusions from database
        const inclusionsData: number[] = (pageInclusionsRes as any)?.data || [];
        const loadedIncludedPages = new Set<number>(inclusionsData);
        
        // Set page inclusions (will be updated when totalPages is calculated)
        if (loadedIncludedPages.size > 0) {
          setIncludedPages(loadedIncludedPages);
          // Set maxInitializedPage to the maximum page number found
          const maxPage = Math.max(...Array.from(loadedIncludedPages), 0);
          maxInitializedPage.current = maxPage;
          hasInitializedPages.current = true;
        }
        // If no inclusions found, the useEffect will handle default initialization
      } catch (e) {
        // ignore
      } finally {
        // Clear any step caches to avoid stale state
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i) || '';
            if (k.startsWith('sumoquote_v2_')) {
              localStorage.removeItem(k);
            }
          }
          localStorage.removeItem('edit_context');
        } catch {}
        setIsEditLoading(false);
        setGenerationStatus(null);
      }
    };
    loadForEdit();
  }, []);

  // Load webhook data when provided
  useEffect(() => {
    if (!webhookData || hasConsumedWebhookData.current) return;

    try {
      console.log('Loading webhook data:', webhookData);
      hasConsumedWebhookData.current = true; // Mark as consumed to prevent re-loading on refresh
      
      // Extract webhook log ID if present
      const webhookId = webhookData._webhookLogId || null;
      if (webhookId) {
        setWebhookLogId(webhookId);
        console.log('Webhook Log ID set:', webhookId);
      }
      
      // Extract formData from webhook payload if it exists
      // Stored Capturify links may be expired presigned URLs - swap in stable ones.
      const sourceData = stabilizeCapturifyUrls(webhookData.formData || webhookData);
      
      // Map webhook data to FormData structure
      const mappedData: Partial<FormData> = {
        clientName: sourceData.clientName || webhookData.clientName || '',
        clientAddress: sourceData.clientAddress || webhookData.clientAddress || '',
        chimneyType: (sourceData.chimneyType || webhookData.chimneyType || '').toString().toLowerCase(),
        reportDate: sourceData.reportDate || webhookData.reportDate || new Date().toISOString().split('T')[0],
        timelineCoverImage: sourceData.timelineCoverImage || sourceData.houseImageUrl || webhookData.timelineCoverImage || webhookData.houseImageUrl || '',
        notes: sourceData.notes || webhookData.notes || formData.notes,
      };

      // Handle scraped images if present
      if (sourceData.scrapedImages && Array.isArray(sourceData.scrapedImages)) {
        mappedData.scrapedImages = sourceData.scrapedImages;
      }

      // Handle selected images if present
      if (sourceData.selectedImages && Array.isArray(sourceData.selectedImages)) {
        mappedData.selectedImages = sourceData.selectedImages;
      }

      // Handle invoice URL (add as a selected image for Step6)
      if (sourceData.invoiceUrl) {
        const invoiceImage: ImageItem = {
          id: 'invoice-' + Date.now(),
          url: sourceData.invoiceUrl,
          alt: 'Invoice',
          pageNumber: 1
        };
        // Add to selectedImages (create array if doesn't exist)
        mappedData.selectedImages = [...(mappedData.selectedImages || []), invoiceImage];
      }

      // Handle invoice data if present
      if (sourceData.invoiceData) {
        mappedData.invoiceData = {
          invoiceNumber: sourceData.invoiceData.invoiceNumber || '',
          paymentMethod: sourceData.invoiceData.paymentMethod || '',
          paymentNumber: sourceData.invoiceData.paymentNumber || '',
          notes: sourceData.invoiceData.notes || '',
          rows: sourceData.invoiceData.rows || []
        };
      }

      // Handle repair estimate data if present
      if (sourceData.repairEstimateData) {
        mappedData.repairEstimateData = {
          estimateNumber: sourceData.repairEstimateData.estimateNumber || '',
          paymentMethod: sourceData.repairEstimateData.paymentMethod || '',
          paymentNumber: sourceData.repairEstimateData.paymentNumber || '',
          rows: sourceData.repairEstimateData.rows || []
        };
      }

      // Handle recommendation sections if present
      if (sourceData.recommendationSections && Array.isArray(sourceData.recommendationSections)) {
        mappedData.recommendationSections = sourceData.recommendationSections;
      }

      // Handle repair estimate pages if present
      if (sourceData.repairEstimatePages && Array.isArray(sourceData.repairEstimatePages)) {
        mappedData.repairEstimatePages = sourceData.repairEstimatePages;
      }

      // Update form data with mapped webhook data
      setFormData(prev => ({ ...prev, ...mappedData }));
      
      // Switch to form step and go to first page
      setCurrentStep('form');
      setCurrentPage(1);

      // Handle page inclusions if provided
      if (webhookData.includedPages && Array.isArray(webhookData.includedPages)) {
        const includedSet = new Set<number>(webhookData.includedPages);
        setIncludedPages(includedSet);
        hasInitializedPages.current = true;
      }

      // Log webhook data loaded
      logger.logCustomEvent({
        username: userEmail,
        event_type: 'webhook_data_loaded',
        event_category: 'report',
        event_details: { 
          clientName: mappedData.clientName,
          hasImages: !!(mappedData.scrapedImages && mappedData.scrapedImages.length > 0)
        }
      });

    } catch (error) {
      console.error('Error loading webhook data:', error);
      alert('Failed to load webhook data. Please try again.');
      logger.logFrontendError(
        userEmail,
        'WEBHOOK_DATA_LOAD_ERROR',
        error instanceof Error ? error.message : 'Unknown error',
        error instanceof Error ? error.stack : undefined
      );
    }
  }, [webhookData]);

  // Fetch form data from webhook when navigating to Step 1 or Step 5
  useEffect(() => {
    // Only fetch if we haven't already loaded webhook data and we're on Step 1 or Step 5
    if (webhookData || (currentPage !== 1 && currentPage !== 5)) return;

    const fetchFormDataWebhook = async () => {
      try {
        // Try to fetch comprehensive form data first
        let response = await fetch('/api/webhook/form-data/latest');
        
        // Fall back to invoice-only webhook
        if (!response.ok) {
          response = await fetch('/api/webhook/invoice/latest');
        }

        if (!response.ok) {
          return; // No webhook data available
        }
        
        const result = await response.json();
        if (!result.success || !result.formData && !result.invoiceData) {
          return;
        }

        console.log('📋 Loaded form data from webhook:', result.formData || result.invoiceData);
        
        // Update formData with all data from webhook
        const webhookFormData = result.formData || {};
        
        setFormData(prev => ({
          ...prev,
          // Step 1 data
          clientName: webhookFormData.clientName || prev.clientName,
          clientAddress: webhookFormData.clientAddress || prev.clientAddress,
          chimneyType: webhookFormData.chimneyType || prev.chimneyType,
          reportDate: webhookFormData.reportDate || prev.reportDate,
          timelineCoverImage: webhookFormData.timelineCoverImage || webhookFormData.houseImageUrl || prev.timelineCoverImage,
          
          // Step 5 invoice data
          invoiceData: webhookFormData.invoiceData ? {
            invoiceNumber: webhookFormData.invoiceData.invoiceNumber || '',
            paymentMethod: webhookFormData.invoiceData.paymentMethod || '',
            paymentNumber: webhookFormData.invoiceData.paymentNumber || '',
            notes: webhookFormData.invoiceData.notes || '',
            rows: webhookFormData.invoiceData.rows || []
          } : prev.invoiceData,

          // Step 5 repair estimate data
          repairEstimateData: webhookFormData.repairEstimateData ? {
            estimateNumber: webhookFormData.repairEstimateData.estimateNumber || '',
            paymentMethod: webhookFormData.repairEstimateData.paymentMethod || '',
            paymentNumber: webhookFormData.repairEstimateData.paymentNumber || '',
            rows: webhookFormData.repairEstimateData.rows || []
          } : prev.repairEstimateData,

          // Step 5 Part 2 recommendation sections
          recommendationSections: webhookFormData.recommendationSections || prev.recommendationSections,

          // Step 6 images (add invoice URL if provided)
          selectedImages: (() => {
            let images = webhookFormData.selectedImages || prev.selectedImages || [];
            if (webhookFormData.invoiceUrl) {
              const invoiceImage: ImageItem = {
                id: 'invoice-' + Date.now(),
                url: webhookFormData.invoiceUrl,
                alt: 'Invoice',
                pageNumber: 1
              };
              images = [...images, invoiceImage];
            }
            return images;
          })(),
          scrapedImages: webhookFormData.scrapedImages || prev.scrapedImages,

          // Other optional data
          notes: webhookFormData.notes !== undefined ? webhookFormData.notes : prev.notes,
        }));

        // Log event
        logger.logCustomEvent({
          username: userEmail,
          event_type: 'webhook_form_data_loaded',
          event_category: 'report',
          event_details: {
            clientName: webhookFormData.clientName,
            hasInvoice: !!webhookFormData.invoiceData,
            invoiceNumber: webhookFormData.invoiceData?.invoiceNumber,
            itemCount: webhookFormData.invoiceData?.rows?.length || 0
          }
        });
      } catch (error) {
        console.log('No webhook form data available:', error instanceof Error ? error.message : 'Unknown error');
        // This is normal if no webhook has been sent
      }
    };

    fetchFormDataWebhook();
  }, [currentPage, webhookData, userEmail]);

  // May return a special key via __key to override default storage key when needed
  const buildStepSnapshot = (logicalStep: number) => {
    const formattedChimneyType = (formData.chimneyType || '').trim();
    if (logicalStep === 1) {
      return {
        'Client Name': formData.clientName || '',
        'Client Address': formData.clientAddress || '',
        'Chimney Type': formattedChimneyType ? formattedChimneyType.charAt(0).toUpperCase() + formattedChimneyType.slice(1) : '',
        'Report Date': formData.reportDate || '',
        'House Image Link': formData.timelineCoverImage || ''
      };
    }
    if (logicalStep === 3) {
      return {
        'Client Name': formData.clientName || ''
      };
    }
    if (logicalStep === 5) {
      // Distinguish between Invoice (Step 5) and Repair Estimate (Step 5 Part 2)
      if (typeof isInvoicePage === 'function' && isInvoicePage(currentPage)) {
        return {
          'Invoice Number': formData.invoiceData?.invoiceNumber || '',
          'Method of Payment': formData.invoiceData?.paymentMethod || '',
          'Payment No': formData.invoiceData?.paymentNumber || '',
          'Notes': formData.invoiceData?.notes || '',
          'Invoice Table': formData.invoiceData?.rows || []
        };
      }
      if (typeof isRepairEstimatePage === 'function' && isRepairEstimatePage(currentPage)) {
        // Step 5 Part 2 snapshot
        const section1 = {
          title: formData.recommendationSection1Title || 'Repair Estimate#1',
          rows: formData.repairEstimateData?.rows || []
        };
        const hasSection2 = !!formData.showRecommendationSection2;
        const section2 = hasSection2 ? {
          title: formData.recommendationSection2Title || 'Repair Estimate#2',
          rows: formData.recommendationSection2?.rows || []
        } : undefined;
        return {
          __key: 'sumoquote_v2_step_5_part2',
          data: {
            'Estimate Number': formData.repairEstimateData?.estimateNumber || '',
            'Method of Payment': formData.repairEstimateData?.paymentMethod || '',
            'Payment No': formData.repairEstimateData?.paymentNumber || '',
            'Section 1 Title': section1.title,
            'Section 1 Rows': section1.rows,
            ...(section2 ? {
              'Section 2 Title': section2.title,
              'Section 2 Rows': section2.rows
            } : {}),
            'Notes': formData.notes || ''
          }
        };
      }
    }
    if (logicalStep === 6) {
      // Step 6 - Selected Images
      return {
        'Selected Images': (formData.selectedImages || []).map(img => ({ id: img.id, url: img.url }))
      };
    }
    if (logicalStep === 7) {
      // Step 7 - Recommendation pages (image + table rows per recommendation)
      const pages = formData.repairEstimatePages || [];
      return {
        'Recommendations': pages.map((p: any, idx: number) => ({
          index: idx + 1,
          reviewImage: p.reviewImage,
          rows: (p.repairEstimateData?.rows) || []
        }))
      };
    }
    return null; // Do not save static or unsupported steps
  };

  // Helper functions for managing recommendation pages
  const getCurrentRecommendationPage = () => {
    const pages = formData.repairEstimatePages || [];
    // Always use currentRecommendationPageIndex for consistency
    return pages[currentRecommendationPageIndex];
  };

  // Calculate how many pages a recommendation needs based on number of rows
  const calculateRecommendationPages = (rows: any[]) => {
    if (rows.length <= 1) return 1; // 1 row or less = 1 page
    return 2; // Multiple rows = 2 pages (table + image)
  };

  const addNewRecommendationPage = (reviewImage: string) => {
    const newPage = {
      id: Date.now().toString(),
      reviewImage,
      customRecommendation: '',
      repairEstimateData: {
        manualEntry: false,
        rows: []
      }
    };

    const updatedPages = [...(formData.repairEstimatePages || []), newPage];
    const usedImages = [...(formData.usedReviewImages || []), reviewImage];

    updateFormData({
      repairEstimatePages: updatedPages,
      usedReviewImages: usedImages
    });

    setCurrentRecommendationPageIndex(updatedPages.length - 1);
  };

  const updateCurrentRecommendationPage = (updates: any) => {
    const pages = [...(formData.repairEstimatePages || [])];
    if (pages[currentRecommendationPageIndex]) {
      pages[currentRecommendationPageIndex] = {
        ...pages[currentRecommendationPageIndex],
        ...updates
      };
      updateFormData({ repairEstimatePages: pages });
    }
  };

  const getAvailableImages = () => {
    const usedImages = formData.usedReviewImages || [];
    return (formData.scrapedImages || []).filter((img: any) => !usedImages.includes(img.url));
  };

  const deleteRecommendationPage = (pageIndex: number) => {
    const pages = formData.repairEstimatePages || [];
    const pageToDelete = pages[pageIndex];
    
    if (pageToDelete) {
      // Remove the page
      const updatedPages = pages.filter((_, index) => index !== pageIndex);
      
      // Remove the image from used images
      const updatedUsedImages = (formData.usedReviewImages || []).filter(
        img => img !== pageToDelete.reviewImage
      );
      
      updateFormData({
        repairEstimatePages: updatedPages,
        usedReviewImages: updatedUsedImages
      });
      
      // Adjust current page index if needed
      let newIndex = currentRecommendationPageIndex;
      if (updatedPages.length === 0) {
        newIndex = 0;
      } else if (currentRecommendationPageIndex >= updatedPages.length) {
        newIndex = updatedPages.length - 1;
      } else if (currentRecommendationPageIndex > pageIndex) {
        // If we deleted a page before the current one, adjust the index
        newIndex = currentRecommendationPageIndex - 1;
      }
      // If we deleted a page after the current one, no adjustment needed
      
      setCurrentRecommendationPageIndex(newIndex);
    }
  };

  const changePageImage = (pageIndex: number, newImageUrl: string) => {
    const pages = [...(formData.repairEstimatePages || [])];
    const oldImageUrl = pages[pageIndex]?.reviewImage;
    
    if (pages[pageIndex]) {
      // Update the page image
      pages[pageIndex].reviewImage = newImageUrl;
      
      // Update used images list
      let updatedUsedImages = [...(formData.usedReviewImages || [])];
      
      // Remove old image from used list
      if (oldImageUrl) {
        updatedUsedImages = updatedUsedImages.filter(img => img !== oldImageUrl);
      }
      
      // Add new image to used list
      if (!updatedUsedImages.includes(newImageUrl)) {
        updatedUsedImages.push(newImageUrl);
      }
      
      updateFormData({
        repairEstimatePages: pages,
        usedReviewImages: updatedUsedImages
      });
    }
  };

  const handleImageSelection = (selectedImages: ImageItem[]) => {
    // Calculate total pages based on highest page number assigned to any image
    const maxPageNumber = selectedImages.length > 0 
      ? Math.max(...selectedImages.map(img => img.pageNumber || 1), 1)
      : 1;
    const totalPages = Math.max(1, maxPageNumber);
    
    setFormData(prev => ({ 
      ...prev, 
      selectedImages,
      step6TotalPages: totalPages
    }));
  };
  
  // Move image to different page
  const handleMoveImageToPage = (imageId: string, targetPage: number) => {
    const updatedImages = (formData.selectedImages || []).map(img => 
      img.id === imageId ? { ...img, pageNumber: targetPage } : img
    );
    
    // Recalculate total pages
    const maxPageNumber = updatedImages.length > 0
      ? Math.max(...updatedImages.map(img => img.pageNumber || 1), 1)
      : 1;
    const totalPages = Math.max(1, maxPageNumber);
    
    updateFormData({ 
      selectedImages: updatedImages,
      step6TotalPages: totalPages
    });
  };
  
  // Add new page for invoice images
  const handleAddStep6Page = () => {
    const currentTotalPages = formData.step6TotalPages || 1;
    const newTotalPages = currentTotalPages + 1;
    setFormData(prev => ({ 
      ...prev, 
      step6TotalPages: newTotalPages,
      step6CurrentPage: newTotalPages // Navigate to new page
    }));
    // Navigate to the new page
    const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
    setCurrentPage(step6Start + newTotalPages - 1);
  };
  
  // Remove page (only if empty)
  const handleRemoveStep6Page = (pageNum: number) => {
    const pageImages = (formData.selectedImages || []).filter(img => (img.pageNumber || 1) === pageNum);
    if (pageImages.length > 0) {
      alert(`Cannot remove page ${pageNum} because it contains ${pageImages.length} image(s). Please move or remove the images first.`);
      return;
    }
    
    const currentTotalPages = formData.step6TotalPages || 1;
    if (currentTotalPages <= 1) {
      alert('Cannot remove the last page.');
      return;
    }
    
    // Shift all images from higher pages down
    const updatedImages = (formData.selectedImages || []).map(img => {
      const imgPage = img.pageNumber || 1;
      if (imgPage > pageNum) {
        return { ...img, pageNumber: imgPage - 1 };
      }
      return img;
    });
    
    const newTotalPages = currentTotalPages - 1;
    const newCurrentPage = Math.min(formData.step6CurrentPage || 1, newTotalPages);
    
    updateFormData({ 
      selectedImages: updatedImages,
      step6TotalPages: newTotalPages,
      step6CurrentPage: newCurrentPage
    });
    
    // Navigate to appropriate page if needed
    const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
    setCurrentPage(step6Start + newCurrentPage - 1);
  };
  
  const handleStep6PageChange = (pageNumber: number) => {
    setFormData(prev => ({ ...prev, step6CurrentPage: pageNumber }));
  };
  
  const handleStep6TotalPagesChange = (totalPages: number) => {
    setFormData(prev => ({ ...prev, step6TotalPages: totalPages }));
  };
  
  const handleStep6ImagePositionChange = (imageId: string, x: number, y: number, width: number, height: number, pageNumber?: number) => {
    const updatedImages = (formData.selectedImages || []).map(img =>
      img.id === imageId
        ? { ...img, positionX: x, positionY: y, width, height, pageNumber: pageNumber || img.pageNumber || 1 }
        : img
    );
    updateFormData({ selectedImages: updatedImages });
  };
  
  const handleStep6TextPositionChange = (x: number, y: number, pageNumber?: number) => {
    // For now, we'll store the position for the current page
    // In the future, we could store per-page positions if needed
    updateFormData({ step6TextPositionX: x, step6TextPositionY: y });
  };

  const handleStep8ImageLayoutChange = (imageId: string, layout: { x: number; y: number; width: number; height: number }) => {
    updateFormData({
      step8ImageLayouts: {
        ...(formData.step8ImageLayouts || {}),
        [imageId]: layout
      }
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const currentSelection = formData.selectedImages || [];
    const MAX_IMAGES_PER_PAGE = 4;
    
    const newImages: ImageItem[] = [];
    let processedCount = 0;
    const filesToProcess = Array.from(files);

    filesToProcess.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          // Distribute images across pages (4 per page)
          // Calculate which page this image should go to based on total count after adding
          const totalImagesAfterAdd = currentSelection.length + newImages.length + 1;
          const targetPage = Math.ceil(totalImagesAfterAdd / MAX_IMAGES_PER_PAGE);
          
          const newImage: ImageItem = {
            id: `uploaded-${Date.now()}-${index}`,
            url: event.target?.result as string,
            alt: file.name,
            pageNumber: targetPage
          };
          newImages.push(newImage);
          processedCount++;

          if (processedCount === filesToProcess.length) {
            const updatedSelection = [...currentSelection, ...newImages];
            // Calculate total pages based on highest page number
            const maxPageNumber = updatedSelection.length > 0
              ? Math.max(...updatedSelection.map(img => img.pageNumber || 1), 1)
              : 1;
            const totalPages = Math.max(1, maxPageNumber);
            setFormData(prev => ({ 
              ...prev, 
              selectedImages: updatedSelection,
              step6TotalPages: totalPages
            }));
            
            // Log image upload
            const reportId = currentReportId || formData.clientName || 'new_report';
            await logger.logImageUploaded(userEmail, reportId, 6, 'inspection_images', {
              file_name: file.name,
              file_size_kb: Math.round(file.size / 1024),
              total_images: updatedSelection.length
            });
          }
        };
        reader.readAsDataURL(file);
      } else {
        processedCount++;
      }
    });

    if (e.target) {
      e.target.value = '';
    }
  };

  const handleCustomRecommendationChange = (customRecommendation: string) => {
    const pages = [...(formData.repairEstimatePages || [])];
    if (pages[currentRecommendationPageIndex]) {
      pages[currentRecommendationPageIndex].customRecommendation = customRecommendation;
      updateFormData({ repairEstimatePages: pages });
    }
  };

  const handleDataExtracted = async (data: {
    clientName: string;
    clientAddress: string;
    chimneyType: string;
    reportDate: string;
    timelineCoverImage: string;
    scrapedImages: ImageItem[];
    dataSourceUrl?: string;
  }) => {
    // Update form data first
    setFormData({
      ...formData,
      clientName: data.clientName,
      clientAddress: data.clientAddress,
      chimneyType: data.chimneyType,
      reportDate: data.reportDate,
      timelineCoverImage: data.timelineCoverImage,
      scrapedImages: data.scrapedImages,
      dataSourceUrl: data.dataSourceUrl,
      selectedImages: [], // Initialize empty selection
      excludedStep8Images: [], // Reset excluded images when new data is loaded
      inspectionImagesOrder: [], // Reset image order
      uploadedInspectionImages: [], // Reset uploaded images
      step8ImageLayouts: {} // Reset image layouts
    });
    
    // Create report in database immediately after data extraction
    setIsSavingStep(true);
    try {
      // Check if user is authenticated
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user?.id) {
        console.warn('User not authenticated, skipping report creation');
        setCurrentReportId(null);
        setCurrentStep('form');
        return;
      }
      
      const userId = userData.user.id;
      const clientName = (data.clientName || '').trim();
      
      // 1) Ensure client exists (reuse by full_name if present)
      let clientId: string | null = null;
      if (clientName) {
        try {
          // Try to find existing client by name
          const { data: existingClients } = await clientsApi.list({ full_name: clientName });

          if (existingClients && existingClients.length > 0) {
            clientId = existingClients[0].id;
          } else {
            const { data: insertedClient } = await clientsApi.create({ full_name: clientName, created_by: userId });
            clientId = insertedClient?.id || null;
          }
        } catch (findOrCreateClientError) {
          console.error('Error finding/creating client:', findOrCreateClientError);
        }
      }

      // 2) Create new report
      const { data: reportRow } = await reportsApi.create({
        client_name: clientName || undefined,
        client_id: clientId || undefined,
        created_by: userId,
      });
      const reportId = reportRow.id;
      setCurrentReportId(reportId);
      console.log('Report created successfully:', reportId);

      // Log report creation started with actual report ID
      await logger.logReportCreationStarted(userEmail, reportId, {
        clientName: data.clientName,
        clientAddress: data.clientAddress,
        chimneyType: data.chimneyType,
        reportDate: data.reportDate,
        dataSourceUrl: data.dataSourceUrl,
        imageCount: data.scrapedImages?.length || 0
      });
    } catch (error) {
      console.error('Failed to create report:', error);
      setCurrentReportId(null);
    } finally {
      setIsSavingStep(false);
    }

    setCurrentStep('form');
  };

  const handleManualEntry = () => {
    setFormData({
      clientName: '',
      clientAddress: '',
      chimneyType: '',
      reportDate: new Date().toISOString().split('T')[0],
      timelineCoverImage: '',
      scrapedImages: [],
      selectedImages: [],
      invoiceData: {
        invoiceNumber: '',
        paymentMethod: '',
        paymentNumber: '',
        notes: '',
        rows: []
      },
      repairEstimatePages: [],
      usedReviewImages: [],
      excludedStep8Images: [],
      inspectionImagesOrder: [],
      uploadedInspectionImages: [],
      step8ImageLayouts: {},
      notes: 'This quote is good for 30 days from date of service. Deposits for scheduled future service is non-refundable.'
    });
    setCurrentReportId(null); // Reset report ID for new report
    setCurrentStep('form');
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    const reportId = currentReportId || formData.clientName || 'new_report';
    
    // Log PDF generation started
    await logger.logPdfGenerationStarted(userEmail, reportId);
    
    try {
      // Find the existing preview div that contains the Page components
      const previewDiv = document.querySelector('[data-preview="true"]');
      if (!previewDiv) {
        throw new Error('Preview div not found. Please ensure you are on the form step.');
      }

      // Use state-based mobile detection
      const isMobileDevice = isMobile;
      
      // Create a temporary clone for PDF generation with adjusted positioning
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '595px';
      tempContainer.style.height = '842px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.overflow = 'hidden';
      tempContainer.style.margin = '0';
      tempContainer.style.padding = '0';
      tempContainer.style.border = 'none';
      tempContainer.style.outline = 'none';
      
      // Clone the preview content
      const clonedContent = previewDiv.cloneNode(true) as HTMLElement;
      
      // Adjust the text position for PDF generation (move up by 5px)
      const titleText = clonedContent.querySelector('.absolute.w-\\[402px\\]');
      if (titleText) {
        (titleText as HTMLElement).style.top = '-5px'; // Override the top-1.5 class, move up by 5px
      }
      
      // Mobile-specific adjustments for PDF generation
      if (isMobileDevice) {
        // Force desktop-like scaling and positioning for mobile PDF generation
        const page1Element = clonedContent.querySelector('[data-preview="true"] > div') as HTMLElement;
        if (page1Element) {
          // Reset any mobile-specific transforms
          page1Element.style.transform = 'none';
          page1Element.style.scale = '1';
          page1Element.style.width = '595px';
          page1Element.style.height = '842px';
          page1Element.style.maxWidth = '595px';
          page1Element.style.maxHeight = '842px';
          
          // Ensure proper positioning
          page1Element.style.position = 'relative';
          page1Element.style.left = '0';
          page1Element.style.top = '0';
          
          // Remove any margins that might cause extra spacing
          page1Element.style.margin = '0';
          page1Element.style.padding = '0';
        }
        
        // Reset any mobile-specific styles on child elements
        const allElements = clonedContent.querySelectorAll('*');
        allElements.forEach((el) => {
          const element = el as HTMLElement;
          // Remove mobile-specific transforms and positioning
          if (element.style.transform && element.style.transform.includes('scale')) {
            element.style.transform = 'none';
          }
          if (element.style.scale) {
            element.style.scale = '1';
          }
          
          // Remove any margins and padding that could cause extra spacing
          element.style.margin = '0';
          element.style.padding = '0';
          
          // Ensure no border spacing
          if (element.tagName === 'TABLE') {
            element.style.borderCollapse = 'collapse';
            element.style.borderSpacing = '0';
          }
        });
      }
      
      tempContainer.appendChild(clonedContent);
      document.body.appendChild(tempContainer);
      
      // Wait a moment for rendering
      await new Promise(resolve => setTimeout(resolve, 100));
      

      
      // Convert to PDF using jsPDF
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      
      // Generate PDF with both pages
      await generateMultiPagePDF(pdf, isMobileDevice);
      
      // Generate PDF blob and show approval modal
        const clientName = sanitizeFileName(formData.clientName || 'client');
        const clientAddress = sanitizeFileName(formData.clientAddress || '');
        const fileName = `A_Step_In_Time_${clientName}${clientAddress ? '_' + clientAddress : ''}.pdf`;
        
        // Save PDF to blob for later use
        const pdfBlob: Blob = pdf.output('blob');
        
        // Download PDF to user's device
        pdf.save(fileName);
        
        // Store PDF blob and filename for approval flow
        setGeneratedPdfBlob(pdfBlob);
        setGeneratedPdfFileName(fileName);
        
        // Log PDF generation success
        const reportId = currentReportId || formData.clientName || 'new_report';
        await logger.logPdfGenerationSuccess(userEmail, reportId, {
          file_name: fileName,
          file_size_kb: Math.round(pdfBlob.size / 1024)
        });
        
        // Show approval modal instead of immediately uploading
        setShowApprovalModal(true);
      
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      
      // Log PDF generation error
      const reportId = currentReportId || formData.clientName || 'new_report';
      await logger.logPdfGenerationFailed(userEmail, reportId, error?.message || 'Unknown error', {
        error_details: error?.toString()
      });
      
      alert('Error generating PDF. Please try again. Make sure all components are visible in the preview.');
    } finally {
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  // Download PDF only (no saving to database or uploading)
  const handleDownloadPDF = async () => {
    // Check if required fields are filled
    if (!formData.clientName || !formData.clientAddress || !formData.chimneyType) {
      alert('Please fill in all required fields before downloading the PDF.');
      return;
    }
    
    setIsGenerating(true);
    setGenerationStatus('generating');
    try {
      // Find the existing preview div that contains the Page components
      const previewDiv = document.querySelector('[data-preview="true"]');
      if (!previewDiv) {
        throw new Error('Preview div not found. Please ensure you are on the form step.');
      }

      // Use state-based mobile detection
      const isMobileDevice = isMobile;
      
      // Create a temporary clone for PDF generation with adjusted positioning
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '595px';
      tempContainer.style.height = '842px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.overflow = 'hidden';
      tempContainer.style.margin = '0';
      tempContainer.style.padding = '0';
      tempContainer.style.border = 'none';
      tempContainer.style.outline = 'none';
      
      // Clone the preview content
      const clonedContent = previewDiv.cloneNode(true) as HTMLElement;
      
      // Adjust the text position for PDF generation (move up by 5px)
      const titleText = clonedContent.querySelector('.absolute.w-\\[402px\\]');
      if (titleText) {
        (titleText as HTMLElement).style.top = '-5px';
      }
      
      // Mobile-specific adjustments for PDF generation
      if (isMobileDevice) {
        const page1Element = clonedContent.querySelector('[data-preview="true"] > div') as HTMLElement;
        if (page1Element) {
          page1Element.style.transform = 'none';
          page1Element.style.scale = '1';
          page1Element.style.width = '595px';
          page1Element.style.height = '842px';
          page1Element.style.maxWidth = '595px';
          page1Element.style.maxHeight = '842px';
          page1Element.style.position = 'relative';
          page1Element.style.left = '0';
          page1Element.style.top = '0';
          page1Element.style.margin = '0';
          page1Element.style.padding = '0';
        }
        
        const allElements = clonedContent.querySelectorAll('*');
        allElements.forEach((el) => {
          const element = el as HTMLElement;
          if (element.style.transform && element.style.transform.includes('scale')) {
            element.style.transform = 'none';
          }
          if (element.style.scale) {
            element.style.scale = '1';
          }
          element.style.margin = '0';
          element.style.padding = '0';
          
          if (element.tagName === 'TABLE') {
            element.style.borderCollapse = 'collapse';
            element.style.borderSpacing = '0';
          }
        });
      }
      
      tempContainer.appendChild(clonedContent);
      document.body.appendChild(tempContainer);
      
      // Wait a moment for rendering
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Convert to PDF using jsPDF
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      
      // Generate PDF with both pages
      await generateMultiPagePDF(pdf, isMobileDevice);
      
      // Save PDF directly (download only, no upload)
      const clientName = sanitizeFileName(formData.clientName || 'client');
      const clientAddress = sanitizeFileName(formData.clientAddress || '');
      const fileName = `A_Step_In_Time_${clientName}${clientAddress ? '_' + clientAddress : ''}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again. Make sure all components are visible in the preview.');
    } finally {
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  // Upload the generated PDF to the backend using multipart/form-data
  const uploadReportPdf = async (pdfBlob: Blob, fileName: string, clientName: string): Promise<string> => {
    const API_BASE = process.env.REACT_APP_API_BASE || 'https://adminbackend.chimneysweeps.com';
    const API_KEY = process.env.REACT_APP_API_KEY || 'pit-0988e019-bccb-4b77-a0a6-f50e56c58522';
    const formData = new FormData();
    formData.append('file', pdfBlob, fileName);
    formData.append('website', 'mysite');
    formData.append('prefix', 'pdfs');
    formData.append('client_name', clientName);
    
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      body: formData,
      headers: API_KEY ? { 'X-API-Key': API_KEY } : undefined
    });
    
    
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
    
    // Try to parse response as JSON to get the URL
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        const data = await response.json();
        // Prioritize download_url, then try other URL fields
        // If we have a regular URL but no download_url, we can use the regular URL as download link
        const downloadUrl = data.download_url || data.downloadUrl;
        const regularUrl = data.url || data.file_url || data.pdf_url || data.fileUrl;
        
        // Return download_url if available, otherwise use regular URL (which can serve as download link)
        return downloadUrl || regularUrl || '';
      } catch {
        return '';
      }
    } else {
      // If response is not JSON, try to get URL from response text
      const text = await response.text().catch(() => '');
      // Try to extract URL from text if it's a plain text response
      const urlMatch = text.match(/https?:\/\/[^\s"']+/);
      return urlMatch ? urlMatch[0] : '';
    }
  };

  // Upload the approved PDF to LeadConnectorHQ media storage as well
  // Tries:
  // 1) hosted=true + fileUrl (reuses the URL we already uploaded to your backend)
  // 2) hosted=false + file (fallback: uploads pdfBlob directly)
  const uploadPdfToLeadConnector = async (pdfBlob: Blob, fileName: string, pdfUrl: string | null): Promise<string | null> => {
    const LEADCONNECTOR_ACCESS_TOKEN = process.env.REACT_APP_LEADCONNECTOR_ACCESS_TOKEN || 'pit-6fa8d6b3-4ec9-4649-add0-8341cd7399d2';
    const LEADCONNECTOR_MEDIA_UPLOAD_URL =
      process.env.REACT_APP_LEADCONNECTOR_MEDIA_UPLOAD_URL ||
      'https://services.leadconnectorhq.com/medias/upload-file';
    const LEADCONNECTOR_VERSION = process.env.REACT_APP_LEADCONNECTOR_VERSION || '2021-07-28';

    if (!LEADCONNECTOR_ACCESS_TOKEN) {
      console.warn('LeadConnector access token not configured. Skipping LeadConnector media upload.');
      return null;
    }

    const extractUploadedUrl = (data: any): string | null => {
      return (
        data?.download_url ||
        data?.downloadUrl ||
        data?.url ||
        data?.file_url ||
        data?.fileUrl ||
        data?.media_url ||
        data?.mediaUrl ||
        null
      );
    };

    const hostedUpload = async (): Promise<string | null> => {
      if (!pdfUrl || !/^https?:\/\//i.test(pdfUrl)) return null;

      const fd = new FormData();
      fd.append('hosted', 'true');
      fd.append('fileUrl', pdfUrl);
      fd.append('name', fileName);

      const resp = await fetch(LEADCONNECTOR_MEDIA_UPLOAD_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${LEADCONNECTOR_ACCESS_TOKEN}`,
          Version: LEADCONNECTOR_VERSION,
        },
        body: fd,
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`LeadConnector hosted upload failed: ${resp.status} ${text}`);
      }

      const data = await resp.json().catch(() => null);
      return extractUploadedUrl(data) || null;
    };

    const directUpload = async (): Promise<string | null> => {
      const fd = new FormData();
      fd.append('file', pdfBlob, fileName);
      fd.append('name', fileName);

      const resp = await fetch(LEADCONNECTOR_MEDIA_UPLOAD_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${LEADCONNECTOR_ACCESS_TOKEN}`,
          Version: LEADCONNECTOR_VERSION,
        },
        body: fd,
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`LeadConnector direct upload failed: ${resp.status} ${text}`);
      }

      const data = await resp.json().catch(() => null);
      return extractUploadedUrl(data) || null;
    };

    // 1) Hosted by URL (preferred)
    try {
      const hostedUrl = await hostedUpload();
      if (hostedUrl) return hostedUrl;
    } catch (err) {
      // Fall back to direct upload
      console.warn('LeadConnector hosted upload failed, falling back to direct upload:', err);
    }

    // 2) Direct upload from blob
    return await directUpload().catch((err) => {
      console.error('LeadConnector direct upload failed:', err);
      return null;
    });
  };

  // Send email with PDF
  const sendEmailPdf = async (email: string, pdfUrl: string, fileName: string): Promise<void> => {
    const API_BASE = process.env.REACT_APP_API_BASE || 'https://adminbackend.chimneysweeps.com';
    const API_KEY = process.env.REACT_APP_API_KEY || 'pit-0988e019-bccb-4b77-a0a6-f50e56c58522';
    
    // Send as FormData
    const formData = new FormData();
    formData.append('email', email);
    formData.append('pdf_url', pdfUrl);
    formData.append('filename', fileName);
    
    const response = await fetch(`${API_BASE}/send_email_pdf`, {
      method: 'POST',
      body: formData,
      headers: API_KEY ? { 'X-API-Key': API_KEY } : undefined
    });
    
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Failed to send email: ${response.status} ${text}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    // Check if required fields are filled
    if (!formData.clientName || !formData.clientAddress || !formData.chimneyType) {
      alert('Please fill in all required fields before generating the report.');
      return;
    }
    
    // Generate PDF first (will show approval modal)
      setIsGenerating(true);
    setGenerationStatus('generating');
    await generatePDF();
  };

  // Handle approval - save data, upload PDF, then ask for email
  const handleApproveReport = async () => {
    if (!generatedPdfBlob || !generatedPdfFileName) {
      alert('PDF not found. Please try generating again.');
      return;
    }

    setShowApprovalModal(false);
    setIsGenerating(true);

    try {
      // Step 1: Save report and step-wise data to Supabase
      let savedReportId: string | null = null;
      try {
      setGenerationStatus('saving');
      savedReportId = await saveReportToSupabase();
    } catch (err: any) {
      console.error('Failed saving to Supabase:', err);
      alert(`Failed saving to database: ${err?.message || 'Unknown error'}`);
        // Continue to upload even if DB save fails
      }

      // Step 2: Upload PDF to backend
      let pdfUrl: string | null = null;
      try {
        setGenerationStatus('uploading');
        pdfUrl = await uploadReportPdf(generatedPdfBlob, generatedPdfFileName, formData.clientName || 'client');
        setUploadedPdfUrl(pdfUrl);

        // Step 2.2: Upload PDF to LeadConnector media storage (optional but requested)
        try {
          setGenerationStatus('uploading');
          const leadConnectorPdfUrl = await uploadPdfToLeadConnector(generatedPdfBlob, generatedPdfFileName, pdfUrl);
          console.log('LeadConnector media upload result:', leadConnectorPdfUrl);
        } catch (leadErr: any) {
          console.warn('LeadConnector media upload failed (non-blocking):', leadErr?.message || leadErr);
        }
        
        // Log PDF upload success
        const reportId = savedReportId || currentReportId || formData.clientName || 'new_report';
        await logger.logPdfUploadSuccess(userEmail, reportId, {
          pdf_url: pdfUrl,
          file_name: generatedPdfFileName
        });
        
        // Step 2.1: Update report with PDF upload status
        if (savedReportId || currentReportId) {
          const reportId = savedReportId || currentReportId;
          try {
            await reportsApi.patch(reportId as string, {
              pdf_uploaded: true,
              pdf_url: pdfUrl,
              pdf_uploaded_at: new Date().toISOString()
            });
            console.log('✓ Report marked as PDF uploaded');

            // If this was edited from a webhook, update the webhook_logs entry with the PDF URL
            if (webhookLogId && pdfUrl) {
              try {
                const { data: webhookLog } = await webhookLogsApi.get(webhookLogId);

                if (webhookLog) {
                  const updatedPayload = typeof webhookLog.payload === 'string'
                    ? JSON.parse(webhookLog.payload)
                    : webhookLog.payload;

                  updatedPayload.reportDownloadUrl = pdfUrl;
                  updatedPayload.reportGenerated = true;
                  updatedPayload.reportId = reportId;

                  await webhookLogsApi.patch(webhookLogId, {
                    payload: updatedPayload,
                    status: 'completed'
                  });
                  console.log('✓ Webhook log updated with PDF URL');
                }
              } catch (webhookUpdateErr) {
                console.warn('Failed to update webhook log:', webhookUpdateErr);
                // Non-critical error
              }
            }
          } catch (updateError) {
            console.error('Failed to update PDF upload status:', updateError);
            // Don't throw - this is not critical
          }
        }
        
        alert('Report saved and uploaded successfully.');
      } catch (uploadError: any) {
        const message = uploadError?.message || 'Unknown error';
        
        // Log PDF upload failure
        const reportId = savedReportId || currentReportId || formData.clientName || 'new_report';
        await logger.logBackendError(userEmail, 'PDF_UPLOAD_FAILED', message, uploadError?.stack);
        
        alert(`Upload failed: ${message}`);
        // Still show email modal even if upload fails (user might want to send later)
      }

      // Step 3: Show email modal
      setShowEmailModal(true);
      
      // If we were editing, reset the report ID for next time
      if (generationStatus === 'editing') {
        setCurrentReportId(null);
        setWebhookLogId(null);
      }
    } catch (error: any) {
      console.error('Error in approval process:', error);
      alert(`Error: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  // Handle email sending
  const handleSendEmail = async (email: string) => {
    if (!uploadedPdfUrl) {
      alert('PDF URL not available. Please try again.');
      return;
    }

    if (!generatedPdfFileName) {
      alert('PDF filename not available. Please try again.');
      return;
    }

    setIsSendingEmail(true);
    try {
      await sendEmailPdf(email, uploadedPdfUrl, generatedPdfFileName);
      alert(`Email sent successfully to ${email}`);
      setShowEmailModal(false);
      // Reset state
      setGeneratedPdfBlob(null);
      setGeneratedPdfFileName('');
      setUploadedPdfUrl(null);
    } catch (error: any) {
      alert(`Failed to send email: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handle skip email
  const handleSkipEmail = () => {
    setShowEmailModal(false);
    // Reset state
    setGeneratedPdfBlob(null);
    setGeneratedPdfFileName('');
    setUploadedPdfUrl(null);
  };

  const handleCropImage = () => {
    setIsCropping(true);
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    if (croppingImageId) {
      // Cropping image by id across all image sources used in Step 6/8
      const updatedSelected = (formData.selectedImages || []).map(img =>
        img.id === croppingImageId ? { ...img, url: croppedImageUrl } : img
      );
      const updatedScraped = (formData.scrapedImages || []).map(img =>
        img.id === croppingImageId ? { ...img, url: croppedImageUrl } : img
      );
      const updatedUploaded = (formData.uploadedInspectionImages || []).map(img =>
        img.id === croppingImageId ? { ...img, url: croppedImageUrl } : img
      );
      updateFormData({
        selectedImages: updatedSelected,
        scrapedImages: updatedScraped,
        uploadedInspectionImages: updatedUploaded
      });
      setCroppingImageId(null);
    } else {
      // Cropping timeline cover image
      updateFormData({ timelineCoverImage: croppedImageUrl });
    }
    setIsCropping(false);
    setCropData(null);
  };

  const handleCropCancel = () => {
    setIsCropping(false);
    setCropData(null);
    setCroppingImageId(null);
  };

  const handleCropInvoiceImage = (imageId: string) => {
    const image =
      formData.selectedImages?.find(img => img.id === imageId) ||
      formData.scrapedImages?.find(img => img.id === imageId) ||
      formData.uploadedInspectionImages?.find(img => img.id === imageId);
    if (image) {
      setCroppingImageId(imageId);
      setIsCropping(true);
    }
  };

  // Calculate total pages including invoice pages
  const MAX_TABLE_HEIGHT = 400; // Max height for table container
  const ROW_HEIGHT = 50; // Estimated row height
  
  const maxRowsPerPage = Math.floor(MAX_TABLE_HEIGHT / ROW_HEIGHT);
  const ITEMS_PER_PAGE = Math.max(1, maxRowsPerPage); // At least 1 row per page
  
  // Persist the report and step-wise data to Supabase
  // Note: Report is already created in handleDataExtracted, this function updates it with all step data
  const saveReportToSupabase = async (): Promise<string | null> => {
    // Require authenticated user for RLS-backed writes
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr) throw userErr;
    const userId = userData?.user?.id || null;
    if (!userId) {
      throw new Error('Not signed in. Please log in to save reports.');
    }

    // 1) Ensure client exists (reuse by full_name if present)
    const clientName = (formData.clientName || '').trim();
    let clientId: string | null = webhookData?.editingClientId || null;
    
    // If not editing, find or create client by name
    if (!clientId && clientName) {
      // First, try to find by name only (to reuse imported clients, regardless of created_by)
      const { data: existingClients } = await clientsApi.list({ full_name: clientName });
      if (existingClients && existingClients.length > 0) {
        clientId = existingClients[0].id;
      } else {
        const { data: insertedClient } = await clientsApi.create({ full_name: clientName, created_by: userId });
        clientId = insertedClient?.id || null;
      }
    }

    // 2) Create new report (when editing, always create new; when creating, use currentReportId)
    let reportId: string;
    if (generationStatus === 'editing') {
      // When editing, ALWAYS create a new report with TODAY's date
      const { data: reportRow } = await reportsApi.create({
        client_name: clientName || undefined,
        client_id: clientId || undefined,
        created_by: userId,
        webhook_log_id: webhookLogId || undefined,
        source: webhookLogId ? 'webhook' : 'manual'
      });
      reportId = reportRow.id;
      setCurrentReportId(reportId);
      console.log('Editing: Created NEW report', reportId, 'with today\'s date');
    } else if (currentReportId) {
      // Normal update for new reports
      const { data: reportRow } = await reportsApi.patch(currentReportId, {
        client_name: clientName || null,
        client_id: clientId,
      });
      reportId = reportRow?.id || currentReportId;
    } else {
      // Fallback: Create new report if somehow currentReportId is not set
      console.warn('Report ID not found, creating new report');
      const { data: reportRow } = await reportsApi.create({
        client_name: clientName || undefined,
        client_id: clientId || undefined,
        created_by: userId,
        webhook_log_id: webhookLogId || undefined,
        source: webhookLogId ? 'webhook' : 'manual'
      });
      reportId = reportRow.id;
      setCurrentReportId(reportId);
    }

    // 3) JSON step tables upserts
    // Step 1 JSON
    const step1Json = {
      'Client Name': formData.clientName || '',
      'Client Address': formData.clientAddress || '',
      'Chimney Type': (formData.chimneyType || '').trim(),
      'Report Date': formData.reportDate || '',
      'House Image Link': formData.timelineCoverImage || ''
    };
    await stepsApi.upsert('step1_json', reportId, step1Json);

    // Step 3 JSON
    const step3Json = { 'Client Name': formData.clientName || '' };
    await stepsApi.upsert('step3_json', reportId, step3Json);

    // Step 5 (Invoice) JSON
    const invoice = formData.invoiceData || { rows: [] } as any;
    const step5InvoiceJson = {
      'Invoice Number': invoice.invoiceNumber || '',
      'Method of Payment': invoice.paymentMethod || '',
      'Payment No': invoice.paymentNumber || '',
      'Notes': invoice.notes || '',
      'Invoice Table': invoice.rows || []
    };
    await stepsApi.upsert('step5_invoice_json', reportId, step5InvoiceJson);

    // Step 5 Part 2 JSON
    const step5Part2Json = {
      'Estimate Number': formData.repairEstimateData?.estimateNumber || '',
      'Method of Payment': formData.repairEstimateData?.paymentMethod || '',
      'Payment No': formData.repairEstimateData?.paymentNumber || '',
      'Section 1 Title': (formData as any).recommendationSection1Title || 'Repair Estimate 1',
      'Section 1 Rows': formData.repairEstimateData?.rows || [],
      ...( (formData as any).showRecommendationSection2 ? {
        'Section 2 Title': (formData as any).recommendationSection2Title || 'Repair Estimate 2',
        'Section 2 Rows': ((formData as any).recommendationSection2?.rows) || []
      } : {}),
      'Show Summary Table': formData.showRepairEstimateSummary !== false,
      'Notes': formData.notes || ''
    };
    await stepsApi.upsert('step5_part2_json', reportId, step5Part2Json);

    // Step 6 JSON (selected images with positions and text position)
    const step6Json = {
      'Selected Images': (formData.selectedImages || []).map(img => ({ 
        id: img.id, 
        url: img.url,
        positionX: img.positionX,
        positionY: img.positionY,
        width: img.width,
        height: img.height,
        pageNumber: img.pageNumber || 1
      })),
      'Text Position': {
        x: formData.step6TextPositionX,
        y: formData.step6TextPositionY
      },
      'Current Page': formData.step6CurrentPage || 1,
      'Total Pages': formData.step6TotalPages || 1
    };
    await stepsApi.upsert('step6_json', reportId, step6Json);

    // Step 7 JSON (recommendations)
    const step7Json = {
      'Recommendations': (formData.repairEstimatePages || []).map((p: any, idx: number) => ({
        index: idx + 1,
        reviewImage: p.reviewImage,
        rows: (p.repairEstimateData?.rows) || []
      }))
    };
    await stepsApi.upsert('step7_json', reportId, step7Json);

    // Step 8 JSON (inspection images)
    const inspectionImages = getUnusedImages().map((img: any) => ({ id: img.id, url: img.url }));
    const step8Json = { 
      'Inspection Images': inspectionImages,
      'Inspection Images Order': formData.inspectionImagesOrder || [],
      'Uploaded Images': formData.uploadedInspectionImages || []
    };
    await stepsApi.upsert('step8_json', reportId, step8Json);

    // Save page inclusions to database
    try {
      // Get current included pages, and replace whatever was stored for this report
      const currentIncludedPages = Array.from(includedPages);
      await pageInclusionsApi.bulkSet(reportId, currentIncludedPages);
    } catch (error) {
      console.error('Error saving page inclusions:', error);
      // Don't throw - page inclusions are not critical for report generation
    }

    return reportId;
  };

  // Smart pagination: ensure no row is cut off
  const calculateSmartInvoicePages = () => {
    const totalRows = formData.invoiceData?.rows?.length || 0;
    if (totalRows === 0) return 1;
    
    // Use 5 rows per page to ensure no cut-offs
    const ROWS_PER_PAGE = 5;
    return Math.ceil(totalRows / ROWS_PER_PAGE);
  };

   const calculateSmartRepairEstimatePages = () => {
     // Get all sections (new array format or legacy format)
     const allSections = formData.recommendationSections && formData.recommendationSections.length > 0
       ? formData.recommendationSections
       : [
           { id: '1', title: formData.recommendationSection1Title || 'Repair Estimate', rows: formData.repairEstimateData?.rows || [] },
           ...(formData.showRecommendationSection2 && formData.recommendationSection2?.rows && formData.recommendationSection2.rows.length > 0
             ? [{ id: '2', title: formData.recommendationSection2Title || 'Additional Repairs', rows: formData.recommendationSection2.rows }]
             : [])
         ];
     
     // Use the same dynamic pagination logic from Step5-part2.tsx
     const SECTIONS_PER_PAGE = 3;
     const MAX_PAGE_HEIGHT = 700;
     const HEADER_HEIGHT = 150;
     const GAP_BETWEEN_SECTIONS = 80;
     const SUMMARY_TITLE_HEIGHT = 30;
     const NOTES_ESTIMATED_HEIGHT = 100;
     
     const totalSections = allSections.length;
     const hasMultipleSections = totalSections > 1;
     const showSummaryTable = hasMultipleSections && formData.showRepairEstimateSummary !== false;
     const hasNotes = !!formData.notes;
     
     if (totalSections === 0) {
       return hasNotes ? 1 : 0;
     }
     
     // Helper to calculate table height (simplified version of calculateTableHeight)
     const calculateTableHeight = (rows: any[]) => {
       if (rows.length === 0) return 0;
       const headerHeight = 20;
       let dataRowsHeight = 0;
       
       rows.forEach(row => {
         const calculateRowsNeeded = (text: string, baseLength: number = 50) => {
           if (!text || text.trim().length === 0) return 1;
           const lines = text.split('\n').length;
           const isURL = text.includes('http://') || text.includes('https://') || text.includes('www.');
           const effectiveBaseLength = isURL ? 40 : baseLength;
           const textLines = text.split('\n');
           let totalCharRows = 0;
           textLines.forEach(line => {
             const lineCharRows = Math.ceil(line.length / effectiveBaseLength);
             totalCharRows += Math.max(1, lineCharRows);
           });
           let rowsNeeded = Math.max(lines, totalCharRows);
           if (lines === 1 && text.split(' ').length > 1) {
             const wordRows = Math.ceil(text.split(' ').length / 8);
             rowsNeeded = Math.max(rowsNeeded, wordRows);
           }
           return Math.max(rowsNeeded, 1);
         };
         
         const descriptionRows = calculateRowsNeeded(row.description || '');
         const unitRows = calculateRowsNeeded(row.unit || '', 10);
         const priceRows = calculateRowsNeeded(row.price || '', 10);
         const maxRowsNeeded = Math.max(descriptionRows, unitRows, priceRows);
         const rowHeight = 15 * maxRowsNeeded;
         dataRowsHeight += rowHeight;
       });
       
       const totalRowHeight = rows.length > 0 ? 20 : 0;
       return headerHeight + dataRowsHeight + totalRowHeight;
     };
     
     // Pre-calculate heights for all sections
     const sectionHeightsArray = allSections.map(section => {
       const titleHeight = hasMultipleSections ? 30 : 20;
       const tableHeight = calculateTableHeight(section.rows || []);
       return titleHeight + tableHeight;
     });
     
     // Calculate summary table height
     const summaryTableHeight = showSummaryTable
       ? SUMMARY_TITLE_HEIGHT + 20 + (totalSections * 20) + 20
       : 0;
     
     // Helper to calculate content height
     const calculateContentHeight = (sectionIndices: number[], includeSummary: boolean, includeNotes: boolean) => {
       let height = HEADER_HEIGHT;
       
       sectionIndices.forEach((idx, i) => {
         height += sectionHeightsArray[idx];
         if (i < sectionIndices.length - 1) {
           const section = allSections[idx];
           const extraSpacing = Math.min((section.rows?.length || 0) * 2, 30);
           height += GAP_BETWEEN_SECTIONS + extraSpacing;
         }
       });
       
       if (includeSummary && sectionIndices.length > 0) {
         height += GAP_BETWEEN_SECTIONS;
       }
       
       if (includeSummary) {
         height += summaryTableHeight;
       }
       
       if (includeNotes && (includeSummary || sectionIndices.length > 0)) {
         const extraGap = totalSections > 9 ? 40 : 25;
         height += extraGap;
       }
       
       if (includeNotes) {
         height += NOTES_ESTIMATED_HEIGHT;
       }
       
       return height;
     };
     
     // Build pages dynamically based on content height
     let pageCount = 0;
     let currentPageSections: number[] = [];
     let sectionIndex = 0;
     
     while (sectionIndex < totalSections) {
       while (sectionIndex < totalSections) {
         const testSections = [...currentPageSections, sectionIndex];
         const heightWithoutSummary = calculateContentHeight(testSections, false, false);
         
         if (heightWithoutSummary <= MAX_PAGE_HEIGHT) {
           currentPageSections.push(sectionIndex);
           sectionIndex++;
         } else {
           // Can't fit this section
           // If currentPageSections is empty, we MUST add this section anyway (too large but unavoidable)
           if (currentPageSections.length === 0) {
             currentPageSections.push(sectionIndex);
             sectionIndex++;
           }
           break;
         }
         
         if (currentPageSections.length >= SECTIONS_PER_PAGE) {
           break;
         }
       }
       
       // Safety check: if we have no sections but haven't processed all, something went wrong
       if (currentPageSections.length === 0) {
         break;
       }
       
       const isLastSectionBatch = sectionIndex >= totalSections;

       if (isLastSectionBatch && showSummaryTable) {
         const heightWithSummary = calculateContentHeight(currentPageSections, true, false);
         const heightWithBoth = calculateContentHeight(currentPageSections, true, hasNotes);
         
         if (heightWithBoth <= MAX_PAGE_HEIGHT) {
           pageCount++;
         } else if (heightWithSummary <= MAX_PAGE_HEIGHT) {
           pageCount++;
           if (hasNotes) {
             pageCount++;
           }
         } else {
           pageCount++;
           pageCount++;
         }
       } else {
         // Not showing the summary table on this (last) batch - either there's only one
         // section, or there are several but the summary toggle is off. Either way, notes
         // (if any) still need to be accounted for here instead of being silently dropped.
         const shouldShowNotes = !showSummaryTable && isLastSectionBatch && hasNotes;

         if (shouldShowNotes) {
           // For single section with notes, check if they fit together
           const heightWithNotes = calculateContentHeight(currentPageSections, false, true);
           const heightWithoutNotes = calculateContentHeight(currentPageSections, false, false);
           
           // If the section alone is already over the limit, notes definitely need separate page
           if (heightWithoutNotes > MAX_PAGE_HEIGHT || heightWithNotes > MAX_PAGE_HEIGHT) {
             // Section too large or doesn't fit with notes - put notes on separate page
             pageCount++; // Page with section only
             pageCount++; // Page with notes only
           } else {
             // Both fit together
             pageCount++; // Page with section and notes
           }
         } else {
           // No notes or not applicable - just add sections
           pageCount++;
         }
       }
       
       currentPageSections = [];
     }
     
     return pageCount;
   };
  
  const totalInvoicePages = calculateSmartInvoicePages();
  const totalRepairEstimatePages = calculateSmartRepairEstimatePages();
    const totalRecommendationPages = formData.repairEstimatePages?.reduce((total, page) => {
      return total + calculateRecommendationPages(page.repairEstimateData?.rows || []);
    }, 0) || 0;
    
    // Get unused images for Page8
    const getUnusedImages = () => {
      const allImages = formData.scrapedImages || [];
      const uploadedImages = formData.uploadedInspectionImages || [];
      const usedImages = new Set<string>();
      
      // Collect all images used in recommendation pages
      formData.repairEstimatePages?.forEach(page => {
        if (page.reviewImage) {
          usedImages.add(page.reviewImage);
        }
      });
      
      // Collect all images selected in Step 6 (invoice images)
      const selectedImageIds = new Set((formData.selectedImages || []).map(img => img.id));
      
      // Collect all images excluded from Step 8
      const excludedImageIds = new Set(formData.excludedStep8Images || []);
      
      // Get filtered scraped images
      const filteredScrapedImages = allImages.filter(img => 
        !usedImages.has(img.url) && 
        !selectedImageIds.has(img.id) &&
        !excludedImageIds.has(img.id)
      );
      
      // Combine scraped and uploaded images
      const allInspectionImages = [...filteredScrapedImages, ...uploadedImages];
      
      // If custom order exists, use it; otherwise return natural order
      if (formData.inspectionImagesOrder && formData.inspectionImagesOrder.length > 0) {
        const orderMap = new Map(allInspectionImages.map(img => [img.id, img]));
        const ordered: ImageItem[] = [];
        const unordered: ImageItem[] = [];
        
        // Add images in specified order
        formData.inspectionImagesOrder.forEach(id => {
          const img = orderMap.get(id);
          if (img) {
            ordered.push(img);
            orderMap.delete(id);
          }
        });
        
        // Add any remaining images that weren't in the order
        orderMap.forEach(img => unordered.push(img));
        
        return [...ordered, ...unordered];
      }
      
      return allInspectionImages;
    };

    // Helper functions to move images up/down for easier reordering
    const moveImageUp = (imageId: string) => {
      const currentImages = getUnusedImages();
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex <= 0) return; // Already at the top
      
      const reordered = [...currentImages];
      [reordered[currentIndex - 1], reordered[currentIndex]] = [reordered[currentIndex], reordered[currentIndex - 1]];
      
      updateFormData({
        inspectionImagesOrder: reordered.map(img => img.id)
      });
    };

    const moveImageDown = (imageId: string) => {
      const currentImages = getUnusedImages();
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex < 0 || currentIndex >= currentImages.length - 1) return; // Already at the bottom
      
      const reordered = [...currentImages];
      [reordered[currentIndex], reordered[currentIndex + 1]] = [reordered[currentIndex + 1], reordered[currentIndex]];
      
      updateFormData({
        inspectionImagesOrder: reordered.map(img => img.id)
      });
    };

    const moveImageToTop = (imageId: string) => {
      const currentImages = getUnusedImages();
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex <= 0) return; // Already at the top
      
      const reordered = [...currentImages];
      const [removed] = reordered.splice(currentIndex, 1);
      reordered.unshift(removed);
      
      updateFormData({
        inspectionImagesOrder: reordered.map(img => img.id)
      });
    };

    const moveImageToBottom = (imageId: string) => {
      const currentImages = getUnusedImages();
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex < 0 || currentIndex >= currentImages.length - 1) return; // Already at the bottom
      
      const reordered = [...currentImages];
      const [removed] = reordered.splice(currentIndex, 1);
      reordered.push(removed);
      
      updateFormData({
        inspectionImagesOrder: reordered.map(img => img.id)
      });
    };

    const moveImageToPosition = (imageId: string, targetPosition: number) => {
      const currentImages = getUnusedImages();
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex < 0) return;
      
      // Validate target position (1-based to 0-based)
      const targetIndex = Math.max(0, Math.min(targetPosition - 1, currentImages.length - 1));
      
      if (currentIndex === targetIndex) return; // Already at target position
      
      const reordered = [...currentImages];
      const [removed] = reordered.splice(currentIndex, 1);
      reordered.splice(targetIndex, 0, removed);
      
      updateFormData({
        inspectionImagesOrder: reordered.map(img => img.id)
      });
    };

    // Get excluded images for Step 8 (for display in restore section)
    const getExcludedStep8Images = () => {
      const allImages = formData.scrapedImages || [];
      const excludedImageIds = new Set(formData.excludedStep8Images || []);
      return allImages.filter(img => excludedImageIds.has(img.id));
    };

    // Calculate total image pages needed (2 images per page)
    // Return 0 if there are no images to avoid blank pages
    const getTotalImagePages = () => {
      const unusedImages = getUnusedImages();
      if (unusedImages.length === 0) return 0;
      return Math.ceil(unusedImages.length / 2);
    };
    
    // Always include at least one page for step 7 (recommendation setup), plus any additional recommendation pages
    const totalImagePages = getTotalImagePages();
    // Calculate Step 9 pages based on chimney type: 3 for both masonry and prefabricated
    const step9Pages = 3;
    // Step 10 has 3 pages for both chimney types (Part 1 & Part 2 temporarily closed)
    const step10Pages = 3;
    const step6Pages = formData.step6TotalPages || 1; // Support multiple Step6 pages
    const totalPages = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages + Math.max(1, totalRecommendationPages) + totalImagePages + step9Pages + step10Pages;
  
  // Initialize all pages as included by default, but preserve user exclusions when totalPages changes
  useEffect(() => {
    if (!hasInitializedPages.current) {
      // First initialization: include all pages
    const allPages = new Set<number>();
    for (let page = 1; page <= totalPages; page++) {
      allPages.add(page);
    }
    setIncludedPages(allPages);
      maxInitializedPage.current = totalPages;
      hasInitializedPages.current = true;
    } else {
      // Subsequent changes: preserve existing exclusions, only add NEW pages (beyond previous max)
      setIncludedPages(prev => {
        const updated = new Set(prev);
        // Remove pages that no longer exist
        Array.from(prev).forEach(page => {
          if (page > totalPages) {
            updated.delete(page);
          }
        });
        // Only add NEW pages (pages beyond the previously initialized max)
        // This preserves user exclusions for existing pages
        if (totalPages > maxInitializedPage.current) {
          for (let page = maxInitializedPage.current + 1; page <= totalPages; page++) {
            updated.add(page); // New pages are included by default
          }
        }
        maxInitializedPage.current = Math.max(maxInitializedPage.current, totalPages);
        return updated;
      });
    }
  }, [totalPages]);

  // Sync step6CurrentPage when navigating to Step6 pages
  useEffect(() => {
    if (isImagePage6(currentPage)) {
      const step6PageIndex = getStep6PageIndex(currentPage);
      const step6PageNumber = step6PageIndex + 1; // Convert to 1-indexed
      if (formData.step6CurrentPage !== step6PageNumber) {
        handleStep6PageChange(step6PageNumber);
      }
    }
  }, [currentPage, formData.step6TotalPages, totalInvoicePages, totalRepairEstimatePages]);

  // Adjust currentPage when totalRepairEstimatePages changes (to prevent navigation to wrong page)
  useEffect(() => {
    const repairEstimateStart = 5 + totalInvoicePages;
    const prevRepairEstimateEnd = repairEstimateStart + prevRepairEstimatePages.current - 1;
    const currentRepairEstimateEnd = repairEstimateStart + totalRepairEstimatePages - 1;
    
    // If repair estimate pages decreased and we were on a page that no longer exists
    if (totalRepairEstimatePages < prevRepairEstimatePages.current) {
      // Check if current page is now beyond the repair estimate section
      if (currentPage >= repairEstimateStart && currentPage > currentRepairEstimateEnd) {
        // Move to the last valid repair estimate page
        if (totalRepairEstimatePages > 0) {
          setCurrentPage(currentRepairEstimateEnd);
        } else {
          // No repair estimate pages left, go to the page before repair estimate section
          setCurrentPage(repairEstimateStart - 1);
        }
      }
    }
    
    // Update the ref for next comparison
    prevRepairEstimatePages.current = totalRepairEstimatePages;
  }, [totalRepairEstimatePages, totalInvoicePages, currentPage]);

  // Helper function to determine the logical step (1-10) based on current page
  const getLogicalStep = (pageNum: number): number => {
    if (pageNum <= 4) return pageNum; // Pages 1-4 are steps 1-4
    if (pageNum <= 4 + totalInvoicePages) return 5; // All invoice pages are step 5
    if (pageNum <= 4 + totalInvoicePages + totalRepairEstimatePages) return 5; // All repair estimate pages are also step 5
    if (isImagePage6(pageNum)) return 6; // All Step6 pages are step 6
    if (isRecommendationPage(pageNum)) return 7; // Recommendation pages are step 7
    if (isImagePage(pageNum)) return 8; // Image pages are step 8
    
    // Calculate Step 9 and Step 10 page ranges based on chimney type
    const step9Pages = 3;
    const step10Pages = 3;
    const step9Start = totalPages - step9Pages - step10Pages + 1;
    const step10Start = totalPages - step10Pages + 1;
    
    if (pageNum >= step9Start && pageNum < step10Start) return 9; // Step 9 pages
    if (pageNum >= step10Start) return 10; // Step 10 pages
    return 7; // Default to recommendation pages
  };

  // Short, human-readable label for each logical step, matching the sidebar step names
  const STEP_LOGICAL_LABELS: Record<number, string> = {
    1: 'Client Info',
    2: 'Company Info',
    3: 'Service Report',
    4: 'Chimney Type',
    5: 'Invoice & Estimate',
    6: 'Invoice Images',
    7: 'Repair Recommendation',
    8: 'Inspection Images',
    9: 'Chimney Type Understanding',
    10: 'Final Steps'
  };

  // Labels for the vertical steps-navigation column (kept slightly shorter than
  // STEP_LOGICAL_LABELS to fit a narrow sidebar).
  const STEPS_NAV: { num: number; label: string }[] = [
    { num: 1, label: 'Client Info' },
    { num: 2, label: 'Company Info' },
    { num: 3, label: 'Service Report' },
    { num: 4, label: 'Chimney Type' },
    { num: 5, label: 'Invoice & Estimate' },
    { num: 6, label: 'Invoice Images' },
    { num: 7, label: 'Repair Rec.' },
    { num: 8, label: 'Inspection Images' },
    { num: 9, label: 'Chimney Type Understanding' },
    { num: 10, label: 'Club & Terms' },
  ];

  // Friendly label for a physical page number, used by the full page checklist
  const getPageLabel = (pageNum: number): string => {
    const logicalStep = getLogicalStep(pageNum);
    return `Page ${pageNum} — ${STEP_LOGICAL_LABELS[logicalStep] || `Step ${logicalStep}`}`;
  };

  // Helper function to check if we're on an invoice page
  const isInvoicePage = (pageNum: number): boolean => {
    return pageNum >= 5 && pageNum <= 4 + totalInvoicePages;
  };

  // Helper function to check if we're on a repair estimate page
  const isRepairEstimatePage = (pageNum: number): boolean => {
    return pageNum > 4 + totalInvoicePages && pageNum <= 4 + totalInvoicePages + totalRepairEstimatePages;
  };
  
  // Helper function to check if we're on an image page (Step 6)
  const isImagePage6 = (pageNum: number): boolean => {
    const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
    const step6Pages = formData.step6TotalPages || 1;
    return pageNum >= step6Start && pageNum < step6Start + step6Pages;
  };
  
  // Helper function to get Step6 page index (0-indexed)
  const getStep6PageIndex = (pageNum: number): number => {
    const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
    return pageNum - step6Start;
  };
  
  // Helper function to check if we're on a recommendation page
  const isRecommendationPage = (pageNum: number): boolean => {
    const step6Pages = formData.step6TotalPages || 1;
    const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
    const recommendationPageStart = step6End + 1;
    const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
    return pageNum >= recommendationPageStart && pageNum < imagePageStart;
  };
  
  // Helper function to get recommendation page index
  const getRecommendationPageIndex = (pageNum: number): number => {
    const step6Pages = formData.step6TotalPages || 1;
    const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
    const recommendationPageStart = step6End + 1;
    const relativePageNum = pageNum - recommendationPageStart; // Pages since start of recommendations
    
    // Find which recommendation this page belongs to
    let pagesAccounted = 0;
    const pages = formData.repairEstimatePages || [];
    for (let i = 0; i < pages.length; i++) {
      const rows = pages[i].repairEstimateData?.rows || [];
      const pagesNeeded = calculateRecommendationPages(rows);
      if (relativePageNum < pagesAccounted + pagesNeeded) {
        return i; // This is the recommendation index
      }
      pagesAccounted += pagesNeeded;
    }
    
    return Math.max(0, pages.length - 1); // Default to last recommendation
  };
  
  // Helper function to check if we're on the first recommendation page (setup page)
  const isRecommendationSetupPage = (pageNum: number): boolean => {
    const imagePageNum = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
    return pageNum === imagePageNum + 1 && totalRecommendationPages === 0; // First page after images, no pages created
  };

  // Helper function to check if we're on an image page (step 8)
  const isImagePage = (pageNum: number): boolean => {
    const totalImagePages = getTotalImagePages();
    if (totalImagePages === 0) return false; // No image pages if there are no images
    const recommendationPageStart = 4 + totalInvoicePages + totalRepairEstimatePages + 1 + 1;
    const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
    const step9Pages = 3;
    const step10Pages = 3;
    const step9Start = totalPages - step9Pages - step10Pages + 1;
    const imagePageEnd = step9Start - 1; // Image pages end before Step 9 starts
    return pageNum >= imagePageStart && pageNum <= imagePageEnd;
  };

  // Helper function to get image page index
  const getImagePageIndex = (pageNum: number): number => {
    const recommendationPageStart = 4 + totalInvoicePages + totalRepairEstimatePages + 1 + 1;
    const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
    return pageNum - imagePageStart;
  };

  // Helper function to get the part number for Step 9
  const getStep9PartNumber = (): number => {
    if (getLogicalStep(currentPage) !== 9) return 1;
    
    const step9Pages = 3;
    const step10Pages = 3;
    const step9Start = totalPages - step9Pages - step10Pages + 1;
    
    return currentPage - step9Start + 1;
  };

  // Helper function to get the part number for Step 10.
  // The 3 physical Step 10 pages render <Step10Part3>, <Step10Part4>, <Step10Part5>
  // (Part1/Part2 components are unused), so the label must start at 3, not 1.
  const getStep10PartNumber = (): number => {
    if (getLogicalStep(currentPage) !== 10) return 3;

    const step10Pages = 3;
    const step10Start = totalPages - step10Pages + 1;

    return currentPage - step10Start + 3;
  };
  
  const currentLogicalStep = getLogicalStep(currentPage);

  // Mark current step as completed when page changes
  useEffect(() => {
    if (currentLogicalStep >= 1 && currentLogicalStep <= 10) {
      markStepCompleted(currentLogicalStep);
    }
  }, [currentPage, currentLogicalStep]);

  // Function to mark a step as completed
  const markStepCompleted = (stepNumber: number) => {
    setCompletedSteps(prev => new Set([...Array.from(prev), stepNumber]));
  };

  // Function to check if a step is completed
  const isStepCompleted = (stepNumber: number): boolean => {
    return completedSteps.has(stepNumber);
  };

  // Debug helper to understand page structure
  const debugPageStructure = () => {
    console.log('=== PAGE STRUCTURE DEBUG ===');
    console.log('totalInvoicePages:', totalInvoicePages);
    console.log('totalRecommendationPages:', totalRecommendationPages);
    console.log('totalImagePages:', getTotalImagePages());
    console.log('totalPages:', totalPages);
    console.log('currentPage:', currentPage);
    console.log('currentLogicalStep:', currentLogicalStep);
    console.log('chimneyType:', formData.chimneyType);
    
    const recommendationPageStart = 4 + totalInvoicePages + totalRepairEstimatePages + 1 + 1;
    const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
    const step9Pages = 3;
    const step10Pages = 3;
    const step9Start = totalPages - step9Pages - step10Pages + 1;
    const step10Start = totalPages - step10Pages + 1;
    const lastImagePage = step9Start - 1;
    
    console.log('Pages 1-4: Client Info, Company Info, Service Report, Chimney Type');
    console.log(`Pages 5-${4 + totalInvoicePages}: Invoice pages`);
    console.log(`Pages ${4 + totalInvoicePages + 1}-${4 + totalInvoicePages + totalRepairEstimatePages}: Repair estimate pages`);
    console.log(`Page ${4 + totalInvoicePages + totalRepairEstimatePages + 1}: Image selection`);
    console.log(`Pages ${recommendationPageStart}-${imagePageStart - 1}: Recommendation pages`);
    console.log(`Pages ${imagePageStart}-${lastImagePage}: Image pages`);
    console.log(`Pages ${step9Start}-${step10Start - 1}: Step 9 pages (${step9Pages} pages for ${formData.chimneyType})`);
    console.log(`Pages ${step10Start}-${totalPages}: Step 10 pages (${step10Pages} pages)`);
    console.log('isImagePage(currentPage):', isImagePage(currentPage));
    console.log('getLogicalStep(currentPage):', getLogicalStep(currentPage));
    console.log('================================');
  };

  // Function to navigate to a specific step
  const navigateToStep = async (targetStep: number) => {
    if (targetStep < 1 || targetStep > 10) return;

    // Auto-save current step data before navigating
    setIsSavingStep(true);
    try {
      await saveCurrentStepToDatabase();
    } finally {
      setIsSavingStep(false);
    }

    // Mark the target step as completed when navigating to it
    markStepCompleted(targetStep);
    
    // Calculate Step 9 and Step 10 page ranges based on chimney type
    const step9Pages = 3;
    const step10Pages = 3;
    const step9Start = totalPages - step9Pages - step10Pages + 1;
    const step10Start = totalPages - step10Pages + 1;
    
    // Special handling for step 9 - go to first Step 9 page
    if (targetStep === 9) {
      setCurrentPage(step9Start);
      return;
    }
    
    // Special handling for step 10 - go to first Step 10 page
    if (targetStep === 10) {
      setCurrentPage(step10Start);
      return;
    }
    
    // Find the first page that corresponds to the target step
    for (let page = 1; page <= totalPages; page++) {
      if (getLogicalStep(page) === targetStep) {
        setCurrentPage(page);
        break;
      }
    }
  };

  // Save current step data to database (auto-save on navigation)
  const saveCurrentStepToDatabase = async () => {
    // Only save if we have a report ID and user is authenticated
    if (!currentReportId) {
      console.log('No report ID, skipping auto-save');
      return;
    }

    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user?.id) {
        console.log('User not authenticated, skipping auto-save');
        return;
      }
      setAutoSaveError(null);

      const logicalStep = getLogicalStep(currentPage);
      console.log(`Auto-saving Step ${logicalStep} data to database...`);

      // Save based on logical step
      switch (logicalStep) {
        case 1: {
          // Step 1 - Client Info & Timeline Cover
          const step1Json = {
            'Client Name': formData.clientName || '',
            'Client Address': formData.clientAddress || '',
            'Chimney Type': (formData.chimneyType || '').trim(),
            'Report Date': formData.reportDate || '',
            'House Image Link': formData.timelineCoverImage || ''
          };
          await stepsApi.upsert('step1_json', currentReportId as string, step1Json);
          console.log('✓ Step 1 data saved');
          break;
        }

        case 3: {
          // Step 3 - Client Name
          const step3Json = { 'Client Name': formData.clientName || '' };
          await stepsApi.upsert('step3_json', currentReportId as string, step3Json);
          console.log('✓ Step 3 data saved');
          break;
        }

        case 5: {
          // Step 5 - Invoice & Repair Estimates
          const invoice = formData.invoiceData || { rows: [] } as any;
          const step5InvoiceJson = {
            'Invoice Number': invoice.invoiceNumber || '',
            'Method of Payment': invoice.paymentMethod || '',
            'Payment No': invoice.paymentNumber || '',
            'Notes': invoice.notes || '',
            'Invoice Table': invoice.rows || []
          };
          await stepsApi.upsert('step5_invoice_json', currentReportId as string, step5InvoiceJson);

          // Build step5Part2Json dynamically from recommendationSections
          const step5Part2Json: any = {
            'Estimate Number': formData.repairEstimateData?.estimateNumber || '',
            'Method of Payment': formData.repairEstimateData?.paymentMethod || '',
            'Payment No': formData.repairEstimateData?.paymentNumber || '',
            'Notes': formData.notes || ''
          };
          
          // Add all recommendation sections dynamically
          if (formData.recommendationSections && formData.recommendationSections.length > 0) {
            formData.recommendationSections.forEach((section, index) => {
              const sectionNum = index + 1;
              step5Part2Json[`Section ${sectionNum} Title`] = section.title || `Repair Estimate ${sectionNum}`;
              step5Part2Json[`Section ${sectionNum} Rows`] = section.rows || [];
            });
          } else {
            // Fallback to old structure if recommendationSections is not available
            step5Part2Json['Section 1 Title'] = (formData as any).recommendationSection1Title || 'Repair Estimate 1';
            step5Part2Json['Section 1 Rows'] = formData.repairEstimateData?.rows || [];
            if ((formData as any).showRecommendationSection2) {
              step5Part2Json['Section 2 Title'] = (formData as any).recommendationSection2Title || 'Repair Estimate 2';
              step5Part2Json['Section 2 Rows'] = ((formData as any).recommendationSection2?.rows) || [];
            }
          }
          step5Part2Json['Show Summary Table'] = formData.showRepairEstimateSummary !== false;
          await stepsApi.upsert('step5_part2_json', currentReportId as string, step5Part2Json);
          console.log('✓ Step 5 data saved');
          break;
        }

        case 6: {
          // Step 6 - Selected/Uploaded Images
          const step6Json = {
            'Selected Images': (formData.selectedImages || []).map(img => ({
              id: img.id,
              url: img.url,
              positionX: img.positionX,
              positionY: img.positionY,
              width: img.width,
              height: img.height,
              pageNumber: img.pageNumber || 1
            })),
            'Text Position': {
              x: formData.step6TextPositionX,
              y: formData.step6TextPositionY
            },
            'Current Page': formData.step6CurrentPage || 1,
            'Total Pages': formData.step6TotalPages || 1
          };
          await stepsApi.upsert('step6_json', currentReportId as string, step6Json);
          console.log('✓ Step 6 data saved (images)');
          break;
        }

        case 7: {
          // Step 7 - Recommendations
          const step7Json = {
            'Recommendations': (formData.repairEstimatePages || []).map((p: any, idx: number) => ({
              index: idx + 1,
              reviewImage: p.reviewImage,
              rows: (p.repairEstimateData?.rows) || []
            }))
          };
          await stepsApi.upsert('step7_json', currentReportId as string, step7Json);
          console.log('✓ Step 7 data saved');
          break;
        }

        case 8: {
          // Step 8 - Inspection Images
          const inspectionImages = getUnusedImages().map((img: any) => ({ id: img.id, url: img.url }));
          const step8Json = {
            'Inspection Images': inspectionImages,
            'Inspection Images Order': formData.inspectionImagesOrder || [],
            'Uploaded Images': formData.uploadedInspectionImages || []
          };
          await stepsApi.upsert('step8_json', currentReportId as string, step8Json);
          console.log('✓ Step 8 data saved');
          break;
        }

        default:
          // Steps 2, 4, 9, 10 are static pages, no data to save
          console.log(`Step ${logicalStep} is static, no data to save`);
      }
    } catch (error: any) {
      const logicalStep = getLogicalStep(currentPage);
      console.error(`Failed to auto-save step ${logicalStep} data (report ${currentReportId}):`, error);
      // Don't throw - allow navigation to continue even if save fails. But DO surface it: this
      // used to fail completely silently, which could lose a step's data with no indication.
      setAutoSaveError(
        `Step ${logicalStep} didn't save (${error?.message || 'unknown error'}). ` +
        `Your changes for this step may be lost - go back and try again once your connection is stable.`
      );
    }
  };

  const handleNextPage = async () => {
    const reportId = currentReportId || formData.clientName || 'new_report';
    const currentLogicalStep = getLogicalStep(currentPage);
    
    // Prepare detailed event data based on the step
    let stepDetails: Record<string, any> = {};
    
    switch (currentLogicalStep) {
      case 5: {
        // Check if it's invoice or repair estimate
        if (isInvoicePage(currentPage)) {
          // Invoice page - only log when leaving the last invoice page
          const lastInvoicePage = 4 + totalInvoicePages;
          if (currentPage === lastInvoicePage) {
            stepDetails = {
              invoice_no: formData.invoiceData?.invoiceNumber || '',
              payment_method: formData.invoiceData?.paymentMethod || '',
              payment_no: formData.invoiceData?.paymentNumber || '',
              items_added: formData.invoiceData?.rows?.length || 0,
              note: formData.invoiceData?.notes || ''
            };
          }
        } else if (isRepairEstimatePage(currentPage)) {
          // Repair estimate - only log when leaving the last repair estimate page
          const lastRepairEstimatePage = 4 + totalInvoicePages + totalRepairEstimatePages;
          if (currentPage === lastRepairEstimatePage) {
            const sections: Record<string, any> = {};
            
            // Capture all recommendation sections
            if (formData.recommendationSections && formData.recommendationSections.length > 0) {
              formData.recommendationSections.forEach((section, index) => {
                sections[`section_${index + 1}`] = {
                  title: section.title,
                  items_added: section.rows?.length || 0
                };
              });
            }
            
            stepDetails = {
              estimate_no: formData.repairEstimateData?.estimateNumber || '',
              payment_method: formData.repairEstimateData?.paymentMethod || '',
              payment_no: formData.repairEstimateData?.paymentNumber || '',
              total_sections: formData.recommendationSections?.length || 0,
              sections: sections
            };
          }
        }
        break;
      }
      
      case 6: {
        // Step 6 - Inspection images - only log when leaving last page
        const step6Pages = formData.step6TotalPages || 1;
        const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
        if (currentPage === step6End) {
          // Get all selected images
          const selectedImgs = formData.selectedImages || [];
          
          // Get IDs of all scraped images
          const scrapedImageIds = new Set((formData.scrapedImages || []).map(img => img.id));
          
          // Count images selected from extracted vs uploaded/added
          let selectedFromExtracted = 0;
          let uploadedOrAdded = 0;
          
          selectedImgs.forEach(img => {
            if (scrapedImageIds.has(img.id)) {
              selectedFromExtracted++;
            } else {
              uploadedOrAdded++;
            }
          });
          
          stepDetails = {
            selected_from_extracted: selectedFromExtracted,
            uploaded: uploadedOrAdded,
            total: selectedImgs.length
          };
        }
        break;
      }
      
      case 7: {
        // Step 7 - Recommendations - only log when leaving last recommendation page
        const isLastRecommendation = currentRecommendationPageIndex === (formData.repairEstimatePages?.length || 0) - 1;
        
        if (isLastRecommendation) {
          // Check if this recommendation has multiple pages
          const currentPageRec = getCurrentRecommendationPage();
          const currentRows = currentPageRec?.repairEstimateData?.rows || [];
          const currentRecommendationTotalPages = calculateRecommendationPages(currentRows);
          
          // Log when:
          // - On page 2 of a 2-page recommendation, OR
          // - On page 1 of a 1-page recommendation
          const shouldLog = (currentRecommendationTotalPages === 2 && currentRecommendationSubPage === 2) ||
                           (currentRecommendationTotalPages === 1 && currentRecommendationSubPage === 1);
          
          if (shouldLog) {
            const pages: Record<string, any> = {};
            formData.repairEstimatePages?.forEach((page, index) => {
              pages[`page_${index + 1}`] = {
                image: page.reviewImage || '',
                custom_recommendation: page.customRecommendation || '',
                items: page.repairEstimateData?.rows?.length || 0
              };
            });
            stepDetails = pages;
          }
        }
        break;
      }
      
      case 8: {
        // Step 8 - Image gallery - only log when leaving last image page
        const lastImagePage = totalPages - 8; // 8 = 3 (step9) + 5 (step10)
        if (currentPage === lastImagePage) {
          const pages: Record<string, any> = {};
          const unusedImages = (formData.selectedImages || []).filter((img: ImageItem) =>
            !(formData.usedReviewImages || []).includes(img.url)
          );
          const totalImagePages = unusedImages.length > 0 ? Math.ceil(unusedImages.length / 2) : 0;
          
          for (let i = 0; i < totalImagePages; i++) {
            const pageImages = unusedImages.slice(i * 2, (i + 1) * 2);
            pages[`page_${i + 1}`] = {
              images: pageImages.length,
              image_ids: pageImages.map(img => img.id)
            };
          }
          stepDetails = {
            total_pages: totalImagePages,
            total_images: unusedImages.length,
            pages: pages
          };
        }
        break;
      }
    }
    
    // Log step completion with details
    await logger.logStepCompleted(userEmail, reportId, currentLogicalStep, stepDetails);

    // Auto-save current step data before navigating
    setIsSavingStep(true);
    try {
      await saveCurrentStepToDatabase();
    } finally {
      setIsSavingStep(false);
    }

    // Persist step-wise labeled snapshot (if applicable), and latest snapshot
    try {
      const logicalStep = getLogicalStep(currentPage);
      const stepSnapshot: any = buildStepSnapshot(logicalStep);
      if (stepSnapshot) {
        const now = new Date().toISOString();
        const keyOverride = (stepSnapshot as any).__key;
        const dataToStore = (stepSnapshot as any).data ? (stepSnapshot as any).data : stepSnapshot;
        const storageKey = keyOverride || `sumoquote_v2_step_${logicalStep}`;
        localStorage.setItem(
          storageKey,
          JSON.stringify({ step: logicalStep, data: dataToStore, savedAt: now })
        );
        // Maintain a lightweight index of saved steps
        const existingIndexRaw = localStorage.getItem('sumoquote_v2_steps_index');
        const existingIndex: number[] = existingIndexRaw ? JSON.parse(existingIndexRaw) : [];
        if (!existingIndex.includes(logicalStep)) {
          localStorage.setItem('sumoquote_v2_steps_index', JSON.stringify([...existingIndex, logicalStep].sort((a, b) => a - b)));
        }
      }
    } catch (err) {
      // Ignore storage errors (e.g., quota exceeded, private mode)
    }

    // Mark current step as completed when moving to next page
    markStepCompleted(currentLogicalStep);
    
    // Debug page structure when navigating
    if (currentLogicalStep >= 7) {
      debugPageStructure();
    }
    
    if (currentLogicalStep === 7) {
      // Check if current recommendation has multiple pages (table + image)
      const currentRecPage = getCurrentRecommendationPage();
      const currentRows = currentRecPage?.repairEstimateData?.rows || [];
      const currentRecommendationTotalPages = calculateRecommendationPages(currentRows);
      
      // If we're on page 1 of a multi-page recommendation, go to page 2 (image page)
      if (currentRecommendationTotalPages > 1 && currentRecommendationSubPage === 1) {
        // Move to page 2 (image page) of same recommendation
        setCurrentRecommendationSubPage(2);
        setCurrentPage(currentPage + 1); // Actually increment the page
      } else {
        // We're on page 2 or single page recommendation, move to next recommendation or next step
        setCurrentRecommendationSubPage(1); // Reset sub-page for next recommendation
        
        if (currentRecommendationPageIndex < (formData.repairEstimatePages?.length || 0) - 1) {
          // Move to next recommendation page
          setCurrentRecommendationPageIndex(currentRecommendationPageIndex + 1);
          setCurrentRecommendationSubPage(1); // Reset to page 1 of new recommendation
          setCurrentPage(currentPage + 1); // Actually increment the page
        } else {
          // If we're on the last recommendation page, go to first image page
          const step6Pages = formData.step6TotalPages || 1;
        const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
        const recommendationPageStart = step6End + 1;
          const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
          setCurrentPage(imagePageStart);
          setCurrentRecommendationPageIndex(0); // Reset for future use
        }
      }
    } else if (isImagePage6(currentPage)) {
      // If we're on Step6 pages, navigate through them
      const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
      const step6Pages = formData.step6TotalPages || 1;
      const step6End = step6Start + step6Pages - 1;
      const currentStep6PageIndex = getStep6PageIndex(currentPage);
      
      if (currentStep6PageIndex < step6Pages - 1) {
        // Move to next Step6 page
        setCurrentPage(currentPage + 1);
        handleStep6PageChange(currentStep6PageIndex + 2); // Update Step6 current page (1-indexed)
      } else {
        // If we're on the last Step6 page, go to first recommendation page
        const recommendationPageStart = step6End + 1;
        setCurrentPage(recommendationPageStart);
      }
    } else if (isImagePage(currentPage)) {
      // If we're on image pages, navigate through image pages
      const recommendationPageStart = 4 + totalInvoicePages + totalRepairEstimatePages + 1 + 1;
      const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
      const step9Pages = 3;
      const step10Pages = 3;
      const step9Start = totalPages - step9Pages - step10Pages + 1;
      const lastImagePage = step9Start - 1;
      
      if (currentPage < lastImagePage) {
        setCurrentPage(currentPage + 1);
      } else {
        // If we're on the last image page, go to first Step 9 page
        setCurrentPage(step9Start);
      }
    } else if (getLogicalStep(currentPage) === 9) {
      // If we're on Step 9 pages, navigate through them
      const step9Pages = 3;
      const step10Pages = 3;
      const step9Start = totalPages - step9Pages - step10Pages + 1;
      const step10Start = totalPages - step10Pages + 1;
      
      if (currentPage < step10Start - 1) {
        setCurrentPage(currentPage + 1);
      } else {
        // If we're on the last Step 9 page, go to first Step 10 page
        setCurrentPage(step10Start);
      }
    } else if (getLogicalStep(currentPage) === 10) {
      // If we're on Step 10 pages, navigate through them
      const step10Pages = 3;
      const step10Start = totalPages - step10Pages + 1;
      
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    } else if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = async () => {
    // Auto-save current step data before navigating
    setIsSavingStep(true);
    try {
      await saveCurrentStepToDatabase();
    } finally {
      setIsSavingStep(false);
    }

    if (isImagePage6(currentPage)) {
      // If we're on Step6 pages, navigate through them
      const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
      const currentStep6PageIndex = getStep6PageIndex(currentPage);
      
      if (currentStep6PageIndex > 0) {
        // Move to previous Step6 page
        setCurrentPage(currentPage - 1);
        handleStep6PageChange(currentStep6PageIndex); // Update Step6 current page (1-indexed)
      } else {
        // If we're on the first Step6 page, go to last repair estimate page
        const repairEstimateEnd = 4 + totalInvoicePages + totalRepairEstimatePages;
        if (repairEstimateEnd >= 5) {
          setCurrentPage(repairEstimateEnd);
        } else {
          setCurrentPage(4); // Go to Step 4 if no repair estimate pages
        }
      }
    } else if (isImagePage(currentPage)) {
      // If we're on image pages, navigate through image pages
      const step6Pages = formData.step6TotalPages || 1;
      const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
      const recommendationPageStart = step6End + 1;
      const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
      const currentImagePageIndex = getImagePageIndex(currentPage);
      
      if (currentImagePageIndex > 0) {
        // Move to previous image page
        setCurrentPage(currentPage - 1);
      } else {
        // If we're on the first image page, go to last recommendation page
        setCurrentPage(imagePageStart - 1);
        setCurrentRecommendationPageIndex((formData.repairEstimatePages?.length || 0) - 1);
      }
    } else if (currentLogicalStep === 7) {
      // Check if current recommendation has multiple pages (table + image)
      const currentPage = getCurrentRecommendationPage();
      const currentRows = currentPage?.repairEstimateData?.rows || [];
      const currentRecommendationTotalPages = calculateRecommendationPages(currentRows);
      
      // If we're on page 2 of a multi-page recommendation, go to page 1 (table page)
      if (currentRecommendationTotalPages > 1 && currentRecommendationSubPage === 2) {
        // Move to page 1 (table page) of same recommendation
        setCurrentRecommendationSubPage(1);
      } else {
        // We're on page 1, move to previous recommendation or previous step
        setCurrentRecommendationSubPage(1); // Reset sub-page for previous recommendation
        
        if (currentRecommendationPageIndex > 0) {
          // Move to previous recommendation page
          setCurrentRecommendationPageIndex(currentRecommendationPageIndex - 1);
          setCurrentRecommendationSubPage(1); // Reset to page 1 of new recommendation
        } else {
          // If we're on the first recommendation page, go to last Step6 page
          const step6Pages = formData.step6TotalPages || 1;
          const step6Start = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
          const step6End = step6Start + step6Pages - 1;
          setCurrentPage(step6End);
          handleStep6PageChange(step6Pages); // Update Step6 current page
          setCurrentRecommendationPageIndex(0);
        }
      }
    } else if (getLogicalStep(currentPage) === 10) {
      // If we're on Step 10 pages, navigate through them
      const step10Pages = 3;
      const step10Start = totalPages - step10Pages + 1;
      
      if (currentPage > step10Start) {
        setCurrentPage(currentPage - 1);
      } else {
        // If we're on the first Step 10 page, go to last Step 9 page
        const step9Pages = 3;
        const step9Start = totalPages - step9Pages - step10Pages + 1;
        setCurrentPage(step9Start + step9Pages - 1);
      }
    } else if (getLogicalStep(currentPage) === 9) {
      // If we're on Step 9 pages, navigate through them
      const step9Pages = 3;
      const step10Pages = 3;
      const step9Start = totalPages - step9Pages - step10Pages + 1;
      
      if (currentPage > step9Start) {
        setCurrentPage(currentPage - 1);
      } else {
        // If we're on the first Step 9 page, go to last image page
        const step6Pages = formData.step6TotalPages || 1;
        const step6End = 4 + totalInvoicePages + totalRepairEstimatePages + step6Pages;
        const recommendationPageStart = step6End + 1;
        const imagePageStart = recommendationPageStart + Math.max(1, totalRecommendationPages);
        const lastImagePage = step9Start - 1;
        setCurrentPage(lastImagePage);
      }
    } else if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Jump straight to the last page of the report (always the final Step 10 page)
  const handleGoToLastPage = async () => {
    if (currentPage === totalPages) return;

    // Auto-save current step data before navigating
    setIsSavingStep(true);
    try {
      await saveCurrentStepToDatabase();
    } finally {
      setIsSavingStep(false);
    }

    setCurrentPage(totalPages);
  };

  // Function to compress images before PDF generation
  const compressImage = async (canvas: HTMLCanvasElement, format: 'JPEG' | 'PNG', quality: number): Promise<string> => {
    return new Promise((resolve) => {
      if (format === 'JPEG') {
        // Compress to JPEG with specified quality
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      } else {
        // PNG compression (PNG is already compressed, but we can optimize)
        const compressedDataUrl = canvas.toDataURL('image/png');
        resolve(compressedDataUrl);
      }
    });
  };

  // Function to create optimized canvas for better compression
  const createOptimizedCanvas = (originalCanvas: HTMLCanvasElement, targetWidth: number, targetHeight: number): HTMLCanvasElement => {
    const optimizedCanvas = document.createElement('canvas');
    const ctx = optimizedCanvas.getContext('2d');
    
    if (ctx) {
      optimizedCanvas.width = targetWidth;
      optimizedCanvas.height = targetHeight;
      
      // Use high-quality image smoothing for better results
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw the original canvas onto the optimized one
      ctx.drawImage(originalCanvas, 0, 0, targetWidth, targetHeight);
    }
    
    return optimizedCanvas;
  };

  // Function to generate multi-page PDF
  const generateMultiPagePDF = async (pdf: any, isMobileDevice: boolean) => {
    const imgWidth = 210; // A4 width in mm
    const imgHeight = 297; // A4 height in mm (full page)
    
    console.log('PDF Generation - Total Pages:', totalPages);
    console.log('PDF Generation - Invoice Data Rows:', formData.invoiceData?.rows?.length || 0);
    
    // Generate only included pages
    let pageCount = 0;
    const totalIncludedPages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => isPageIncluded(p)).length;
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      // Check if this page should be included in PDF
      if (!isPageIncluded(pageNum)) {
        console.log('Skipping PDF Page:', pageNum, '(excluded by user)');
        continue;
      }
      
      console.log('Generating PDF Page:', pageNum);
      const pageDisplayIndex = pageCount + 1;
      const pageCanvas = await generatePageCanvas(pageNum, pageDisplayIndex, totalIncludedPages);
      const isStep6ImagePage = isImagePage6(pageNum);
      
      // For Step 6 (Invoice Images) pages, avoid compression and keep PNG
      let pageImgData: string;
      if (isStep6ImagePage) {
        // Use original canvas at full fidelity in PNG (no quality loss)
        pageImgData = pageCanvas.toDataURL('image/png');
      } else if (pageNum === 1 || pageNum === 2 || pageNum === 3 || pageNum === 4 || getLogicalStep(pageNum) === 9 || getLogicalStep(pageNum) === 10) {
        // Keep Step 1, Step 2, Step 3, and Step 4 as PNG to preserve quality
        pageImgData = pageCanvas.toDataURL('image/png');
      } else {
        // Default path: scale and compress as JPEG
        const optimizedCanvas = createOptimizedCanvas(pageCanvas, 842, 1190);
        const compressionQuality = 0.78;
        pageImgData = await compressImage(optimizedCanvas, 'JPEG', compressionQuality);
      }
      
      if (pageCount > 0) {
        pdf.addPage();
      }
      pageCount++;
      
      const imgType = (isStep6ImagePage || pageNum === 1 || pageNum === 2 || pageNum === 3 || pageNum === 4 || getLogicalStep(pageNum) === 7 || getLogicalStep(pageNum) === 9 || getLogicalStep(pageNum) === 10) ? 'PNG' : 'JPEG';
      if (isMobileDevice) {
        pdf.addImage(pageImgData, imgType, 0, 0, imgWidth, imgHeight, '', 'FAST');
      } else {
        pdf.addImage(pageImgData, imgType, 0, 0, imgWidth, imgHeight);
      }

      // Header: centered page number at top
      try {
        const headerY = imgHeight - 4; // mm from top
        const headerText = `Page ${pageDisplayIndex} of ${totalIncludedPages}`;
        // page number text
        // Medium gray (approx #6B7280)
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(10);
        // center align
        if (typeof (pdf as any).text === 'function') {
          (pdf as any).text(headerText, imgWidth / 2, headerY, { align: 'center' });
        } else {
          // fallback: approximate center by measuring width
          const textWidth = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(headerText) : 0;
          const startX = (imgWidth - textWidth) / 2;
          (pdf as any).text(headerText, startX, headerY);
        }
      } catch {}
    }
  };

  // Function to generate canvas for specific page
  const generatePageCanvas = async (pageNumber: number, displayIndex?: number, totalIncluded?: number) => {
    // Create temporary container for the specific page
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '595px';
    tempContainer.style.height = '842px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.overflow = 'hidden';
    tempContainer.style.margin = '0';
    tempContainer.style.padding = '0';
    tempContainer.style.border = 'none';
    tempContainer.style.outline = 'none';
    
    // Add PDF-specific classes and attributes
    tempContainer.className = 'pdf-generation';
    tempContainer.setAttribute('data-pdf', 'true');
    
    // Create the page component
    const pageElement = pageNumber === 1 ? 
      React.createElement(Step1, { formData, updateFormData, isPDF: true, timelineCoverImage: formData.timelineCoverImage }) :
      pageNumber === 2 ?
      React.createElement(Step2, { formData, updateFormData, isPDF: true }) :
      pageNumber === 3 ?
      React.createElement(Step3, { formData, updateFormData, isPDF: true }) :
      pageNumber === 4 ?
      React.createElement(Step4, { chimneyType: formData.chimneyType, isPDF: true }) :
      pageNumber >= 5 && pageNumber <= 4 + totalInvoicePages ?
      React.createElement(Step5, { isPDF: true, invoiceData: formData.invoiceData, currentInvoicePage: pageNumber - 4 }) :
      pageNumber > 4 + totalInvoicePages && pageNumber <= 4 + totalInvoicePages + totalRepairEstimatePages ?
      React.createElement(Step5Part2, { 
        isPDF: true, 
        repairEstimateData: formData.repairEstimateData,
        sections: formData.recommendationSections && formData.recommendationSections.length > 0
          ? formData.recommendationSections.map(s => ({ title: s.title, rows: s.rows }))
          : undefined,
        section1: { title: formData.recommendationSection1Title || 'Repair Estimate#1', rows: formData.repairEstimateData?.rows || [] },
        section2: formData.showRecommendationSection2 ? { title: formData.recommendationSection2Title || 'Repair Estimate#2', rows: formData.recommendationSection2?.rows || [] } : undefined,
        currentEstimatePage: pageNumber - 4 - totalInvoicePages,
        notes: formData.notes,
        dataSourceUrl: formData.dataSourceUrl
      }) :
      isImagePage6(pageNumber) ?
      React.createElement(Step6, { 
        isPDF: true, 
        scrapedImages: formData.scrapedImages || [], 
        selectedImages: formData.selectedImages || [],
        textPositionX: formData.step6TextPositionX,
        textPositionY: formData.step6TextPositionY,
        currentPage: getStep6PageIndex(pageNumber) + 1, // Convert to 1-indexed
        totalPages: formData.step6TotalPages || 1
      }) :
      isRecommendationPage(pageNumber) ?
      React.createElement(Step7, { 
        isPDF: true, 
        scrapedImages: formData.scrapedImages || [], 
        selectedImages: formData.selectedImages || [],
        repairEstimateData: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.repairEstimateData || { manualEntry: false, rows: [] },
        reviewImage: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.reviewImage || '',
        customRecommendation: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.customRecommendation || '',
        imagePositionX: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.imagePositionX,
        imagePositionY: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.imagePositionY,
        imageWidth: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.imageWidth,
        imageHeight: formData.repairEstimatePages?.[getRecommendationPageIndex(pageNumber)]?.imageHeight,
        currentPage: (() => {
          const pageIndex = getRecommendationPageIndex(pageNumber);
          const page = formData.repairEstimatePages?.[pageIndex];
          if (!page) return 1;
          const rows = page.repairEstimateData?.rows || [];
          
          // Calculate which sub-page this should be based on the actual page number
          if (rows.length <= 1) return 1; // Single page recommendation
          
          // Calculate where this recommendation starts
          const imagePageNum = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
          const recommendationPageStart = imagePageNum + 1;
          
          // Count how many pages previous recommendations have used
          let pagesUsedByPreviousRecommendations = 0;
          for (let i = 0; i < pageIndex; i++) {
            const prevPage = formData.repairEstimatePages?.[i];
            if (prevPage) {
              const prevRows = prevPage.repairEstimateData?.rows || [];
              pagesUsedByPreviousRecommendations += calculateRecommendationPages(prevRows);
            }
          }
          
          // Calculate the start page of this specific recommendation
          const thisRecommendationStartPage = recommendationPageStart + pagesUsedByPreviousRecommendations;
          
          // Calculate which page within this recommendation
          const relativePageInRecommendation = pageNumber - thisRecommendationStartPage + 1;
          
          return relativePageInRecommendation;
        })(),
        totalPages: (() => {
          const pageIndex = getRecommendationPageIndex(pageNumber);
          const page = formData.repairEstimatePages?.[pageIndex];
          if (!page) return 1;
          const rows = page.repairEstimateData?.rows || [];
          return calculateRecommendationPages(rows);
        })()
      }) :
      isImagePage(pageNumber) && getTotalImagePages() > 0 ?
      React.createElement(Step8, { 
        isPDF: true, 
        unusedImages: getUnusedImages(),
        currentPage: getImagePageIndex(pageNumber) + 1,
        totalPages: getTotalImagePages(),
        selectedImages: formData.selectedImages || [],
        imageLayouts: formData.step8ImageLayouts || {},
        onImageLayoutChange: handleStep8ImageLayoutChange,
        onCropImage: handleCropInvoiceImage
      }) :
      // Step 9 pages (3 pages for both chimney types)
      getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 5) ?
      React.createElement(
        Step9Part1,
        {
          chimneyType:
            (formData.chimneyType?.toLowerCase() === 'prefabricated'
              ? 'prefabricated'
              : 'masonry')
        }
      ) :
      getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 4) ?
      React.createElement(Step9Part2, {
        chimneyType:
          (formData.chimneyType?.toLowerCase() === 'prefabricated'
            ? 'prefabricated'
            : 'masonry')
      }) :
      getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 3) ?
      React.createElement(Step9Part3, {
        chimneyType:
          (formData.chimneyType?.toLowerCase() === 'prefabricated'
            ? 'prefabricated'
            : 'masonry')
      }) :
      // Step 10 pages (3 pages for both chimney types)
      getLogicalStep(pageNumber) === 10 && pageNumber === (totalPages - 2) ?
      React.createElement(Step10Part3, { chimneyType: formData.chimneyType }) :
      getLogicalStep(pageNumber) === 10 && pageNumber === (totalPages - 1) ?
      React.createElement(Step10Part4, { chimneyType: formData.chimneyType }) :
      getLogicalStep(pageNumber) === 10 && pageNumber === totalPages ?
      React.createElement(Step10Part5) :
      React.createElement(Step5) ;
    
    // Render the page to the container
    const root = ReactDOM.createRoot(tempContainer);
    root.render(pageElement);
    
    document.body.appendChild(tempContainer);
    
    // Wait for rendering and CSS to be applied
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Debug: Log the positioning of key elements
    if (pageNumber === 3) {
      const overlapElement = tempContainer.querySelector('.overlap') as HTMLElement;
      const titleElement = tempContainer.querySelector('.title') as HTMLElement;
      const clientNameElement = tempContainer.querySelector('.div') as HTMLElement;
      const emailElement = tempContainer.querySelector('.email') as HTMLElement;
      
      console.log('PDF Generation Debug - Step 3:');
      console.log('Overlap position:', overlapElement?.style.left, overlapElement?.style.top);
      console.log('Title position:', titleElement?.style.left, titleElement?.style.top);
      console.log('Client name position:', clientNameElement?.style.left, clientNameElement?.style.top);
      console.log('Email position:', emailElement?.style.left, emailElement?.style.top);
    }
    
    // No in-canvas badge for PDF; footer added via jsPDF

    // html2canvas (useCORS) silently drops cross-origin images whose host sends no
    // CORS headers (e.g. Capturify), so route them through our same-origin proxy.
    const remoteImages = Array.from(tempContainer.querySelectorAll('img')).filter(img => {
      const src = img.getAttribute('src') || '';
      return toSameOriginImageUrl(src) !== src;
    });
    await Promise.all(remoteImages.map(img => new Promise<void>(resolve => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      img.removeAttribute('srcset');
      img.src = toSameOriginImageUrl(img.getAttribute('src') || '');
      setTimeout(done, 20000);
    })));

    // Use html2canvas to capture the page with optimized settings
    const { default: html2canvas } = await import('html2canvas');
    // Compute per-page render scale
    const isStep1 = pageNumber === 1;
    const isInvoicePages = pageNumber >= 5 && pageNumber <= (4 + totalInvoicePages);
    const isRepairEstimatePages = pageNumber > (4 + totalInvoicePages) && pageNumber <= (4 + totalInvoicePages + totalRepairEstimatePages);
    const isStep7Page = isRecommendationPage(pageNumber);
    const isStep2 = pageNumber === 2;
    const isStep3 = pageNumber === 3;
    const isStep9Part1 = getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 5);
    const isStep9Part2 = getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 4);
    const isStep9Part3 = getLogicalStep(pageNumber) === 9 && pageNumber === (totalPages - 3);
    const isStep10Part3 = getLogicalStep(pageNumber) === 10 && pageNumber === (totalPages - 2);
    const isStep10Part4 = getLogicalStep(pageNumber) === 10 && pageNumber === (totalPages - 1);
    const isStep10Part5 = getLogicalStep(pageNumber) === 10 && pageNumber === totalPages;

    const renderScale = (isStep1 || isInvoicePages || isRepairEstimatePages)
      ? 2
      : ((isStep2 || isStep3 || isStep9Part1 || isStep9Part2 || isStep9Part3 || isStep10Part3 || isStep10Part4 || isStep10Part5)
        ? 3
        : (isStep7Page ? 3 : 2)); // Step 7 at 3x, default 2x

    const canvas = await html2canvas(tempContainer, {
      scale: renderScale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 595,
      height: 842,
      scrollX: 0,
      scrollY: 0,
      // Additional compression optimizations
      removeContainer: true,
      foreignObjectRendering: false
    });
    
    // Clean up
    document.body.removeChild(tempContainer);
    
    return canvas;
  };

  return (
    <div className="w-full">
      {/* Generation/Upload Overlay */}
      {(isGenerating || isEditLoading) && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg px-6 py-5 border border-gray-200 max-w-sm w-full mx-4 text-center">
            <div className="mx-auto mb-3 w-10 h-10 rounded-full border-4 border-[#F0D8D6] border-t-[#722420] animate-spin" />
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {generationStatus === 'editing' ? 'Loading report…' : generationStatus === 'saving' ? 'Saving data…' : generationStatus === 'uploading' ? 'Uploading report…' : 'Generating PDF…'}
            </h4>
            <p className="text-xs text-gray-500">
              {generationStatus === 'editing' ? 'Preparing your report for editing.' : generationStatus === 'saving' ? 'Writing your report data to the database.' : generationStatus === 'uploading' ? 'Please wait while we upload your report.' : 'Rendering pages into a high-quality PDF.'}
            </p>
          </div>
        </div>
      )}
      {/* Step Auto-Save Overlay (shown while writing to the database: creating the report or saving step data before navigating) */}
      {isSavingStep && !isGenerating && !isEditLoading && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg px-6 py-5 border border-gray-200 max-w-sm w-full mx-4 text-center">
            <div className="mx-auto mb-3 w-10 h-10 rounded-full border-4 border-[#F0D8D6] border-t-[#722420] animate-spin" />
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {currentStep === 'scrape' ? 'Creating report…' : 'Saving…'}
            </h4>
            <p className="text-xs text-gray-500">
              {currentStep === 'scrape' ? 'Setting up your new report.' : 'Writing your progress to the database.'}
            </p>
          </div>
        </div>
      )}
      {/* Auto-save failure banner - non-blocking (unlike the overlays above) so the user can
          keep working, but visible so a failed save is never silent. */}
      {autoSaveError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-lg w-full mx-4 bg-red-50 border border-red-300 text-red-800 rounded-lg shadow-lg px-4 py-3 flex items-start gap-3">
          <span className="text-lg leading-none">⚠️</span>
          <p className="text-sm flex-1">{autoSaveError}</p>
          <button
            onClick={() => setAutoSaveError(null)}
            className="text-red-500 hover:text-red-700 font-bold leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {/* Mobile Blocking Overlay */}
      {isMobile && (
        <div className="mobile-block-overlay">
          <div className="mobile-block-content">
            <div className="text-6xl mb-4">📱</div>
            <h2 className="text-2xl font-bold text-[#722420] mb-4">Mobile Device Detected</h2>
            <p className="text-gray-700 mb-6">
              This website requires Desktop View for the best experience. Please switch your browser to Desktop Mode.
            </p>
            
            <div className="bg-red-50 rounded-lg p-4 mb-6 text-left border border-red-200">
              <h3 className="font-semibold text-black mb-2">How to switch to Desktop View:</h3>
              <div className="space-y-2 text-sm text-black">
                <div><strong>Safari:</strong> Tap AA → Request Desktop Website</div>
                <div><strong>Chrome:</strong> Tap ⋮ → Desktop Site</div>
                <div><strong>Firefox:</strong> Tap ⋮ → Desktop Site</div>
                <div><strong>Edge:</strong> Tap ⋯ → Desktop Site</div>
              </div>
            </div>
            
            <button 
              onClick={() => window.location.reload()}
              className="bg-[#722420] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#5a1d1a] transition-colors"
            >
              I've Switched to Desktop View
            </button>
            
            <p className="text-xs text-gray-500 mt-4">
              After switching, tap this button to refresh the page
            </p>
          </div>
        </div>
      )}
      
      {/* Content */}
      {currentStep === 'scrape' ? (
        <div className="card w-full">
          <ScrapeStep
            onDataExtracted={handleDataExtracted}
            setCurrentStep={setCurrentStep}
            setFormData={(data) => setFormData({ ...data, notes: 'This quote is good for 30 days from date of service. Deposits for scheduled future service is non-refundable.' })}
            userEmail={userEmail}
          />
        </div>
      ) : (
        <>
          {/* Steps Navigation + Input Fields + Live Preview - one connected card, divided by
              hairline borders instead of gaps, so the three sections read as one workspace
              rather than three floating panels with page-background gaps between them. */}
          <div className="card overflow-hidden">
          <div className="grid grid-cols-1 xl:grid-cols-[200px_minmax(400px,1fr)_660px]">
          {/* Steps Navigation */}
          <div className="p-3 border-b xl:border-b-0 xl:border-r border-gray-300 xl:self-start">
            <button
              onClick={async () => {
                const willDelete = !!currentReportId && !isEditingExistingReport;
                const confirmMessage = willDelete
                  ? 'Start over? Everything you\'ve entered for this report will be permanently deleted.'
                  : 'Start over? Your unsaved edits will be discarded (the original report will not be changed).';
                if (!window.confirm(confirmMessage)) return;

                if (willDelete && currentReportId) {
                  try {
                    await reportsApi.remove(currentReportId);
                  } catch (err) {
                    console.error('Failed to delete report on Start Over:', err);
                  }
                }
                window.location.href = '/';
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-3 bg-[#722420] text-white text-sm font-semibold rounded-lg hover:bg-[#8B2E26] shadow-sm transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              Start Over
            </button>
            <h4 className="text-xs font-semibold text-gray-400 px-1 mb-2">Steps</h4>
            <div className="space-y-0.5">
              {STEPS_NAV.map((step) => {
                const isCurrent = currentLogicalStep === step.num;
                const isDone = isStepCompleted(step.num) && !isCurrent;
                return (
                  <button
                    key={step.num}
                    onClick={() => navigateToStep(step.num)}
                    className={`w-full flex items-center gap-2.5 text-left px-2 py-1.5 rounded-lg transition-colors ${
                      isCurrent ? 'bg-[#FBEEED]' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                      isCurrent ? 'bg-[#722420] text-white' : isDone ? 'bg-[#722420]/15 text-[#722420]' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isDone ? '✓' : step.num}
                    </div>
                    <span className={`text-sm ${isCurrent ? 'font-semibold text-gray-900' : isDone ? 'text-gray-700' : 'text-gray-400'}`}>
                      {step.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Input Fields Section */}
          <div className="p-4 sm:p-6 border-b xl:border-b-0 xl:border-r border-gray-300">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {currentLogicalStep === 1 ? 'Step 1 - Manual Entry' : 
                   currentLogicalStep === 2 ? 'Step 2 - Static Content' : 
                   currentLogicalStep === 3 ? 'Step 3 - Service Report' : 
                   currentLogicalStep === 4 ? 'Step 4 - Chimney Type' : 
                   currentLogicalStep === 5 ? `Step 5 - Invoice & Estimate${isInvoicePage(currentPage) ? (totalInvoicePages > 1 ? ` (Invoice ${currentPage - 4}/${totalInvoicePages})` : ' (Invoice)') : isRepairEstimatePage(currentPage) ? (totalRepairEstimatePages > 1 ? ` (Estimate ${currentPage - 4 - totalInvoicePages}/${totalRepairEstimatePages})` : ' (Estimate)') : ''}` : 
                   currentLogicalStep === 6 ? 'Step 6 - Project Images' :
                   currentLogicalStep === 7 ? 'Step 7 - Repair Estimate' :
                   currentLogicalStep === 8 ? 'Step 8 - Inspection Images' :
                  currentLogicalStep === 9 ? `Step 9 - Documentation (Part ${getStep9PartNumber()})` :
                  currentLogicalStep === 10 ? `Step 10 - Final Steps (Part ${getStep10PartNumber()})` :
                  'Unknown Step'}
                </h3>
              </div>
            </div>
            
            {/* Final Report Generation Indicator (now on Step 5) */}
           
            
            {currentPage === 1 ? (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Client Name */}
                  <div>
                    <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
                      Client Name
                    </label>
                    <input
                      type="text"
                      id="clientName"
                      value={formData.clientName}
                      onChange={(e) => updateFormData({ clientName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                      placeholder="Enter client name"
                    />
                  </div>
                  
                  {/* Client Address */}
                  <div>
                    <label htmlFor="clientAddress" className="block text-sm font-medium text-gray-700 mb-2">
                      Client Address
                    </label>
                    <textarea
                      id="clientAddress"
                      value={formData.clientAddress}
                      onChange={(e) => updateFormData({ clientAddress: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                      placeholder="Enter client address"
                    />
                  </div>
                  
                  {/* Chimney Type */}
                  <div>
                    <label htmlFor="chimneyType" className="block text-sm font-medium text-gray-700 mb-2">
                      Chimney Type
                    </label>
                    <select
                      id="chimneyType"
                      value={formData.chimneyType}
                      onChange={(e) => updateFormData({ chimneyType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    >
                      <option value="">Select chimney type</option>
                      <option value="masonry">Masonry</option>
                      <option value="prefabricated">Prefabricated</option>
                      <option value="none">None</option>
                    </select>
                  </div>

                  {/* Report Date */}
                  <div>
                    <label htmlFor="reportDate" className="block text-sm font-medium text-gray-700 mb-2">
                      Report Date
                    </label>
                    <input
                      type="date"
                      id="reportDate"
                      value={formData.reportDate}
                      onChange={(e) => updateFormData({ reportDate: e.target.value })}
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-[#722420] text-center font-medium cursor-pointer hover:border-gray-400 transition-colors"
                      style={{
                          fontSize: '16px',
                          minHeight: '48px'
                      }}
                    />
                  </div>

                  {/* House Image Upload - Only show if no timelineCoverImage exists */}
                  {!formData.timelineCoverImage && (
                    <div className="space-y-4">
                      
                      
                      
                      <div>
                        <label htmlFor="imageUrlInput" className="block text-sm font-medium text-gray-700 mb-2">
                          Image URL
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="url"
                            id="imageUrlInput"
                            placeholder="https://example.com/image.jpg"
                            className="flex-1 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-[#722420]"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const url = (e.target as HTMLInputElement).value.trim();
                                if (url) {
                                  updateFormData({ timelineCoverImage: url });
                                }
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const urlInput = document.getElementById('imageUrlInput') as HTMLInputElement;
                              const url = urlInput.value.trim();
                              if (url) {
                                updateFormData({ timelineCoverImage: url });
                              }
                            }}
                            className="px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#5a1d1a] transition-colors text-sm"
                          >
                            Load
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show existing image info if timelineCoverImage exists */}
                  {formData.timelineCoverImage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="text-green-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-800">House Image Available</p>
                          <p className="text-xs text-green-600">Image has been extracted or uploaded</p>
                        </div>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleCropImage()}
                          className="ml-7 p-6 py-3 text-xs bg-[#722420] text-white rounded hover:bg-[#5a1d1a] transition-colors"
                        >
                          ✂️ Crop Image
                        </button>
                        <br />
                        <button
                          type="button"
                          onClick={() => updateFormData({ timelineCoverImage: '' })}
                          className="text-sm text-[#722420] hover:text-black underline"
                        >
                          Remove image to upload a new one
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            ) : currentPage === 2 ? (
              <div className="text-center py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="text-red-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-black mb-2">Step 2 - Static Content</h4>
                  <p className="text-black mb-4">
                    This page contains company credentials and insurance information that cannot be edited.
                  </p>
                  <p className="text-sm text-black">
                    All information is pre-filled and will be included in the final PDF report.
                  </p>
                </div>
              </div>
            ) : currentPage === 3 ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="page3ClientName" className="block text-sm font-medium text-gray-700 mb-2">
                    Client Name for Greeting
                  </label>
                  <input
                    type="text"
                    id="page3ClientName"
                    value={formData.clientName}
                    onChange={(e) => updateFormData({ clientName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    placeholder="Enter client name for greeting"
                  />
                </div>
              </div>
            ) : currentPage === 4 ? (
              <div className="text-center py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="text-red-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-black mb-2">Step 4 - Static Content</h4>
                  <p className="text-black mb-4">
                    This page summarizes the chimney type selection and is not editable.
                  </p>
                  <p className="text-sm text-black">
                    The content is pre-filled and will be included in the final PDF report.
                  </p>
                </div>
              </div>
            ) : isInvoicePage(currentPage) ? (
              <div className="space-y-4">
                {/* Invoice Number */}
                <div>
                  <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    id="invoiceNumber"
                    value={formData.invoiceData?.invoiceNumber || ''}
                    onChange={(e) => updateFormData({ 
                      invoiceData: { 
                        invoiceNumber: e.target.value,
                        paymentMethod: formData.invoiceData?.paymentMethod || '',
                        paymentNumber: formData.invoiceData?.paymentNumber || '',
                        notes: formData.invoiceData?.notes || '',
                        rows: formData.invoiceData?.rows || []
                      } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    placeholder="Enter invoice number"
                  />
                </div>

                {/* Payment Method */}
                <div>
                  <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    id="paymentMethod"
                    value={formData.invoiceData?.paymentMethod || ''}
                    onChange={(e) => updateFormData({ 
                      invoiceData: { 
                        invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                        paymentMethod: e.target.value,
                        paymentNumber: formData.invoiceData?.paymentNumber || '',
                        notes: formData.invoiceData?.notes || '',
                        rows: formData.invoiceData?.rows || []
                      } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                  >
                    <option value="">Select payment method</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Billing">Billing</option>
                    <option value="Waived Off">Waived Off</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Payment Number */}
                <div>
                  <label htmlFor="paymentNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Number (Optional)
                  </label>
                  <input
                    type="text"
                    id="paymentNumber"
                    value={formData.invoiceData?.paymentNumber || ''}
                    onChange={(e) => updateFormData({ 
                      invoiceData: { 
                        invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                        paymentMethod: formData.invoiceData?.paymentMethod || '',
                        paymentNumber: e.target.value,
                        notes: formData.invoiceData?.notes || '',
                        rows: formData.invoiceData?.rows || []
                      } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    placeholder="Enter payment number (check #, transaction ID, etc.)"
                  />
                </div>

                {/* Invoice Items */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Items
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowInvoiceQuickAdd(!showInvoiceQuickAdd)}
                        className="px-3 py-1 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm"
                      >
                        + Add Item
                      </button>
                      {showInvoiceQuickAdd && (
                        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-2">
                            <button
                              type="button"
                              onClick={() => {
                                const newRow = {
                                  id: Date.now().toString(),
                                  description: 'NFPA Level 1 Chimney Inspection (discounted)',
                                  unit: '1',
                                  price: '99.00'
                                };
                                updateFormData({ 
                                  invoiceData: { 
                                    invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                    paymentMethod: formData.invoiceData?.paymentMethod || '',
                                    paymentNumber: formData.invoiceData?.paymentNumber || '',
                                    notes: formData.invoiceData?.notes || '',
                                    rows: [...(formData.invoiceData?.rows || []), newRow]
                                  } 
                                });
                                setShowInvoiceQuickAdd(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Add NFPA Level 1 Chimney Inspection (discounted)($99)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newRow = {
                                  id: Date.now().toString(),
                                  description: 'NFPA Level 1 Chimney Inspection and Sweep (discounted)',
                                  unit: '1',
                                  price: '149.00'
                                };
                                updateFormData({ 
                                  invoiceData: { 
                                    invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                    paymentMethod: formData.invoiceData?.paymentMethod || '',
                                    paymentNumber: formData.invoiceData?.paymentNumber || '',
                                    notes: formData.invoiceData?.notes || '',
                                    rows: [...(formData.invoiceData?.rows || []), newRow]
                                  } 
                                });
                                setShowInvoiceQuickAdd(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Add NFPA Level 1 Chimney Inspection and Sweep (discounted)($149)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newRow = {
                                  id: Date.now().toString(),
                                  description: 'CC Processing fees',
                                  unit: '1',
                                  price: ''
                                };
                                updateFormData({ 
                                  invoiceData: { 
                                    invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                    paymentMethod: formData.invoiceData?.paymentMethod || '',
                                    paymentNumber: formData.invoiceData?.paymentNumber || '',
                                    notes: formData.invoiceData?.notes || '',
                                    rows: [...(formData.invoiceData?.rows || []), newRow]
                                  } 
                                });
                                setShowInvoiceQuickAdd(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              CC Processing fees
                            </button>
                            <div className="border-t my-1" />
                            <button
                              type="button"
                              onClick={() => {
                                const newRow = {
                                  id: Date.now().toString(),
                                  description: '',
                                  unit: '',
                                  price: ''
                                };
                                updateFormData({ 
                                  invoiceData: { 
                                    invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                    paymentMethod: formData.invoiceData?.paymentMethod || '',
                                    paymentNumber: formData.invoiceData?.paymentNumber || '',
                                    notes: formData.invoiceData?.notes || '',
                                    rows: [...(formData.invoiceData?.rows || []), newRow]
                                  } 
                                });
                                setShowInvoiceQuickAdd(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Add Blank Item
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(formData.invoiceData?.rows || []).map((row, index) => {
                      // Function to add a new row after the current row
                      const handleAddNewRow = () => {
                        const newRow = {
                          id: Date.now().toString(),
                          description: '',
                          unit: '',
                          price: ''
                        };
                        const currentRows = formData.invoiceData?.rows || [];
                        const currentIndex = currentRows.findIndex(r => r.id === row.id);
                        const updatedRows = [
                          ...currentRows.slice(0, currentIndex + 1),
                          newRow,
                          ...currentRows.slice(currentIndex + 1)
                        ];
                        
                        updateFormData({ 
                          invoiceData: { 
                            invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                            paymentMethod: formData.invoiceData?.paymentMethod || '',
                            paymentNumber: formData.invoiceData?.paymentNumber || '',
                            notes: formData.invoiceData?.notes || '',
                            rows: updatedRows
                          } 
                        });
                        
                        // Store the new row ID to focus it after render
                        newRowDescriptionRef.current = newRow.id;
                        
                        // Focus the new row's description field after a short delay
                        setTimeout(() => {
                          const newRowInput = document.querySelector(`[data-row-id="${newRow.id}"]`) as HTMLInputElement | HTMLTextAreaElement;
                          if (newRowInput) {
                            newRowInput.focus();
                            newRowDescriptionRef.current = null;
                          }
                        }, 50);
                      };
                      
                      return (
                        <div key={row.id} className="flex space-x-2 p-2 border border-gray-200 rounded-md">
                        <AutocompleteInput
                          value={row.description}
                          onChange={(value) => {
                            const updatedRows = (formData.invoiceData?.rows || []).map(r => 
                              r.id === row.id ? { ...r, description: value } : r
                            );
                            updateFormData({ 
                              invoiceData: { 
                                invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                paymentMethod: formData.invoiceData?.paymentMethod || '',
                                paymentNumber: formData.invoiceData?.paymentNumber || '',
                                notes: formData.invoiceData?.notes || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          onSelectRow={(selectedRow: SheetRow) => {
                            // When a row is selected from description field, populate all three fields
                            const priceValue = selectedRow.price || '';
                            // Remove $ sign and store as plain number string (e.g., "400" not "$400")
                            const priceWithoutDollar = priceValue.replace(/^\$/, '').trim();
                            
                            const updatedRows = (formData.invoiceData?.rows || []).map(r => 
                              r.id === row.id ? { 
                                ...r, 
                                description: selectedRow.description,
                                unit: selectedRow.unit || '1', // Default to "1" if not provided
                                price: priceWithoutDollar // Store without $ sign
                              } : r
                            );
                            updateFormData({ 
                              invoiceData: { 
                                invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                paymentMethod: formData.invoiceData?.paymentMethod || '',
                                paymentNumber: formData.invoiceData?.paymentNumber || '',
                                notes: formData.invoiceData?.notes || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          dataRowId={row.id}
                          placeholder="Description"
                          field="description"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                          sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                          sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                        />
                        <input
                          type="text"
                          placeholder="Unit"
                          value={row.unit}
                          data-row-id={row.id}
                          onChange={(e) => {
                            const updatedRows = (formData.invoiceData?.rows || []).map(r => 
                              r.id === row.id ? { ...r, unit: e.target.value } : r
                            );
                            updateFormData({ 
                              invoiceData: { 
                                invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                paymentMethod: formData.invoiceData?.paymentMethod || '',
                                paymentNumber: formData.invoiceData?.paymentNumber || '',
                                notes: formData.invoiceData?.notes || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddNewRow();
                            }
                          }}
                          className="w-10 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                        />
                        <input
                          type="text"
                          placeholder="Price"
                          value={row.price}
                          data-row-id={row.id}
                          onChange={(e) => {
                            const updatedRows = (formData.invoiceData?.rows || []).map(r => 
                              r.id === row.id ? { ...r, price: e.target.value } : r
                            );
                            updateFormData({ 
                              invoiceData: { 
                                invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                paymentMethod: formData.invoiceData?.paymentMethod || '',
                                paymentNumber: formData.invoiceData?.paymentNumber || '',
                                notes: formData.invoiceData?.notes || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddNewRow();
                            }
                          }}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedRows = (formData.invoiceData?.rows || []).filter(r => r.id !== row.id);
                            updateFormData({
                              invoiceData: {
                                invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                                paymentMethod: formData.invoiceData?.paymentMethod || '',
                                paymentNumber: formData.invoiceData?.paymentNumber || '',
                                notes: formData.invoiceData?.notes || '',
                                rows: updatedRows
                              }
                            });
                          }}
                          className="px-2 py-1  text-red-600 hover:text-red-800 text-m flex items-center justify-center"
                          title="Delete item"
                        >
                          <svg 
                            width="17" 
                            height="17" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                      );
                    })}
                    {(!formData.invoiceData?.rows || formData.invoiceData.rows.length === 0) && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No items added yet. Click "Add Item" to add invoice items.
                      </p>
                    )}
                  </div>
                </div>

                {/* Notes Section */}
                <div className="pt-4 border-t border-gray-200">
                  <label htmlFor="invoiceNotes" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="invoiceNotes"
                    value={formData.invoiceData?.notes || ''}
                    onChange={(e) => updateFormData({ 
                      invoiceData: { 
                        invoiceNumber: formData.invoiceData?.invoiceNumber || '',
                        paymentMethod: formData.invoiceData?.paymentMethod || '',
                        paymentNumber: formData.invoiceData?.paymentNumber || '',
                        notes: e.target.value,
                        rows: formData.invoiceData?.rows || []
                      } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    rows={3}
                    placeholder="Enter notes for the invoice..."
                  />
                </div>
              </div>
            ) : isRepairEstimatePage(currentPage) ? (
              <div className="space-y-4">
                {/* Initialize recommendationSections if not exists, using backward compatible data */}
                {(() => {
                  if (!formData.recommendationSections || formData.recommendationSections.length === 0) {
                    // Initialize from existing section1/section2 data
                    const sections = [{
                      id: 'section-1',
                      title: formData.recommendationSection1Title || 'Repair Estimate#1',
                      rows: formData.repairEstimateData?.rows || []
                    }];
                    if (formData.showRecommendationSection2) {
                      sections.push({
                        id: 'section-2',
                        title: formData.recommendationSection2Title || 'Repair Estimate#2',
                        rows: formData.recommendationSection2?.rows || []
                      });
                    }
                    // Update formData with sections (but don't trigger re-render here)
                    setTimeout(() => {
                      updateFormData({ recommendationSections: sections });
                    }, 0);
                  }
                  return null;
                })()}
                
                {/* Sections Manager */}
                <div className="space-y-4">
                  {/* Header with Add Section button */}
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Repair Estimate Sections</h3>
                    <button
                      type="button"
                      onClick={() => {
                        const currentSections = formData.recommendationSections || [];
                        const newSection = {
                          id: `section-${Date.now()}`,
                          title: `Repair Estimate#${currentSections.length + 1}`,
                          rows: []
                        };
                        updateFormData({ 
                          recommendationSections: [...currentSections, newSection]
                        });
                        setEditingSectionIndex(currentSections.length); // Switch to the new section
                      }}
                      className="px-4 py-2 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm font-medium"
                    >
                      + Add New Section
                    </button>
                  </div>

                  {/* Summary table toggle - only meaningful once there's more than one
                      section, since the summary table never shows with just one. */}
                  {(() => {
                    const sectionCount = formData.recommendationSections && formData.recommendationSections.length > 0
                      ? formData.recommendationSections.length
                      : (1 + (formData.showRecommendationSection2 ? 1 : 0));
                    if (sectionCount <= 1) return null;
                    const includeSummary = formData.showRepairEstimateSummary !== false;
                    return (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                        <span className="text-sm text-gray-700">Show summary table (combined totals)</span>
                        <button
                          type="button"
                          onClick={() => updateFormData({ showRepairEstimateSummary: !includeSummary })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#722420] focus:ring-offset-1 ${
                            includeSummary ? 'bg-[#722420]' : 'bg-gray-300'
                          }`}
                          role="switch"
                          aria-checked={includeSummary}
                          aria-label="Show summary table"
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                              includeSummary ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })()}

                  {/* Section Navigation and Current Section Display */}
                  {(formData.recommendationSections && formData.recommendationSections.length > 0) && (() => {
                    const sections = formData.recommendationSections;
                    const totalSections = sections.length;
                    
                    // Ensure editingSectionIndex is valid
                    const validIndex = Math.max(0, Math.min(editingSectionIndex, totalSections - 1));
                    if (validIndex !== editingSectionIndex) {
                      setEditingSectionIndex(validIndex);
                    }
                    
                    const section = sections[validIndex];
                    const sectionIndex = validIndex;

                    return (
                      <div className="space-y-4">
                        {/* Section Navigation Bar */}
                        {totalSections > 1 && (
                          <div className="flex items-center justify-between bg-gray-50 border border-gray-300 rounded-lg p-3">
                            <button
                              type="button"
                              onClick={() => setEditingSectionIndex(Math.max(0, editingSectionIndex - 1))}
                              disabled={editingSectionIndex === 0}
                              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                                editingSectionIndex === 0
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                              Previous
                            </button>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">
                                Section {editingSectionIndex + 1} of {totalSections}
                              </span>
                              {/* Section quick navigation dots */}
                              <div className="flex gap-1">
                                {sections.map((_, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => setEditingSectionIndex(idx)}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                      idx === editingSectionIndex ? 'bg-[#722420]' : 'bg-gray-300 hover:bg-gray-400'
                                    }`}
                                    title={`Go to section ${idx + 1}`}
                                  />
                                ))}
                              </div>
                            </div>
                            
                            <button
                              type="button"
                              onClick={() => setEditingSectionIndex(Math.min(totalSections - 1, editingSectionIndex + 1))}
                              disabled={editingSectionIndex === totalSections - 1}
                              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                                editingSectionIndex === totalSections - 1
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                              }`}
                            >
                              Next
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        )}

                        {/* Current Section Editor */}
                        <div className="border border-gray-300 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <input
                              type="text"
                              placeholder={`Title for Section ${sectionIndex + 1}`}
                              value={section.title}
                              onChange={(e) => {
                                const updatedSections = sections.map(s =>
                                  s.id === section.id ? { ...s, title: e.target.value } : s
                                );
                                updateFormData({ recommendationSections: updatedSections });
                              }}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent mr-2"
                            />
                            {totalSections > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSections = sections.filter(s => s.id !== section.id);
                                  updateFormData({ recommendationSections: updatedSections });
                                  // Adjust editingSectionIndex after deletion
                                  if (editingSectionIndex >= updatedSections.length) {
                                    setEditingSectionIndex(Math.max(0, updatedSections.length - 1));
                                  }
                                }}
                                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                title="Remove section"
                              >
                                Remove Section
                              </button>
                            )}
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">Items</span>
                            <button
                              type="button"
                              onClick={() => {
                                const newRow = {
                                  id: Date.now().toString(),
                                  description: '',
                                  unit: '',
                                  price: ''
                                };
                                const updatedSections = sections.map(s =>
                                  s.id === section.id ? { ...s, rows: [...s.rows, newRow] } : s
                                );
                                updateFormData({ recommendationSections: updatedSections });
                              }}
                              className="px-3 py-1 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm"
                            >
                              + Add Item
                            </button>
                          </div>
                          
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {section.rows.map((row) => {
                          const handleAddNewRow = () => {
                            const newRow = {
                              id: Date.now().toString(),
                              description: '',
                              unit: '',
                              price: ''
                            };
                            const currentRows = section.rows;
                            const currentIndex = currentRows.findIndex(r => r.id === row.id);
                            const updatedRows = [
                              ...currentRows.slice(0, currentIndex + 1),
                              newRow,
                              ...currentRows.slice(currentIndex + 1)
                            ];
                            const updatedSections = (formData.recommendationSections || []).map(s =>
                              s.id === section.id ? { ...s, rows: updatedRows } : s
                            );
                            updateFormData({ recommendationSections: updatedSections });
                            
                            newRowDescriptionRef.current = newRow.id;
                            setTimeout(() => {
                              const newRowInput = document.querySelector(`[data-row-id="${newRow.id}"]`) as HTMLInputElement | HTMLTextAreaElement;
                              if (newRowInput) {
                                newRowInput.focus();
                                newRowDescriptionRef.current = null;
                              }
                            }, 50);
                          };
                          
                          return (
                            <div key={row.id} className="flex items-center gap-1 p-2 border border-gray-200 rounded-md w-full">
                              <AutocompleteInput
                                value={row.description}
                                onChange={(value) => {
                                  const updatedSections = (formData.recommendationSections || []).map(s =>
                                    s.id === section.id
                                      ? { ...s, rows: s.rows.map(r => r.id === row.id ? { ...r, description: value } : r) }
                                      : s
                                  );
                                  updateFormData({ recommendationSections: updatedSections });
                                }}
                                onSelectRow={(selectedRow: SheetRow) => {
                                  const priceWithoutDollar = (selectedRow.price || '').replace(/^\$/, '').trim();
                                  const updatedSections = (formData.recommendationSections || []).map(s =>
                                    s.id === section.id
                                      ? { ...s, rows: s.rows.map(r => r.id === row.id ? { 
                                          ...r, 
                                          description: selectedRow.description,
                                          unit: selectedRow.unit || '1',
                                          price: priceWithoutDollar
                                        } : r) }
                                      : s
                                  );
                                  updateFormData({ recommendationSections: updatedSections });
                                }}
                                dataRowId={row.id}
                                placeholder="Description"
                                field="description"
                                className="w-full min-w-0 mr-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                                sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                                sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                              />
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <AutocompleteInput
                                  value={row.unit}
                                  onChange={(value) => {
                                    const updatedSections = (formData.recommendationSections || []).map(s =>
                                      s.id === section.id
                                        ? { ...s, rows: s.rows.map(r => r.id === row.id ? { ...r, unit: value } : r) }
                                        : s
                                    );
                                    updateFormData({ recommendationSections: updatedSections });
                                  }}
                                  onSelectRow={(selectedRow: SheetRow) => {
                                    const priceWithoutDollar = (selectedRow.price || '').replace(/^\$/, '').trim();
                                    const updatedSections = (formData.recommendationSections || []).map(s =>
                                      s.id === section.id
                                        ? { ...s, rows: s.rows.map(r => r.id === row.id ? { 
                                            ...r, 
                                            description: selectedRow.description,
                                            unit: selectedRow.unit || '1',
                                            price: priceWithoutDollar
                                          } : r) }
                                        : s
                                    );
                                    updateFormData({ recommendationSections: updatedSections });
                                  }}
                                  dataRowId={row.id}
                                  placeholder="Unit"
                                  field="unit"
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                                  sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                                  sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                                />
                                <input
                                  type="text"
                                  value={row.price}
                                  onChange={(e) => {
                                    const updatedSections = (formData.recommendationSections || []).map(s =>
                                      s.id === section.id
                                        ? { ...s, rows: s.rows.map(r => r.id === row.id ? { ...r, price: e.target.value } : r) }
                                        : s
                                    );
                                    updateFormData({ recommendationSections: updatedSections });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddNewRow();
                                    }
                                  }}
                                  placeholder="Price"
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedSections = (formData.recommendationSections || []).map(s =>
                                    s.id === section.id
                                      ? { ...s, rows: s.rows.filter(r => r.id !== row.id) }
                                      : s
                                  );
                                  updateFormData({ recommendationSections: updatedSections });
                                }}
                                className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                                title="Delete row"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                    );
                  })()}
                </div>

                {/* Legacy Section 1 UI - Keep for backward compatibility but hide if using new sections */}
                {(!formData.recommendationSections || formData.recommendationSections.length === 0) && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Repair Estimate Section 1
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const newRow = {
                          id: Date.now().toString(),
                          description: '',
                          unit: '',
                          price: ''
                        };
                        updateFormData({ 
                          repairEstimateData: { 
                            estimateNumber: '',
                            paymentMethod: '',
                            paymentNumber: '',
                            rows: [...(formData.repairEstimateData?.rows || []), newRow]
                          } 
                        });
                      }}
                      className="px-3 py-1 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm"
                    >
                      + Add Item
                    </button>
                  </div>
                  <div className="mb-2">
                    <input
                      type="text"
                      placeholder="Title for Repair Estimate#1"
                      value={formData.recommendationSection1Title || 'Repair Estimate#1'}
                      onChange={(e) => updateFormData({ recommendationSection1Title: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                    />
                  </div>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(formData.repairEstimateData?.rows || []).map((row, index) => {
                      // Function to add a new row after the current row
                      const handleAddNewRow = () => {
                        const newRow = {
                          id: Date.now().toString(),
                          description: '',
                          unit: '',
                          price: ''
                        };
                        const currentRows = formData.repairEstimateData?.rows || [];
                        const currentIndex = currentRows.findIndex(r => r.id === row.id);
                        const updatedRows = [
                          ...currentRows.slice(0, currentIndex + 1),
                          newRow,
                          ...currentRows.slice(currentIndex + 1)
                        ];
                        
                        updateFormData({ 
                          repairEstimateData: { 
                            estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                            paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                            paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                            rows: updatedRows
                          } 
                        });
                        
                        // Store the new row ID to focus it after render
                        newRowDescriptionRef.current = newRow.id;
                        
                        // Focus the new row's description field after a short delay
                        setTimeout(() => {
                          const newRowInput = document.querySelector(`[data-row-id="${newRow.id}"]`) as HTMLInputElement | HTMLTextAreaElement;
                          if (newRowInput) {
                            newRowInput.focus();
                            newRowDescriptionRef.current = null;
                          }
                        }, 50);
                      };
                      
                      return (
                      <div key={row.id} className="flex items-center gap-1 p-2 border border-gray-200 rounded-md w-full">
                        <AutocompleteInput
                          value={row.description}
                          onChange={(value) => {
                            const updatedRows = (formData.repairEstimateData?.rows || []).map(r => 
                              r.id === row.id ? { ...r, description: value } : r
                            );
                            updateFormData({ 
                              repairEstimateData: { 
                                estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                                paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                                paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          onSelectRow={(selectedRow: SheetRow) => {
                            // When a row is selected from description field, populate all three fields
                            const priceValue = selectedRow.price || '';
                            // Remove $ sign and store as plain number string (e.g., "400" not "$400")
                            const priceWithoutDollar = priceValue.replace(/^\$/, '').trim();
                            
                            const updatedRows = (formData.repairEstimateData?.rows || []).map(r => 
                              r.id === row.id ? { 
                                ...r, 
                                description: selectedRow.description,
                                unit: selectedRow.unit || '1', // Default to "1" if not provided
                                price: priceWithoutDollar // Store without $ sign
                              } : r
                            );
                            updateFormData({ 
                              repairEstimateData: { 
                                estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                                paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                                paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          dataRowId={row.id}
                          placeholder="Description"
                          field="description"
                          className=" w-full min-w-0 mr-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                          sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                          sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                        />
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <AutocompleteInput
                            value={row.unit}
                            onChange={(value) => {
                              const updatedRows = (formData.repairEstimateData?.rows || []).map(r => 
                                r.id === row.id ? { ...r, unit: value } : r
                              );
                              updateFormData({ 
                                repairEstimateData: { 
                                  estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                                  paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                                  paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                                  rows: updatedRows
                                } 
                              });
                            }}
                            onEnterKey={handleAddNewRow}
                            dataRowId={row.id}
                            placeholder="Unit"
                            field="unit"
                            className="w-12 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                            sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                            sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                          />
                          <input
                            type="text"
                            value={row.price}
                            data-row-id={row.id}
                            onChange={(e) => {
                              // Remove $ sign if user types it
                              const priceWithoutDollar = e.target.value.replace(/^\$/, '').trim();
                              const updatedRows = (formData.repairEstimateData?.rows || []).map(r => 
                                r.id === row.id ? { ...r, price: priceWithoutDollar } : r
                              );
                              updateFormData({ 
                                repairEstimateData: { 
                                  estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                                  paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                                  paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                                  rows: updatedRows
                                } 
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddNewRow();
                              }
                            }}
                            placeholder="Price"
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedRows = (formData.repairEstimateData?.rows || []).filter(r => r.id !== row.id);
                            updateFormData({ 
                              repairEstimateData: { 
                                estimateNumber: formData.repairEstimateData?.estimateNumber || '',
                                paymentMethod: formData.repairEstimateData?.paymentMethod || '',
                                paymentNumber: formData.repairEstimateData?.paymentNumber || '',
                                rows: updatedRows
                              } 
                            });
                          }}
                          className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                          title="Delete row"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Legacy Section 2 Controls - Keep for backward compatibility but hide if using new sections */}
                {(!formData.recommendationSections || formData.recommendationSections.length === 0) && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Repair Estimate Section 2</div>
                    {!formData.showRecommendationSection2 ? (
                      <button
                        type="button"
                        onClick={() => updateFormData({ showRecommendationSection2: true, recommendationSection2: { rows: [] } })}
                        className="px-3 py-1 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm"
                      >
                        + Add Repair Estimate Section 2
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => updateFormData({ showRecommendationSection2: false, recommendationSection2: { rows: [] }, recommendationSection2Title: '' })}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                      >
                        Remove Repair Estimate Section 2
                      </button>
                    )}
                  </div>

                  {formData.showRecommendationSection2 && (
                    <div className="mt-3 space-y-3">
                      <input
                        type="text"
                        placeholder="Title for Repair Estimate#2"
                        value={formData.recommendationSection2Title !== undefined ? formData.recommendationSection2Title : 'Repair Estimate#2'}
                        onChange={(e) => updateFormData({ recommendationSection2Title: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Items (Repair Estimate Section 2)</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newRow = { id: Date.now().toString(), description: '', unit: '', price: '' };
                            const existing = formData.recommendationSection2?.rows || [];
                            updateFormData({ recommendationSection2: { rows: [...existing, newRow] } });
                          }}
                          className="px-3 py-1 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] text-sm"
                        >
                          + Add Item (Section 2)
                        </button>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(formData.recommendationSection2?.rows || []).map((row) => {
                          // Function to add a new row after the current row for Section 2
                          const handleAddNewRowSection2 = () => {
                            const newRow = {
                              id: Date.now().toString(),
                              description: '',
                              unit: '',
                              price: ''
                            };
                            const currentRows = formData.recommendationSection2?.rows || [];
                            const currentIndex = currentRows.findIndex(r => r.id === row.id);
                            const updatedRows = [
                              ...currentRows.slice(0, currentIndex + 1),
                              newRow,
                              ...currentRows.slice(currentIndex + 1)
                            ];
                            
                            updateFormData({ recommendationSection2: { rows: updatedRows } });
                            
                            // Store the new row ID to focus it after render
                            newRowDescriptionRef.current = newRow.id;
                            
                            // Focus the new row's description field after a short delay
                            setTimeout(() => {
                              const newRowInput = document.querySelector(`[data-row-id="${newRow.id}"]`) as HTMLInputElement | HTMLTextAreaElement;
                              if (newRowInput) {
                                newRowInput.focus();
                                newRowDescriptionRef.current = null;
                              }
                            }, 50);
                          };
                          
                          return (
                          <div key={row.id} className="flex items-center gap-1 p-2 border border-gray-200 rounded-md w-full">
                            <AutocompleteInput
                              value={row.description}
                              onChange={(value) => {
                                const updatedRows = (formData.recommendationSection2?.rows || []).map(r => 
                                  r.id === row.id ? { ...r, description: value } : r
                                );
                                updateFormData({ recommendationSection2: { rows: updatedRows } });
                              }}
                              onSelectRow={(selectedRow: SheetRow) => {
                                // When a row is selected from description field, populate all three fields
                                const priceValue = selectedRow.price || '';
                                // Remove $ sign and store as plain number string (e.g., "400" not "$400")
                                const priceWithoutDollar = priceValue.replace(/^\$/, '').trim();
                                
                                const updatedRows = (formData.recommendationSection2?.rows || []).map(r => 
                                  r.id === row.id ? { 
                                    ...r, 
                                    description: selectedRow.description,
                                    unit: selectedRow.unit || '1', // Default to "1" if not provided
                                    price: priceWithoutDollar // Store without $ sign
                                  } : r
                                );
                                updateFormData({ recommendationSection2: { rows: updatedRows } });
                              }}
                              dataRowId={row.id}
                              placeholder="Description"
                              field="description"
                              className="w-full min-w-0 mr-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                              sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                              sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                            />
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <AutocompleteInput
                              value={row.unit}
                                onChange={(value) => {
                                const updatedRows = (formData.recommendationSection2?.rows || []).map(r => 
                                    r.id === row.id ? { ...r, unit: value } : r
                                );
                                updateFormData({ recommendationSection2: { rows: updatedRows } });
                              }}
                              onEnterKey={handleAddNewRowSection2}
                              dataRowId={row.id}
                                placeholder="Unit"
                                field="unit"
                              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                                sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                                sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Repairs!A:E'}
                            />
                              <input
                                type="text"
                                value={row.price}
                                data-row-id={row.id}
                                onChange={(e) => {
                                  const updatedRows = (formData.recommendationSection2?.rows || []).map(r => 
                                    r.id === row.id ? { ...r, price: e.target.value } : r
                                  );
                                  updateFormData({ recommendationSection2: { rows: updatedRows } });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddNewRowSection2();
                                  }
                                }}
                                placeholder="Price"
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updatedRows = (formData.recommendationSection2?.rows || []).filter(r => r.id !== row.id);
                                updateFormData({ recommendationSection2: { rows: updatedRows } });
                              }}
                              className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                              title="Delete row"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Notes Section */}
                <div className="pt-4 border-t border-gray-200">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={(() => {
                      const baseNotes = formData.notes !== undefined ? formData.notes : 'This quote is good for 30 days from date of service. Deposits for scheduled future service is non-refundable.';
                      if (formData.dataSourceUrl && !baseNotes.includes('Link to view timeline:')) {
                        return `${baseNotes}\n\nLink to view timeline: ${formData.dataSourceUrl}`;
                      }
                      return baseNotes;
                    })()}
                    onChange={(e) => {
                      const fullText = e.target.value;
                      // Extract the link part if it exists at the end
                      const linkPattern = /\n\nLink to view timeline:\s*(.+)$/;
                      const linkMatch = fullText.match(linkPattern);
                      
                      if (linkMatch) {
                        // Link found at the end - extract it and save separately
                        const url = linkMatch[1].trim();
                        const notesWithoutLink = fullText.replace(linkPattern, '').trim();
                        updateFormData({ 
                          notes: notesWithoutLink,
                          dataSourceUrl: url
                        });
                      } else {
                        // No link pattern found - check if user removed it
                        // If we had a URL before but it's not in the text now, clear it
                        if (formData.dataSourceUrl && !fullText.includes('Link to view timeline:')) {
                          updateFormData({ notes: fullText, dataSourceUrl: '' });
                        } else {
                          // Just update notes, preserve existing URL
                          updateFormData({ notes: fullText });
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                    rows={3}
                    placeholder="Enter notes for the repair estimate..."
                  />
                </div>
              </div>
            ) : isImagePage6(currentPage) ? (
              // Step 6 - Image Selection Interface
              <div className="space-y-4">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-800">
                      Select Project Images
                    </h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddStep6Page}
                        className="px-3 py-1.5 bg-[#722420] text-white rounded text-sm font-medium hover:bg-[#5a1d1a] transition-colors"
                        title="Add new invoice page"
                      >
                        + Add Page
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    <p className="text-sm text-gray-600">
                      Selected: {formData.selectedImages?.length || 0} images
                      {formData.step6TotalPages && formData.step6TotalPages > 1 && (
                        <span className="ml-2">({formData.step6TotalPages} pages)</span>
                      )}
                    </p>
                    {formData.step6TotalPages && formData.step6TotalPages > 1 && (
                      <button
                        onClick={() => {
                          const currentStep6Page = formData.step6CurrentPage || 1;
                          if (currentStep6Page > 1) {
                            handleRemoveStep6Page(currentStep6Page);
                          }
                        }}
                        disabled={(formData.step6CurrentPage || 1) === 1}
                        className="px-2 py-1 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove current page"
                      >
                        Remove Current Page
                      </button>
                    )}
                  </div>
                  
                  {/* Image management by page */}
                  {formData.step6TotalPages && formData.step6TotalPages > 1 && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Images by Page:</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {Array.from({ length: formData.step6TotalPages }, (_, i) => i + 1).map(pageNum => {
                          const pageImages = (formData.selectedImages || []).filter(img => (img.pageNumber || 1) === pageNum);
                          return (
                            <div key={pageNum} className="text-xs">
                              <span className="font-medium">Page {pageNum}:</span> {pageImages.length} image{pageImages.length !== 1 ? 's' : ''}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Scraped Images Section */}
                <div className="space-y-3 border border-gray-200 p-4 rounded-lg" >
                  <h5 className="text-md font-semibold text-gray-700 flex items-center space-x-2">
                    <svg className="w-5 h-5 text-[#722420]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Images from Inspection</span>
                  </h5>
                  
                {(formData.scrapedImages?.length || 0) > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {formData.scrapedImages?.map((image) => {
                      const isSelected = formData.selectedImages?.some(img => img.id === image.id) || false;
                      const currentPage = formData.step6CurrentPage || 1;
                      
                      return (
                        <div
                          key={image.id}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            isSelected
                              ? 'border-[#722420] shadow-lg scale-105'
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                          onClick={() => {
                            const currentSelected = formData.selectedImages || [];
                            let newSelection: ImageItem[];

                            if (isSelected) {
                              // Remove image from selection
                              newSelection = currentSelected.filter(img => img.id !== image.id);
                            } else {
                              // Add image to selection - distribute across pages (4 per page)
                              const MAX_IMAGES_PER_PAGE = 4;
                              // Calculate which page this image should go to based on total count
                              const totalImagesAfterAdd = currentSelected.length + 1;
                              const targetPage = Math.ceil(totalImagesAfterAdd / MAX_IMAGES_PER_PAGE);
                              
                              newSelection = [...currentSelected, { ...image, pageNumber: targetPage }];
                            }

                            handleImageSelection(newSelection);
                          }}
                        >
                          <div className="aspect-square bg-gray-100">
                            <img
                              src={image.url}
                              alt={image.alt || 'Scraped image'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </div>
                          
                          {isSelected && (
                            <>
                              <div className="absolute top-2 right-2 bg-[#722420] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                                ✓
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCropInvoiceImage(image.id);
                                }}
                                className="absolute top-2 left-2 bg-red-900 hover:bg-red-950 text-white rounded px-2 py-1 text-xs font-medium transition-colors"
                                title="Crop this image"
                              >
                                ✂️ Crop
                              </button>
                            </>
                          )}
                          
                          {isSelected && (
                            <div className="absolute bottom-0 left-0 right-0 bg-[#722420] text-white text-xs">
                              <select
                                value={(formData.selectedImages?.find(img => img.id === image.id)?.pageNumber || 1)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleMoveImageToPage(image.id, Number(e.target.value));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full bg-[#722420] text-white text-xs p-1 text-center border-none cursor-pointer appearance-none focus:outline-none hover:bg-[#8a2e2a]"
                                title="Move to page"
                                style={{
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 4px center',
                                  paddingRight: '20px'
                                }}
                              >
                                {(() => {
                                  // Get list of pages that exist (have images or were manually added)
                                  const existingPages = new Set<number>();
                                  (formData.selectedImages || []).forEach(img => {
                                    existingPages.add(img.pageNumber || 1);
                                  });
                                  // Include manually added pages (up to totalPages)
                                  for (let i = 1; i <= (formData.step6TotalPages || 1); i++) {
                                    existingPages.add(i);
                                  }
                                  return Array.from(existingPages).sort((a, b) => a - b);
                                })().map(pageNum => (
                                  <option key={pageNum} value={pageNum} className="bg-white text-gray-800">
                                    Page {pageNum}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="text-center">
                      <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm mb-1">No scraped images available</p>
                      <p className="text-xs text-gray-400">Images will appear here after data scraping</p>
                    </div>
                  </div>
                )}
                </div>
                <h5 className="text-md font-semibold text-gray-700 flex items-center space-x-2">
                    {/* <svg className="w-5 h-5 text-[#722420]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg> */}
                    <span> Upload Images</span>
                  </h5>
                <div className="space-y-3 border border-gray-200 p-4 rounded-lg" >
                  
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 shadow-md flex items-center justify-center space-x-2 bg-[#722420] text-white hover:bg-[#5a1d1a] hover:shadow-lg"
                  >
                     
                    <span>
                      📤 Upload from Device
                    </span>
                  </button>
             
                {/* Uploaded Images Section */}
                {(() => {
                  const uploadedImages = (formData.selectedImages || []).filter(img => img.id.startsWith('uploaded-'));
                  return uploadedImages.length > 0 && (
                    <div className="space-y-3 border-t border-gray-200 pt-4">
                      <h5 className="text-md font-semibold text-gray-700 flex items-center space-x-2">
                        
                        <span>Uploaded Images ({uploadedImages.length})</span>
                      </h5>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {uploadedImages.map((image, index) => {
                          const isSelected = formData.selectedImages?.some(img => img.id === image.id) || false;
                          
                          return (
                            <div
                              key={image.id}
                              className="relative rounded-lg overflow-hidden border-2 border-[#722420] shadow-md"
                            >
                              <div className="aspect-square bg-gray-100">
                                <img
                                  src={image.url}
                                  alt={image.alt || 'Uploaded image'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}
                                />
                              </div>
                              
                              {/* Remove button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newSelection = (formData.selectedImages || []).filter(img => img.id !== image.id);
                                  handleImageSelection(newSelection);
                                }}
                                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shadow-lg transition-colors"
                                title="Remove this image"
                              >
                                ✕
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCropInvoiceImage(image.id);
                                }}
                                className="absolute top-2 left-2 bg-[#722420] hover:bg-[#5a1d1a] text-white rounded px-2 py-1 text-xs font-medium transition-colors shadow-lg"
                                title="Crop this image"
                              >
                                ✂️ Crop
                              </button>
                              
                              {isSelected && (
                                <div className="absolute bottom-0 left-0 right-0 bg-[#722420] text-white text-xs">
                                  <select
                                    value={(formData.selectedImages?.find(img => img.id === image.id)?.pageNumber || 1)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleMoveImageToPage(image.id, Number(e.target.value));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-[#722420] text-white text-xs p-1 text-center border-none cursor-pointer appearance-none focus:outline-none hover:bg-[#8a2e2a]"
                                    title="Move to page"
                                    style={{
                                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundPosition: 'right 4px center',
                                      paddingRight: '20px'
                                    }}
                                  >
                                    {(() => {
                                      // Get list of pages that exist (have images or were manually added)
                                      const existingPages = new Set<number>();
                                      (formData.selectedImages || []).forEach(img => {
                                        existingPages.add(img.pageNumber || 1);
                                      });
                                      // Include manually added pages (up to totalPages)
                                      for (let i = 1; i <= (formData.step6TotalPages || 1); i++) {
                                        existingPages.add(i);
                                      }
                                      return Array.from(existingPages).sort((a, b) => a - b);
                                    })().map(pageNum => (
                                      <option key={pageNum} value={pageNum} className="bg-white text-gray-800">
                                        Page {pageNum}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
   </div>
                {(formData.selectedImages?.length || 0) > 0 && (
                  <div className="mt-4 space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="text-green-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            {formData.selectedImages?.length} image{(formData.selectedImages?.length || 0) !== 1 ? 's' : ''} selected for PDF
                          </p>
                          <p className="text-xs text-green-600">Selected images will appear in the final report</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Selected Images with Crop Options */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Selected Images (Click to crop)</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {formData.selectedImages?.map((image, index) => (
                          <div
                            key={image.id}
                            className="relative border-2 border-[#722420] rounded-lg overflow-hidden"
                          >
                            <div className="aspect-square bg-gray-100">
                              <img
                                src={image.url}
                                alt={`Selected image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute top-1 left-1 bg-[#722420] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            
                            
                            
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {(formData.selectedImages?.length || 0) > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Tip:</strong> Drag images to move them, or drag the resize handles (right edge, bottom edge, or corner) to resize them. The "Written Invoice" text can also be dragged to reposition it.
                    </p>
                  </div>
                )}

              </div>
            ) : currentLogicalStep === 7 ? (
              // Step 7 - Repair Estimate Interface
              <div className="space-y-4">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    Repair Estimate Configuration
                  </h4>
                  
                  {/* Page Management Buttons */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-gray-700">Page Management</h5>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowReviewImageSelector(true)}
                          disabled={getAvailableImages().length === 0}
                          className={`px-3 py-1.5 rounded text-sm transition-colors ${
                            getAvailableImages().length > 0
                              ? 'bg-[#722420] text-white hover:bg-[#5a1d1a]' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          + Add New Page
                        </button>
                        {formData.repairEstimatePages && formData.repairEstimatePages.length > 0 && (
                          <>
                            <button
                              onClick={() => setShowChangeImageModal(true)}
                              disabled={getAvailableImages().length === 0}
                              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                                getAvailableImages().length > 0
                                  ? 'bg-gray-800 text-white hover:bg-[black]' 
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              Change Image
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete page ${currentRecommendationPageIndex + 1}?`)) {
                                  deleteRecommendationPage(currentRecommendationPageIndex);
                                }
                              }}
                              className="px-3 py-1.5 bg-[#722420] text-white rounded text-sm hover:bg-[#5a1d1a] transition-colors"
                            >
                              Delete Current Page
                            </button>
                            
                          </>
                        )}
                      </div>
                    </div>
                    
                  </div>
                </div>

                {/* Table Management */}
                {getCurrentRecommendationPage() && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h5 className="font-medium text-gray-700">Add Items</h5>
                      <button
                        onClick={() => setShowAddRowModal(true)}
                        className="px-3 py-1.5 bg-[#722420] text-white rounded text-sm hover:bg-[#5a1d1a] transition-colors"
                      >
                        + Add Row
                      </button>
                    </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '65%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '13%' }} />
                      </colgroup>
                      <thead className="bg-[#722420]">
                        <tr>
                          <th className="px-2 py-2 text-left text-white font-bold text-xs" style={{ borderBottom: '1px solid #722420' }}>Description</th>
                          <th className="px-1 py-2 text-center text-white font-bold text-xs" style={{ borderBottom: '1px solid #722420' }}>Unit</th>
                          <th className="px-1 py-2 text-center text-white font-bold text-xs" style={{ borderBottom: '1px solid #722420' }}>Price</th>
                          <th className="px-2 py-2 text-center text-white font-bold text-xs" style={{ borderBottom: '1px solid #722420' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(getCurrentRecommendationPage()?.repairEstimateData.rows || []).map((row: any, index: number) => (
                          <tr key={row.id}>
                            <td className="px-2 py-2" style={{ width: '65%' }}>
                              <div className="w-full">
                                <AutocompleteInput 
                                  value={row.description || ''}
                                  onChange={(value) => {
                                    const currentPage = getCurrentRecommendationPage();
                                    if (currentPage) {
                                      const updatedRows = currentPage.repairEstimateData.rows.map((r: any) => 
                                        r.id === row.id ? { ...r, description: value } : r
                                      );
                                      updateCurrentRecommendationPage({
                                        repairEstimateData: {
                                          ...currentPage.repairEstimateData,
                                          rows: updatedRows
                                        }
                                      });
                                    }
                                  }}
                                  onSelectRow={(selectedRow: SheetRow) => {
                                    const currentPage = getCurrentRecommendationPage();
                                    if (currentPage) {
                                      // When a row is selected from description field, populate all three fields
                                      const priceValue = selectedRow.price || '';
                                      // Remove $ sign and store as plain number string (e.g., "400" not "$400")
                                      const priceWithoutDollar = priceValue.replace(/^\$/, '').trim();
                                      
                                      const updatedRows = currentPage.repairEstimateData.rows.map((r: any) => 
                                        r.id === row.id ? { 
                                          ...r, 
                                          description: selectedRow.description,
                                          unit: selectedRow.unit || '1', // Default to "1" if not provided
                                          price: priceWithoutDollar // Store without $ sign
                                        } : r
                                      );
                                      updateCurrentRecommendationPage({
                                        repairEstimateData: {
                                          ...currentPage.repairEstimateData,
                                          rows: updatedRows
                                        }
                                      });
                                    }
                                  }}
                                  placeholder="Enter description (Press Enter for new line)"
                                  field="description"
                                  className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                                  sheetId={process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q'}
                                  sheetRange={process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E'}
                                />
                              </div>
                            </td>
                            <td className="px-1 py-2" style={{ width: '10%' }}>
                              <input
                                type="text"
                                value={String(row.unit || '')}
                                onChange={(e) => {
                                  const currentPage = getCurrentRecommendationPage();
                                  if (currentPage) {
                                    const updatedRows = currentPage.repairEstimateData.rows.map((r: any) => 
                                      r.id === row.id ? { ...r, unit: parseInt(e.target.value) || 0 } : r
                                    );
                                    updateCurrentRecommendationPage({
                                      repairEstimateData: {
                                        ...currentPage.repairEstimateData,
                                        rows: updatedRows
                                      }
                                    });
                                  }
                                }}
                                placeholder="Unit"
                                className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                              />
                            </td>
                            <td className="px-1 py-2" style={{ width: '12%' }}>
                              <input
                                type="text"
                                value={row.price ? `$${row.price}` : ''}
                                onChange={(e) => {
                                  const currentPage = getCurrentRecommendationPage();
                                  if (currentPage) {
                                    // Remove $ and parse as float
                                    const numericValue = e.target.value.replace(/^\$/, '').trim();
                                    const updatedRows = currentPage.repairEstimateData.rows.map((r: any) => 
                                      r.id === row.id ? { ...r, price: parseFloat(numericValue) || 0 } : r
                                    );
                                    updateCurrentRecommendationPage({
                                      repairEstimateData: {
                                        ...currentPage.repairEstimateData,
                                        rows: updatedRows
                                      }
                                    });
                                  }
                                }}
                                placeholder="Price"
                                className="w-full px-1 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#722420]"
                              />
                            </td>
                            <td className="px-1 py-2" style={{ width: '13%' }}>
                              <button
                              onClick={() => {
                                const currentPage = getCurrentRecommendationPage();
                                if (currentPage) {
                                  const updatedRows = currentPage.repairEstimateData.rows.filter((r: any) => r.id !== row.id);
                                  updateCurrentRecommendationPage({
                                    repairEstimateData: {
                                      ...currentPage.repairEstimateData,
                                      rows: updatedRows
                                    }
                                  });
                                }
                              }}
                                className="w-full py-0.5  text-red-600 hover:text-red-800 text-m whitespace-nowrap flex items-center justify-center"
                                title="Remove"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!getCurrentRecommendationPage()?.repairEstimateData.rows || getCurrentRecommendationPage()?.repairEstimateData.rows.length === 0) && (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                              No items added yet. Click "Add Row" to add repair items.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}

                {/* Recommendation Section - Show when there are any rows */}
                {getCurrentRecommendationPage()?.repairEstimateData.rows && getCurrentRecommendationPage()?.repairEstimateData.rows.length > 0 && (
                  <div className="space-y-3 mt-4">
                    <h5 className="font-medium text-gray-700">Professional Recommendation</h5>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <label className="block text-sm font-medium text-blue-800 mb-2">
                        Recommendation Text:
                      </label>
                      <textarea
                        value={(() => {
                          const currentPage = getCurrentRecommendationPage();
                          if (!currentPage) return '';
                          // Use custom recommendation if it's explicitly set and not empty, otherwise combine from rows
                          if (currentPage.customRecommendation !== undefined && 
                              currentPage.customRecommendation !== null && 
                              currentPage.customRecommendation.trim() !== '') {
                            return currentPage.customRecommendation;
                          }
                          // Combine recommendations from all rows, preserving line breaks
                          const rowRecommendations = (currentPage.repairEstimateData.rows || [])
                            .filter((r: any) => r.recommendation)
                            .map((r: any) => r.recommendation)
                            .join('\n');
                          return rowRecommendations || '';
                        })()}
                        onChange={(e) => {
                          // Update customRecommendation when user edits
                          handleCustomRecommendationChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          // Allow Enter key to create new lines (default behavior)
                          // No need to prevent default - let textarea handle it naturally
                        }}
                        placeholder="Enter recommendation text here..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                        rows={6}
                        style={{ fontFamily: 'Inter, Arial, sans-serif', whiteSpace: 'pre-wrap' }}
                      />
                      <p className="text-xs text-blue-600 mt-2">
                        This text will be displayed in the Professional Recommendations section on Recommendation Pages.
                      </p>
                    </div>
                  </div>
                )}
                {getCurrentRecommendationPage()?.reviewImage && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                  💡 <strong>Tip:</strong> Drag the image to move it, or drag the resize handle (bottom-right corner) to resize it.
                                </p>
                              </div>
                            )}
              </div>
              
            ) : isImagePage(currentPage) ? (
              <div className="py-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-semibold text-black mb-2">Step 8 - Inspection Images</h4>
                  <p className="text-sm text-black mb-2">
                    Manage which images appear in the inspection images section. Remove images you don't want to include.
                  </p>
                  <p className="text-xs text-gray-600">
                    Showing {getUnusedImages().length} image(s) • Page {getImagePageIndex(currentPage) + 1} of {getTotalImagePages()}
                  </p>
                </div>
                
                {/* Upload Images Section */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Upload Images</h5>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        // Process all files and convert to base64
                        const filePromises = files.map((file) => {
                          return new Promise<ImageItem>((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const imageUrl = event.target?.result as string;
                              const newImage: ImageItem = {
                                id: `uploaded-${Date.now()}-${Math.random()}`,
                                url: imageUrl
                              };
                              resolve(newImage);
                            };
                            reader.onerror = () => resolve({ id: '', url: '' });
                            reader.readAsDataURL(file);
                          });
                        });
                        
                        const newImages = await Promise.all(filePromises);
                        const validImages = newImages.filter(img => img.url);
                        
                        if (validImages.length > 0) {
                          const currentUploaded = formData.uploadedInspectionImages || [];
                          const currentImages = getUnusedImages();
                          const allImages = [...currentImages, ...validImages];
                          
                          updateFormData({
                            uploadedInspectionImages: [...currentUploaded, ...validImages],
                            inspectionImagesOrder: allImages.map(img => img.id)
                          });
                        }
                        
                        // Reset file input
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-[#722420] hover:bg-[#5a1d1a] text-white rounded-md text-sm font-medium transition-colors"
                    >
                      📤 Upload Images from PC
                    </button>
                      <button
                        type="button"
                        onClick={loadJobsFromUrl}
                        disabled={!formData.dataSourceUrl || isLoadingJobs}
                        className="px-4 py-2 bg-[#722420] hover:bg-[#5a1d1a] text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Select images from other dates"
                      >
                        {isLoadingJobs ? 'Loading...' : '📅 Select Date Images'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Click to upload images from your computer or select images from other dates. Images will be added to the inspection images section.
                    </p>
                  </div>
                </div>

                {/* All Unused Images List with Drag-and-Drop */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                    All Available Images (Use Up/Down for quick moves, # for specific position, or drag to reorder)
                  </h5>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                    {getUnusedImages().length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No images available. Upload images or restore removed images.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-3">
                        {getUnusedImages().map((image, index) => (
                          <div
                            key={image.id}
                            className="flex flex-col"
                          >
                            {/* Image Container */}
                            <div
                            draggable
                            onDragStart={(e) => {
                              setDraggedImageId(image.id);
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              if (draggedImageId && draggedImageId !== image.id) {
                                e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                              }
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                              if (!draggedImageId || draggedImageId === image.id) return;
                              
                              const currentImages = getUnusedImages();
                              const draggedIndex = currentImages.findIndex(img => img.id === draggedImageId);
                              const targetIndex = currentImages.findIndex(img => img.id === image.id);
                              
                              if (draggedIndex === -1 || targetIndex === -1) return;
                              
                              // Reorder images
                              const reordered = [...currentImages];
                              const [removed] = reordered.splice(draggedIndex, 1);
                              reordered.splice(targetIndex, 0, removed);
                              
                              // Update order
                              updateFormData({
                                inspectionImagesOrder: reordered.map(img => img.id)
                              });
                              
                              setDraggedImageId(null);
                            }}
                            onDragEnd={() => {
                              setDraggedImageId(null);
                              // Remove any drag-over classes
                              document.querySelectorAll('.border-blue-500.bg-blue-50').forEach(el => {
                                el.classList.remove('border-blue-500', 'bg-blue-50');
                              });
                            }}
                            className={`relative aspect-square border-2 rounded overflow-hidden transition-all cursor-move ${
                              draggedImageId === image.id 
                                ? 'border-blue-500 opacity-50' 
                                : 'border-gray-300 hover:border-[#722420]'
                            }`}
                          >
                            <img
                              src={image.url}
                              alt="Inspection"
                              className="w-full h-full object-cover pointer-events-none"
                                draggable={false}
                            />
                            <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                              {index + 1}
                            </div>
                              
                              {/* Remove button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Check if this is an uploaded image
                                const isUploaded = formData.uploadedInspectionImages?.some(img => img.id === image.id);
                                
                                if (isUploaded) {
                                  // Remove uploaded image completely
                                  const updatedUploaded = (formData.uploadedInspectionImages || []).filter(img => img.id !== image.id);
                                  const currentOrder = formData.inspectionImagesOrder || [];
                                  updateFormData({
                                    uploadedInspectionImages: updatedUploaded,
                                    inspectionImagesOrder: currentOrder.filter(id => id !== image.id)
                                  });
                                } else {
                                  // Exclude scraped image
                                  const excluded = formData.excludedStep8Images || [];
                                  if (!excluded.includes(image.id)) {
                                    const currentOrder = formData.inspectionImagesOrder || [];
                                    updateFormData({
                                      excludedStep8Images: [...excluded, image.id],
                                      inspectionImagesOrder: currentOrder.filter(id => id !== image.id)
                                    });
                                  }
                                }
                              }}
                              className="absolute top-1 right-1 bg-[#722420] hover:bg-[#5a1d1a] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg z-10"
                              title="Remove from Step 8"
                            >
                              ×
                            </button>
                            </div>
                            
                            {/* Reorder buttons section below image */}
                            <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2">
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                {/* Move to Top */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveImageToTop(image.id);
                                  }}
                                  disabled={index === 0}
                                  className="bg-[#722420] hover:bg-[#5a1d1a] disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded px-2 py-1 text-xs font-bold shadow transition-colors"
                                  title="Move to top"
                                >
                                  ↑↑
                                </button>
                                {/* Image Number - Click to move to position */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setImageForPositionMove(image.id);
                                    setTargetPosition(String(index + 1));
                                    setShowPositionModal(true);
                                  }}
                                  className="bg-gray-700 hover:bg-gray-800 text-white rounded px-3 py-1 text-sm font-bold shadow transition-colors"
                                  title="Click to move to specific position"
                                >
                                  {index + 1}
                                </button>
                                {/* Move to Bottom */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveImageToBottom(image.id);
                                  }}
                                  disabled={index === getUnusedImages().length - 1}
                                  className="bg-[#722420] hover:bg-[#5a1d1a] disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded px-2 py-1 text-xs font-bold shadow transition-colors"
                                  title="Move to bottom"
                                >
                                  ↓↓
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Excluded Images Section */}
                {getExcludedStep8Images().length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">
                      Removed Images ({getExcludedStep8Images().length})
                    </h5>
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                      <div className="grid grid-cols-4 gap-2">
                        {getExcludedStep8Images().map((image) => (
                          <div
                            key={image.id}
                            className="relative aspect-square border-2 border-dashed border-gray-400 rounded overflow-hidden cursor-pointer hover:border-[#722420] transition-colors"
                            onClick={() => {
                              const excluded = formData.excludedStep8Images || [];
                              updateFormData({
                                excludedStep8Images: excluded.filter(id => id !== image.id)
                              });
                            }}
                            title="Click to restore"
                          >
                            <img
                              src={image.url}
                              alt="Excluded"
                              className="w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-600 bg-white/80 px-1 rounded">
                                Restore
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : getLogicalStep(currentPage) === 9 ? (
              <div className="text-center py-8">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="text-green-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-black mb-2">Step 9 - Documentation (Part {getStep9PartNumber()})</h4>
                  <p className="text-black mb-4">
                    {getStep9PartNumber() === 1 ? 'This page contains chimney documentation information that cannot be edited.' :
                     getStep9PartNumber() === 2 ? 'This page contains additional chimney documentation information that cannot be edited.' :
                     'This page contains final chimney documentation information that cannot be edited.' 
                      }
                  </p>
                  <p className="text-sm text-black">
                    All information is pre-filled and will be included in the final PDF report.
                  </p>
                </div>
              </div>
            ) : getLogicalStep(currentPage) === 10 ? (
              <>
              <div className="text-center py-8">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="text-blue-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-black mb-2">Step 10 - Final Steps (Part {getStep10PartNumber()})</h4>
                  <p className="text-black mb-4">
                    {getStep10PartNumber() === 3 ? 'This page contains the terms and conditions of your inspection report.' :
                     getStep10PartNumber() === 4 ? 'This page contains additional terms and conditions, including payment and cancellation policy.' :
                     'This is the closing page of your report, thanking the client for their business.'}
                  </p>
                  <p className="text-sm text-black">
                    This is part {getStep10PartNumber()} of the final steps in your chimney inspection report.
                  </p>
                </div>
              </div>

              {/* Full Page Checklist - Only on the last page of the report: review and toggle every page */}
              {currentPage === totalPages && (
                <div className="mt-2 text-left">
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-semibold text-gray-700">Review all pages</span>
                      <span className="font-semibold text-[#722420]">
                        {Array.from(includedPages).length} of {totalPages} included
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Check or uncheck any page below to control whether it's included in the final PDF.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={includeAllPages}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-[#722420] border border-[#722420] rounded-md hover:bg-[#5a1d1a] transition-colors duration-200"
                      >
                        Include All
                      </button>
                      <button
                        type="button"
                        onClick={excludeAllPages}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      >
                        Exclude All
                      </button>
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <label
                        key={pageNum}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                          pageNum === currentPage ? 'bg-[#F0D8D6]/40' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isPageIncluded(pageNum)}
                          onChange={() => togglePageInclusion(pageNum)}
                          className="w-4 h-4 rounded border-gray-300 text-[#722420] focus:ring-[#722420] accent-[#722420]"
                        />
                        <span className={`text-sm ${isPageIncluded(pageNum) ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                          {getPageLabel(pageNum)}
                        </span>
                        {pageNum === currentPage && (
                          <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-[#722420]">Current</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              </>
            ) : null}

             {/* Generate Button - Only show on last page */}
             <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
               {currentPage === totalPages ? (
                <>
                  {/* Page inclusion summary + Include All/Exclude All now live in the page checklist above, to avoid showing them twice on this page */}
                  <div className="space-y-2">
                    {/* <button 
                      type="button"
                      onClick={handleDownloadPDF}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isGenerating}
                    >
                      {isGenerating && generationStatus === 'generating'
                        ? 'Generating PDF...'
                        : 'Download PDF'}
                    </button> */}
                    
                    <button 
                      type="button"
                      onClick={handleSubmit}
                      className="w-full btn-primary"
                      disabled={isGenerating}
                    >
                      {isGenerating
                        ? (generationStatus === 'saving'
                            ? 'Saving Data...'
                            : generationStatus === 'uploading'
                              ? 'Uploading Report...'
                              : 'Generating PDF...')
                        : 'Generate Report'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 mb-3">
                    {currentLogicalStep === 1 ? 'Fill in the client information above' : 
                     currentLogicalStep === 2 ? 'Review the static content' : 
                     currentLogicalStep === 3 ? 'Review service report details' : 
                     currentLogicalStep === 4 ? 'Review chimney type details' : 
                     currentLogicalStep === 5 ? (isInvoicePage(currentPage) ? 'Review invoice details' : 'Review repair estimate details') : 
                     currentLogicalStep === 6 ? 'Select project images for the report' :
                     currentLogicalStep === 7 ? 'Configure repair estimate table' : 
                     currentLogicalStep === 8 ? 'Review inspection images' :
                     currentLogicalStep === 9 ? 'Review chimney documentation' :
                     currentLogicalStep === 10 ? 'Review final steps and completion information' :
                     "This page is static and not editable"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentPage < totalPages ? 'Navigate through all pages to complete your report' : 'All pages completed - ready to generate report'}
                  </p>
                </div>
              )}
              
              {/* Mobile optimization note */}
              {isMobile && currentPage === 5 && (
                <div className="mt-3 text-xs text-gray-500 text-center">
                  📱 Mobile optimized: PDF will be generated with desktop-quality layout
                </div>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div>
            {/* Improved Header Section */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-300">
              <div className="p-3">
                {/* Page Navigation */}
                <div className="flex items-center justify-end mb-2">
              <div className="flex items-center space-x-2">
                {(() => {
                  // Calculate the actual page number being displayed
                  let actualPageNumber = currentPage;
                  if (currentLogicalStep === 7) {
                    const imagePageNum = 4 + totalInvoicePages + totalRepairEstimatePages + 1;
                    actualPageNumber = imagePageNum + 1 + currentRecommendationPageIndex;
                  } else if (currentLogicalStep === 8) {
                    actualPageNumber = currentPage;
                  }
                  
                  return (
                    <>
                      {(actualPageNumber > 1) && (
                        <button
                          onClick={handlePrevPage}
                          disabled={isSavingStep}
                              className="px-3 py-2 text-sm font-medium bg-white text-[#722420] border border-[#722420] rounded-lg hover:bg-[#722420] hover:text-white transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                              Prev
                        </button>
                      )}
                          <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border border-gray-300 shadow-sm">
                            <span className="text-xs text-gray-500 hidden sm:inline">Page</span>
                      <label className="sr-only" htmlFor="jumpToPage">Jump to page</label>
                      <input
                        id="jumpToPage"
                        type="number"
                        min={1}
                        max={totalPages}
                        value={actualPageNumber}
                        onChange={(e) => {
                          const next = Number(e.target.value || 1);
                          if (!Number.isFinite(next)) return;
                          const clamped = Math.max(1, Math.min(totalPages, Math.floor(next)));
                          if (isRecommendationPage(clamped)) {
                            const idx = getRecommendationPageIndex(clamped);
                            setCurrentRecommendationPageIndex(idx);
                          }
                          setCurrentPage(clamped);
                        }}
                              className="w-12 text-center text-sm font-semibold text-gray-900 border-0 focus:outline-none focus:ring-0 p-0"
                        aria-label="Jump to page"
                      />
                            <span className="text-xs text-gray-400">/</span>
                            <span className="text-xs text-gray-600 font-medium">{totalPages}</span>
                          </div>
                      {(actualPageNumber < totalPages) && (
                        <button
                          onClick={handleNextPage}
                          disabled={isSavingStep}
                              className="px-3 py-2 text-sm font-medium bg-white text-[#722420] border border-[#722420] rounded-lg hover:bg-[#722420] hover:text-white transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                              Next
                              <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                        </button>
                      )}
                      {(actualPageNumber < totalPages) && (
                        <button
                          onClick={handleGoToLastPage}
                          disabled={isSavingStep}
                          title="Go to last page"
                              className="px-3 py-2 text-sm font-medium bg-white text-[#722420] border border-[#722420] rounded-lg hover:bg-[#722420] hover:text-white transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                              Last
                              <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                              </svg>
                        </button>
                      )}
                    </>
                  );
                })()}
                  </div>
                </div>
                
                {/* Include/Exclude Toggle */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-gray-300">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <svg className={`w-5 h-5 ${isCurrentPageIncluded() ? 'text-[#722420]' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">Include Page {currentPage} in PDF</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => togglePageInclusion()}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#722420] focus:ring-offset-2 shadow-md ${
                        isCurrentPageIncluded() 
                          ? 'bg-[#722420] shadow-[#722420]/50' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      role="switch"
                      aria-checked={isCurrentPageIncluded()}
                      aria-label={`${isCurrentPageIncluded() ? 'Include' : 'Exclude'} page ${currentPage} from PDF`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out flex items-center justify-center ${
                          isCurrentPageIncluded() 
                            ? 'translate-x-7' 
                            : 'translate-x-1'
                        }`}
                      >
                        {isCurrentPageIncluded() ? (
                          <svg className="w-4 h-4 text-[#722420]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </span>
                    </button>
                    <span className={`text-sm font-semibold transition-colors duration-200 min-w-[3rem] ${
                      isCurrentPageIncluded() 
                        ? 'text-[#722420]' 
                        : 'text-gray-500'
                    }`}>
                      {isCurrentPageIncluded() ? 'Included' : 'Excluded'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview Content */}
            <div className="flex justify-center overflow-x-auto" data-preview="true">
                          {currentPage === 1 ? (
              <Step1 formData={formData} updateFormData={updateFormData} timelineCoverImage={formData.timelineCoverImage} />
            ) : currentPage === 2 ? (
              <Step2 formData={formData} updateFormData={updateFormData} />
              ) : currentPage === 3 ? (
              <Step3 formData={formData} updateFormData={updateFormData} />
              ) : currentPage === 4 ? (
                <Step4 chimneyType={formData.chimneyType} />
              ) : currentPage === 5 ? (
                <Step5 
                  invoiceData={formData.invoiceData} 
                  updateInvoiceData={(data) => updateFormData({ invoiceData: data })}
                  currentInvoicePage={1}
                />
              ) : isInvoicePage(currentPage) ? (
                <Step5 
                  invoiceData={formData.invoiceData} 
                  updateInvoiceData={(data) => updateFormData({ invoiceData: data })}
                  currentInvoicePage={currentPage - 4}
                  isPDF={false}
                />
              ) : isRepairEstimatePage(currentPage) ? (
                <Step5Part2 
                  repairEstimateData={formData.repairEstimateData}
                  sections={formData.recommendationSections && formData.recommendationSections.length > 0
                    ? formData.recommendationSections.map(s => ({ title: s.title, rows: s.rows }))
                    : undefined}
                  section1={{
                    title: formData.recommendationSection1Title || 'Repair Estimate#1',
                    rows: formData.repairEstimateData?.rows || []
                  }}
                  section2={formData.showRecommendationSection2 ? {
                    title: formData.recommendationSection2Title || 'Repair Estimate#2',
                    rows: formData.recommendationSection2?.rows || []
                  } : undefined}
                  updateRepairEstimateData={(data) => updateFormData({ repairEstimateData: data })}
                  currentEstimatePage={currentPage - 4 - totalInvoicePages}
                  isPDF={false}
                  notes={formData.notes}
                  dataSourceUrl={formData.dataSourceUrl}
                  includeSummaryTable={formData.showRepairEstimateSummary !== false}
                />
              ) : isImagePage6(currentPage) ? (
                <Step6 
                  scrapedImages={formData.scrapedImages || []}
                  selectedImages={formData.selectedImages || []}
                  onImageSelection={handleImageSelection}
                  isPDF={false}
                  onImagePositionChange={handleStep6ImagePositionChange}
                  textPositionX={formData.step6TextPositionX}
                  textPositionY={formData.step6TextPositionY}
                  onTextPositionChange={handleStep6TextPositionChange}
                  currentPage={formData.step6CurrentPage}
                  onPageChange={handleStep6PageChange}
                  totalPages={formData.step6TotalPages}
                  onTotalPagesChange={handleStep6TotalPagesChange}
                />
              ) : isRecommendationPage(currentPage) ? (
                getCurrentRecommendationPage() ? (
                  <Step7 
                    scrapedImages={formData.scrapedImages || []}
                    selectedImages={formData.selectedImages || []}
                    onImageSelection={handleImageSelection}
                    isPDF={false}
                    repairEstimateData={getCurrentRecommendationPage()?.repairEstimateData}
                    reviewImage={getCurrentRecommendationPage()?.reviewImage}
                    customRecommendation={getCurrentRecommendationPage()?.customRecommendation || ''}
                    currentPage={currentRecommendationSubPage}
                    totalPages={(() => {
                      const currentPage = getCurrentRecommendationPage();
                      if (!currentPage) return 1;
                      const rows = currentPage.repairEstimateData?.rows || [];
                      return calculateRecommendationPages(rows);
                    })()}
                    imagePositionX={getCurrentRecommendationPage()?.imagePositionX}
                    imagePositionY={getCurrentRecommendationPage()?.imagePositionY}
                    imageWidth={getCurrentRecommendationPage()?.imageWidth}
                    imageHeight={getCurrentRecommendationPage()?.imageHeight}
                    onImagePositionChange={(x, y, width, height) => {
                      updateCurrentRecommendationPage({
                        imagePositionX: x,
                        imagePositionY: y,
                        imageWidth: width,
                        imageHeight: height
                      });
                    }}
                  />
                ) : (
                  // Show empty Step7 when no recommendation pages exist
                  <Step7 
                    scrapedImages={formData.scrapedImages || []}
                    selectedImages={formData.selectedImages || []}
                    onImageSelection={handleImageSelection}
                    isPDF={false}
                    repairEstimateData={{ manualEntry: false, rows: [] }}
                    reviewImage=""
                    customRecommendation=""
                    currentPage={1}
                    totalPages={1}
                  />
                 )
               ) : isImagePage(currentPage) ? (
                 getTotalImagePages() > 0 ? (
                 <Step8 
                   isPDF={false} 
                   unusedImages={getUnusedImages()} 
                   currentPage={getImagePageIndex(currentPage) + 1}
                   totalPages={getTotalImagePages()}
                   selectedImages={formData.selectedImages || []}
                   imageLayouts={formData.step8ImageLayouts || {}}
                   onImageLayoutChange={handleStep8ImageLayoutChange}
                   onCropImage={handleCropInvoiceImage}
                 />
                 ) : (
                   <div className="text-center py-8">
                     <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                       <p className="text-sm text-yellow-800">
                         No images available for Step 8. All images have been used in other steps or removed.
                       </p>
                     </div>
                   </div>
                 )
                ) : getLogicalStep(currentPage) === 9 && currentPage === (totalPages - 5) ? (
                  <Step9Part1 
                    chimneyType={
                      (formData.chimneyType?.toLowerCase() === 'prefabricated'
                        ? 'prefabricated'
                        : 'masonry') as any
                    }
                  />
                ) : getLogicalStep(currentPage) === 9 && currentPage === (totalPages - 4) ? (
                  <Step9Part2 
                    chimneyType={
                      (formData.chimneyType?.toLowerCase() === 'prefabricated'
                        ? 'prefabricated'
                        : 'masonry')
                    }
                  />
                ) : getLogicalStep(currentPage) === 9 && currentPage === (totalPages - 3) ? (
                  <Step9Part3 
                    chimneyType={
                      (formData.chimneyType?.toLowerCase() === 'prefabricated'
                        ? 'prefabricated'
                        : 'masonry')
                    }
                  />
                ) : getLogicalStep(currentPage) === 10 && currentPage === (totalPages - 2) ? (
                  <Step10Part3 isPDF={false} chimneyType={formData.chimneyType} />
                ) : getLogicalStep(currentPage) === 10 && currentPage === (totalPages - 1) ? (
                  <Step10Part4 isPDF={false} chimneyType={formData.chimneyType} />
                ) : getLogicalStep(currentPage) === 10 && currentPage === totalPages ? (
                  <Step10Part5 />
                ) : null}
             </div>
            
            {/* Mobile preview instructions */}
            {isMobile && (
              <div className="p-3 text-center bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-200">
                <p className="text-xs text-blue-700 font-medium">
                  📱 Desktop view enabled for better experience
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Full layout visible on mobile devices • Navigate between pages
                </p>
              </div>
            )}
          </div>
        </div>
        </div>
        </>
      )}

      {/* Image Cropping Modal */}
      {isCropping && (formData.timelineCoverImage || croppingImageId) && (() => {
        const cropTargetImage = croppingImageId
          ? formData.selectedImages?.find(img => img.id === croppingImageId) ||
            formData.scrapedImages?.find(img => img.id === croppingImageId) ||
            formData.uploadedInspectionImages?.find(img => img.id === croppingImageId)
          : null;
        const imageUrl = croppingImageId 
          ? cropTargetImage?.url
          : formData.timelineCoverImage || null;
        
        if (!imageUrl) return null;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] flex flex-col">
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {croppingImageId ? 'Crop Invoice Image' : 'Crop Timeline Cover Image'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {window.innerWidth <= 768 
                        ? "Touch and drag to crop. Use corners to resize."
                        : "Drag to move, use corners to resize. Free-form cropping available."
                      }
                    </p>
                  </div>
                  <button
                    onClick={handleCropCancel}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold p-2"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-hidden p-2 sm:p-4">
                <ImageCropper
                  imageUrl={imageUrl}
                  onCropComplete={handleCropComplete}
                  onCancel={handleCropCancel}
                  aspectRatio={croppingImageId ? 1 : 284/220} // Free-form for invoice images, fixed for timeline
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Review Image Selection Modal */}
      {showReviewImageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select Image for New Recommendation Page</h3>
                <button
                  onClick={() => setShowReviewImageSelector(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">Choose an image to create a new recommendation page</p>
              {formData.usedReviewImages && formData.usedReviewImages.length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  {formData.usedReviewImages.length} image(s) already used in other pages
                </p>
              )}
            </div>
            
            <div className="p-4">
              {getAvailableImages().length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {getAvailableImages().map((image, index) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url}
                        alt={`Available Image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#722420] transition-colors"
                        onClick={() => {
                          addNewRecommendationPage(image.url);
                          setShowReviewImageSelector(false);
                        }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to create page
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg mb-2">No more images available</p>
                  <p className="text-sm">All scraped images have been used for recommendation pages</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Image Modal */}
      {showChangeImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Change Image for Current Page</h3>
                <button
                  onClick={() => setShowChangeImageModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">Choose a different image for the current recommendation page</p>
            </div>
            
            <div className="p-4">
              {getAvailableImages().length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {getAvailableImages().map((image: any, index: number) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url}
                        alt={`Available Image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#722420] transition-colors"
                        onClick={() => {
                          changePageImage(currentRecommendationPageIndex, image.url);
                          setShowChangeImageModal(false);
                        }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to change
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg mb-2">No available images</p>
                  <p className="text-sm">All scraped images are currently in use</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Row Modal */}
      {showAddRowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Add Repair Item</h3>
                <button
                  onClick={() => setShowAddRowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">Choose how you want to add the repair item</p>
            </div>
            
            <div className="p-6 space-y-4">
              <button
                onClick={() => {
                        const currentPage = getCurrentRecommendationPage();
                        if (currentPage) {
                          const newRow = {
                            id: Date.now().toString(),
                            srNo: currentPage.repairEstimateData.rows.length + 1,
                            description: '',
                            unit: 1,
                            price: 0,
                            isManual: true
                          };
                          updateCurrentRecommendationPage({
                            repairEstimateData: {
                              ...currentPage.repairEstimateData,
                              rows: [...currentPage.repairEstimateData.rows, newRow]
                            }
                          });
                        }
                  setShowAddRowModal(false);
                }}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-[#722420] hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Custom Entry</h4>
                    <p className="text-sm text-gray-600 mt-1">Enter your own description, unit, and price manually</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowAddRowModal(false);
                  setShowPredefinedModal(true);
                }}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-[#722420] hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">From Predefined List</h4>
                    <p className="text-sm text-gray-600 mt-1">Choose from common repair items with preset pricing</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Predefined Options Modal */}
      {showPredefinedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh]">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select Predefined Repair Item</h3>
                <button
                  onClick={() => {
                    setShowPredefinedModal(false);
                    setPredefinedSearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {predefinedSearchTerm ? 
                  `Showing ${dropdownOptions.filter(option => 
                    option.description.toLowerCase().includes(predefinedSearchTerm.toLowerCase())
                  ).length} of ${dropdownOptions.length} predefined repair items` :
                  `Choose from ${dropdownOptions.length} predefined repair items`
                }
              </p>
              
              {/* Search Input */}
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Search repair items..."
                  value={predefinedSearchTerm}
                  onChange={(e) => setPredefinedSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {dropdownOptions
                  .filter(option => 
                    predefinedSearchTerm === '' || 
                    option.description.toLowerCase().includes(predefinedSearchTerm.toLowerCase())
                  )
                  .map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      const currentPage = getCurrentRecommendationPage();
                      if (currentPage) {
                      const newRow = {
                        id: Date.now().toString(),
                        srNo: currentPage.repairEstimateData.rows.length + 1,
                        description: option.description,
                        unit: option.unit,
                        price: option.price,
                        recommendation: option.recommendation,
                        isManual: true // Make predefined entries editable by default
                      };
                        // If customRecommendation is empty/undefined, set it to the new option's recommendation
                        // If it already has content, replace it with the new recommendation (user can edit if needed)
                        let updatedRecommendation: string;
                        const existingRecommendation = currentPage.customRecommendation?.trim() || '';
                        if (existingRecommendation !== '') {
                          // If there's existing content, replace it with the new recommendation
                          // (User might want to see the latest selected recommendation)
                          updatedRecommendation = option.recommendation || '';
                        } else {
                          // If empty or undefined, use the option's recommendation
                          updatedRecommendation = option.recommendation || '';
                        }
                        updateCurrentRecommendationPage({
                          repairEstimateData: {
                            ...currentPage.repairEstimateData,
                            rows: [...currentPage.repairEstimateData.rows, newRow]
                          },
                          customRecommendation: updatedRecommendation
                        });
                      }
                      setShowPredefinedModal(false);
                      setPredefinedSearchTerm('');
                    }}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-[#722420] hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">
                          {option.srNo}. {option.description}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Unit: {option.unit} | Price: ${option.price}
                        </div>
                      </div>
                      <div className="ml-4 text-xs text-gray-500">
                        #{option.id}
                      </div>
                    </div>
                  </button>
                ))}
                
                {/* No Results Message */}
                {predefinedSearchTerm && dropdownOptions.filter(option => 
                  option.description.toLowerCase().includes(predefinedSearchTerm.toLowerCase())
                ).length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-sm">
                      No repair items found matching "{predefinedSearchTerm}"
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Try searching with different keywords
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Selector Modal for Step 8 */}
      {showDateSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select Date for Images</h3>
                <button
                  onClick={() => setShowDateSelector(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Choose a date to view and merge its images with your current job.
              </p>
            </div>
            
            <div className="p-4">
              {allJobs.length > 0 ? (
                <div className="space-y-3">
                  {allJobs.map((job, index) => (
                    <div 
                      key={job.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => handleDateSelection(job)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {job.date}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {job.imageUrls.length} image{job.imageUrls.length !== 1 ? 's' : ''} available
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            {index === 0 ? 'Latest' : index === 1 ? 'Previous' : `${index + 1}${index === 2 ? 'rd' : index === 3 ? 'th' : 'th'}`}
                          </span>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Dates Found</h3>
                  <p className="text-sm text-gray-600 mb-4">No jobs with dates were found in the HTML content.</p>
                  <button
                    onClick={() => setShowDateSelector(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected Date Images Modal for Step 8 */}
      {showPreviousJobImages && selectedDateJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Images from {selectedDateJob.date}</h3>
                <button
                  onClick={() => {
                    setShowPreviousJobImages(false);
                    setSelectedDateJob(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Found {selectedDateJob.imageUrls.length} image{selectedDateJob.imageUrls.length !== 1 ? 's' : ''} from {selectedDateJob.date}. 
                Select the images you want to add to your current job.
              </p>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSelectAllImages}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedImages.length === selectedDateJob.imageUrls.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {selectedImages.length} of {selectedDateJob.imageUrls.length} selected
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              {selectedDateJob.imageUrls.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    {selectedDateJob.imageUrls.map((imageUrl, index) => {
                      const isSelected = selectedImages.includes(imageUrl);
                      return (
                        <div 
                          key={index} 
                          className={`relative group cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => handleDateImageSelection(imageUrl)}
                        >
                          <img
                            src={imageUrl}
                            alt={`Image from ${selectedDateJob.date} ${index + 1}`}
                            className={`w-full h-32 object-cover rounded-lg border-2 transition-all ${
                              isSelected ? 'border-blue-500' : 'border-gray-200'
                            }`}
                          />
                          
                          {/* Selection checkbox */}
                          <div className="absolute top-2 right-2">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected 
                                ? 'bg-blue-500 border-blue-500' 
                                : 'bg-white border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          
                          {/* Date overlay */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            {selectedDateJob.date}
                          </div>
                          
                          {/* Selection indicator */}
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={handleMergeSelectedDateImages}
                      disabled={selectedImages.length === 0}
                      className={`px-6 py-2 rounded-lg transition-colors font-medium ${
                        selectedImages.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Merge Selected Images ({selectedImages.length})
                    </button>
                    <button
                      onClick={() => {
                        setShowPreviousJobImages(false);
                        setSelectedDateJob(null);
                        setSelectedImages([]);
                      }}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Images Found</h3>
                  <p className="text-sm text-gray-600 mb-4">No images were found for {selectedDateJob.date}.</p>
                  <button
                    onClick={() => {
                      setShowPreviousJobImages(false);
                      setSelectedDateJob(null);
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Move to Position Modal */}
      {showPositionModal && imageForPositionMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Move Image to Position</h3>
                <button
                  onClick={() => {
                    setShowPositionModal(false);
                    setImageForPositionMove(null);
                    setTargetPosition('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Enter the position number (1 to {getUnusedImages().length}) where you want to move this image.
              </p>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label htmlFor="targetPosition" className="block text-sm font-medium text-gray-700 mb-2">
                  Target Position
                </label>
                <input
                  type="number"
                  id="targetPosition"
                  min="1"
                  max={getUnusedImages().length}
                  value={targetPosition}
                  onChange={(e) => setTargetPosition(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const pos = parseInt(targetPosition);
                      if (pos >= 1 && pos <= getUnusedImages().length) {
                        moveImageToPosition(imageForPositionMove, pos);
                        setShowPositionModal(false);
                        setImageForPositionMove(null);
                        setTargetPosition('');
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-transparent"
                  placeholder={`1 - ${getUnusedImages().length}`}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current total images: {getUnusedImages().length}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    const pos = parseInt(targetPosition);
                    if (pos >= 1 && pos <= getUnusedImages().length) {
                      moveImageToPosition(imageForPositionMove, pos);
                      setShowPositionModal(false);
                      setImageForPositionMove(null);
                      setTargetPosition('');
                    } else {
                      alert(`Please enter a number between 1 and ${getUnusedImages().length}`);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#5a1d1a] transition-colors font-medium"
                >
                  Move to Position
                </button>
                <button
                  onClick={() => {
                    setShowPositionModal(false);
                    setImageForPositionMove(null);
                    setTargetPosition('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                Report Generated Successfully!
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Your PDF report has been generated and downloaded. Please review it and approve to save the data and upload the report.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleApproveReport}
                  className="flex-1 px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#5a1d1a] transition-colors font-medium"
                >
                  Approve & Continue
                </button>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setGeneratedPdfBlob(null);
                    setGeneratedPdfFileName('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <EmailModal
          onSend={handleSendEmail}
          onSkip={handleSkipEmail}
          isSending={isSendingEmail}
          clientName={formData.clientName || 'Client'}
          pdfUrl={uploadedPdfUrl}
        />
      )}
    </div>
  );
};

export default MultiStepForm;

